import React, { useState, useEffect, useRef, useCallback } from"react";
import { useNavigate, useLocation } from"react-router-dom";
import { getSocket } from"../../services/socket";
import NegotiationPopup from"./NegotiationPopup";
import PaymentModal from"./PaymentModal";
import RatingModal from"./RatingModal";
import RejectionPopup from"./RejectionPopup";
import DriverAccepted from"./driverAccepted";
import DriverArrived from"./driverArrived";
import RideCompleted from"./rideCompleted";
import DriverBusyPopup from"./DriverBusyPopup";
import MessageModal from"../common/MessageModal";
import ShareRide from"./ShareRide";
import OtpSuccessPopup from"./OtpSuccessPopup";
import { io } from"socket.io-client";
import axios from"axios";
import Api, { getApiUrl } from"../../services/api";
import { clearBookingState } from"../../services/bookingStatePersistence";
import {
 isDriverPaymentConfirmed,
 isPaymentFullySettled,
 isUserPaymentSubmitted,
} from"../../utils/paymentConfirmation";

/**
 * Comprehensive function to clear ALL booking-related storage
 * This ensures a fresh start when starting a new ride after completion
 */
const clearAllBookingStorage = () => {
 try {
 // Clear booking state persistence
 clearBookingState();
 
 // Clear sessionStorage booking data
 sessionStorage.removeItem('current_booking_data');
 sessionStorage.removeItem('is_on_book_ride_page');
 sessionStorage.removeItem('is_on_nearby_drivers_page');
 
 // Clear user-independent booking draft
 sessionStorage.removeItem('uttaapp_book_ride_active_draft_v1');
 
 // Clear all user-specific booking drafts
 // We need to iterate through all sessionStorage keys to find draft keys
 const keysToRemove = [];
 for (let i = 0; i < sessionStorage.length; i++) {
 const key = sessionStorage.key(i);
 if (key && key.startsWith('uttaapp_book_ride_draft_v1:')) {
 keysToRemove.push(key);
 }
 }
 keysToRemove.forEach(key => sessionStorage.removeItem(key));
 
 // Clear localStorage booking data
 localStorage.removeItem('current_booking_data');
 localStorage.removeItem('nearby_drivers_nav_state');
 localStorage.removeItem('trip_started_booking');
 localStorage.removeItem('uttaapp_current_ride_id');
 
 console.log("🧹 Cleared all booking-related storage (drafts, state, navigation data)");
 } catch (error) {
 console.error("❌ Error clearing booking storage:", error);
 }
};

const BookingConfirmationPage = ({ user, onLogout: _onLogout }) => {
 const navigate = useNavigate();
 const location = useLocation();

 // CRITICAL: bookingId can be provided via query string for direct URL access / refresh
 // Example: /booking-confirmation?bookingId=xxxx (used in NotificationContext.js)
 const bookingIdFromQuery = new URLSearchParams(location.search).get("bookingId");

 // Get booking data from navigation state
 const {
 booking: locationBooking,
 driver: locationDriver,
 pickupLocation: locationPickup,
 dropoffLocation: locationDropoff,
 } = location.state || {};

 // CRITICAL: Initialize booking and related data from localStorage if location.state is not available
 // This ensures normal refresh shows correct status immediately
 const getInitialBookingData = () => {
 // CRITICAL: Check if user is on NearbyDriversPage - if so, don't load booking data from localStorage
 // This prevents auto-loading when user refreshes NearbyDriversPage
 const isOnNearbyDriversPage = sessionStorage.getItem('is_on_nearby_drivers_page') ==='true';
 if (isOnNearbyDriversPage && !locationBooking) {
 console.log("🚫 User is on NearbyDriversPage - skipping booking data initialization from localStorage");
 return {
 booking: null,
 driver: null,
 pickupLocation: null,
 dropoffLocation: null,
 };
 }
 
 // First priority: location.state
 if (locationBooking) {
 return {
 booking: locationBooking,
 driver: locationDriver,
 pickupLocation: locationBooking.pickupLocation || locationPickup || null,
 dropoffLocation: locationBooking.dropoffLocation || locationDropoff || null,
 };
 }
 
 // Second priority: localStorage (for normal refresh)
 try {
 const savedData = localStorage.getItem("current_booking_data");
 if (savedData) {
 const parsed = JSON.parse(savedData);
 const savedBooking = parsed.booking || {};
 const savedDriver = parsed.driver || null;
 const savedPickupLocation = parsed.pickupLocation || savedBooking.pickupLocation || null;
 const savedDropoffLocation = parsed.dropoffLocation || savedBooking.dropoffLocation || null;
 
 if (savedBooking && Object.keys(savedBooking).length > 0) {
 console.log("📊 Initializing booking data from localStorage:", {
 hasBooking: true,
 status: parsed.status || savedBooking.status,
 hasDriver: !!savedDriver
 });
 return {
 booking: savedBooking,
 driver: savedDriver,
 pickupLocation: savedPickupLocation,
 dropoffLocation: savedDropoffLocation,
 };
 }
 }
 } catch (error) {
 console.warn("⚠️ Error reading booking data from localStorage:", error);
 }
 
 // Default: null
 return {
 booking: null,
 driver: null,
 pickupLocation: null,
 dropoffLocation: null,
 };
 };

 const initialData = getInitialBookingData();
 
 // State for booking and driver data (can be from location.state or localStorage)
 const [booking, setBooking] = useState(initialData.booking);
 const [driver, setDriver] = useState(initialData.driver);
 // Priority: booking.pickupLocation (Google Places) > locationPickup (never use pickupLocationName for state)
 const [pickupLocation, setPickupLocation] = useState(initialData.pickupLocation);
 const [dropoffLocation, setDropoffLocation] = useState(initialData.dropoffLocation);
 const [loading, setLoading] = useState(!locationBooking && !initialData.booking);
 const [error, setError] = useState(null);

 // CRITICAL: Initialize booking status from location.state or localStorage immediately
 // This prevents showing"pending"status when page refreshes with"accepted"status
 const getInitialBookingStatus = () => {
 // First priority: status from location.state booking object
 if (locationBooking?.status) {
 console.log("📊 Initializing status from location.state:", locationBooking.status);
 return locationBooking.status;
 }
 
 // Second priority: status from localStorage (for page refresh)
 try {
 const savedData = localStorage.getItem("current_booking_data");
 if (savedData) {
 const parsed = JSON.parse(savedData);
 // Check both parsed.status and parsed.booking.status
 const savedStatus = parsed.status || parsed.booking?.status;
 if (savedStatus) {
 // Accept any status, including"pending"if that's what's saved
 // But prefer non-pending statuses
 if (savedStatus !=="pending") {
 console.log("📊 Initializing status from localStorage (non-pending):", savedStatus);
 return savedStatus;
 } else {
 console.log("📊 Found'pending'status in localStorage - will check API");
 // Still return pending, but API will update it if needed
 return savedStatus;
 }
 }
 }
 } catch (error) {
 console.warn("⚠️ Error reading status from localStorage:", error);
 }
 
 // Default: pending
 console.log("📊 Initializing with default status: pending");
 return"pending";
 };

 const [bookingStatus, setBookingStatusRaw] = useState(getInitialBookingStatus());
 
 // Get bookingId from query param or booking object
 const bookingId = bookingIdFromQuery || booking?.id || booking?._id;
 
 // CRITICAL: Wrapper function to prevent status downgrade from"accepted"to"pending"// This ensures status never goes backwards after being accepted
 const setBookingStatus = (newStatus) => {
 setBookingStatusRaw((currentStatus) => {
 // Status priority order
 const statusPriority = {"pending": 0,"accepted": 1,"driver_arrived": 2,"in_progress": 3,"completed": 4,"rejected": -1,"cancelled": -1
 };
 
 const getPriority = (status) => statusPriority[status] || 0;
 const currentPriority = getPriority(currentStatus);
 const newPriority = getPriority(newStatus);
 
 // CRITICAL: Never downgrade from accepted/later status to pending
 // Only allow status change if:
 // 1. New status has higher or equal priority, OR
 // 2. Current status is pending (can upgrade), OR
 // 3. New status is a final state (rejected/cancelled)
 if (currentPriority >= 1 && newPriority === 0 && newStatus ==="pending") {
 console.warn("🚫 Preventing status downgrade from", currentStatus,"to", newStatus);
 console.warn("🚫 Keeping current status:", currentStatus);
 return currentStatus; // Keep current status
 }
 
 // Allow status change
 if (currentStatus !== newStatus) {
 console.log("✅ Status change:", currentStatus,"->", newStatus);
 }
 return newStatus;
 });
 };

 // Clear booking state when page loads (booking-confirmation is beyond early stage)
 useEffect(() => {
 if (booking && booking.id) {
 console.log('📋 Booking confirmation page loaded - clearing booking state (beyond early stage)');
 clearBookingState();
 }
 }, [booking]);

 // Clear booking state when booking reaches blocked states (accepted or beyond)
 useEffect(() => {
 // Blocked states: accepted, confirmed, driver_arrived, in_progress, completed, cancelled, rejected
 const blockedStates = ['accepted','confirmed','driver_arrived','in_progress','completed','cancelled','rejected'];
 if (blockedStates.includes(bookingStatus)) {
 console.log('✅ Booking reached blocked state:', bookingStatus,'- clearing booking state');
 clearBookingState();
 }
 }, [bookingStatus]);

 // CRITICAL: Track whether we have fetched status from backend on this page load
 // This prevents localStorage writes from overwriting the last server-known status during soft refresh.
 const [hasServerSynced, setHasServerSynced] = useState(false);
 // CRITICAL: Initialize fare and OTP from localStorage if available (for normal refresh)
 // OTP is ONLY initialized if driver has arrived (status: driver_arrived or in_progress)
 const getInitialFareAndOtp = () => {
 if (initialData.booking) {
 const savedFare = initialData.booking.fare || initialData.booking.acceptedFare;
 const savedStatus = initialData.booking.status || getInitialBookingStatus();
 
 // CRITICAL: Only initialize OTP if driver has arrived
 let savedOtp = null;
 if (savedStatus ==="driver_arrived"|| savedStatus ==="in_progress") {
 savedOtp = initialData.booking.otp?.code || initialData.booking.otp || initialData.booking.otpCode;
 }
 
 if (savedFare || savedOtp) {
 console.log("📊 Initializing fare and OTP from localStorage:", {
 fare: savedFare,
 hasOtp: !!savedOtp,
 status: savedStatus
 });
 }
 return {
 fare: savedFare || null,
 otp: savedOtp || null,
 };
 }
 return { fare: null, otp: null };
 };

 const initialFareAndOtp = getInitialFareAndOtp();

 const [elapsedTime, setElapsedTime] = useState(0);
 const [showNegotiationPopup, setShowNegotiationPopup] = useState(false);
 const [negotiationBooking, setNegotiationBooking] = useState(null);
 const [acceptedFare, setAcceptedFare] = useState(initialFareAndOtp.fare);
 const [otpCode, setOtpCode] = useState(initialFareAndOtp.otp);
 // CRITICAL: If fare is already set from localStorage, negotiation was accepted
 const [negotiationAccepted, setNegotiationAccepted] = useState(!!initialFareAndOtp.fare);
 const [showPaymentModal, setShowPaymentModal] = useState(false);
 const [paymentStatus, setPaymentStatus] = useState(null);
 const [showRatingModal, setShowRatingModal] = useState(false);
 const [hasRated, setHasRated] = useState(false);
 const [showBookingInfoModal, setShowBookingInfoModal] = useState(false);
 const [showRejectionPopup, setShowRejectionPopup] = useState(false);
 const [showCancelConfirm, setShowCancelConfirm] = useState(false);
 const [cancelling, setCancelling] = useState(false);
 const [showDriverBusyPopup, setShowDriverBusyPopup] = useState(false);
 const [driverBusyData, setDriverBusyData] = useState(null);
 const [successMessage, setSuccessMessage] = useState({ show: false, message:'', title:''});
 const [messageModal, setMessageModal] = useState({ show: false, message:'', type:'info', title:'Message'});
 const [driverLocation, setDriverLocation] = useState(null); // Driver's current GPS location
 const [pickupCoordinates, setPickupCoordinates] = useState(null); // Pickup location coordinates
 const [showFindAnotherDriver, setShowFindAnotherDriver] = useState(false); // Show"Find Another Driver"button after timeout
 const [dismissedFindAnotherDriverPopup, setDismissedFindAnotherDriverPopup] = useState(false); // Dismiss timeout popup
 const [bookingStartTime, setBookingStartTime] = useState(null); // Track when booking was created (pending status)
 const [notificationSent, setNotificationSent] = useState(false); // Track if push notification has been sent
 const [showShareRide, setShowShareRide] = useState(false); // Show Share Ride modal
 const [showPickupOtpPopup, setShowPickupOtpPopup] = useState(false); // Popup when driver arrives
 const [showOtpSuccessPopup, setShowOtpSuccessPopup] = useState(false); // OTP verification success popup
 const [resendCountdown, setResendCountdown] = useState(0); // Resend OTP countdown
 const [resending, setResending] = useState(false); // Resending OTP status
 const [resendMessage, setResendMessage] = useState(null); // Resend message
 const [acceptedAtTimestamp, setAcceptedAtTimestamp] = useState(null); // Track when booking was accepted
 const [canCancel, setCanCancel] = useState(false); // Whether user can cancel (within 3 minutes)
 const [isWebView, setIsWebView] = useState(() => {
 if (typeof window ==="undefined") return false;
 const userAgent = navigator.userAgent || navigator.vendor || window.opera ||"";
 const isAndroidWebView = /\bwv\b|; wv\)/i.test(userAgent);
 const isIosWebView =
 /iPad|iPhone|iPod/i.test(userAgent) &&
 /AppleWebKit/i.test(userAgent) &&
 !/Safari/i.test(userAgent);
 const isEmbeddedAppWebView = Boolean(
 window.ReactNativeWebView ||
 window.flutter_inappwebview ||
 window.webkit?.messageHandlers
 );
 const previewParams = new URLSearchParams(window.location.search);
 const forceWebViewPreview =
 previewParams.get("webview") ==="1"||
 previewParams.get("preview") ==="webview";

 return (
 isAndroidWebView ||
 isIosWebView ||
 isEmbeddedAppWebView ||
 forceWebViewPreview
 );
 });

 const prevBookingStatusRef = useRef(bookingStatus);
 const prevOtpCodeRef = useRef(otpCode);
 const otpReceivedTimeRef = useRef(null); // Track when OTP was first received

 const getPickupOtpPopupStorageKey = useCallback(() => {
 const id = bookingIdFromQuery || booking?.id || booking?._id;
 return id ?`pickup_otp_popup_shown_${id}`: null;
 }, [bookingIdFromQuery, booking?.id, booking?._id]);

 const openPickupOtpPopupIfNeeded = useCallback((otp) => {
 if (!otp) return;
 const key = getPickupOtpPopupStorageKey();
 if (key && sessionStorage.getItem(key) ==='true') return;
 setShowPickupOtpPopup(true);
 if (key) sessionStorage.setItem(key,'true');
 }, [getPickupOtpPopupStorageKey]);

 useEffect(() => {
 const userAgent = navigator.userAgent || navigator.vendor || window.opera ||"";
 const isAndroidWebView = /\bwv\b|; wv\)/i.test(userAgent);
 const isIosWebView =
 /iPad|iPhone|iPod/i.test(userAgent) &&
 /AppleWebKit/i.test(userAgent) &&
 !/Safari/i.test(userAgent);
 const isEmbeddedAppWebView = Boolean(
 window.ReactNativeWebView ||
 window.flutter_inappwebview ||
 window.webkit?.messageHandlers
 );
 const previewParams = new URLSearchParams(location.search);
 const forceWebViewPreview =
 previewParams.get("webview") ==="1"||
 previewParams.get("preview") ==="webview";

 setIsWebView(
 isAndroidWebView ||
 isIosWebView ||
 isEmbeddedAppWebView ||
 forceWebViewPreview
 );
 }, [location.search]);

 // Track when booking status becomes"accepted"to enable 3-minute cancellation window
 useEffect(() => {
 const prevStatus = prevBookingStatusRef.current;
 
 // When status changes to"accepted", record the timestamp
 if (bookingStatus ==="accepted"&& prevStatus !=="accepted") {
 // Use current time when status becomes"accepted"// If booking was already accepted on page load, we'll use current time
 // (user gets 3 minutes from when they see the page)
 const timestamp = Date.now();
 setAcceptedAtTimestamp(timestamp);
 setCanCancel(true); // Enable cancellation immediately
 console.log("✅ Booking accepted - cancellation window started at:", new Date(timestamp).toISOString());
 }
 
 // If status is no longer"accepted", disable cancellation
 if (bookingStatus !=="accepted") {
 setCanCancel(false);
 if (prevStatus ==="accepted") {
 console.log("⚠️ Booking status changed from accepted - cancellation disabled");
 }
 }
 
 prevBookingStatusRef.current = bookingStatus;
 }, [bookingStatus]);

 // Check if 3 minutes have passed since acceptance (disable cancellation after 3 minutes)
 useEffect(() => {
 if (!acceptedAtTimestamp || bookingStatus !=="accepted") {
 return;
 }

 const checkCancellationWindow = () => {
 const now = Date.now();
 const elapsed = now - acceptedAtTimestamp;
 const threeMinutes = 3 * 60 * 1000; // 3 minutes in milliseconds
 
 if (elapsed > threeMinutes) {
 setCanCancel(false);
 console.log("⏰ 3-minute cancellation window expired");
 } else {
 setCanCancel(true);
 }
 };

 // Check immediately
 checkCancellationWindow();

 // Check every second to update UI
 const interval = setInterval(checkCancellationWindow, 1000);

 return () => clearInterval(interval);
 }, [acceptedAtTimestamp, bookingStatus]);

 // Open OTP popup when driver arrives and OTP becomes available.
 // This is triggered by socket events or polling, and it won't re-open for the same booking in the same tab.
 useEffect(() => {
 const prevStatus = prevBookingStatusRef.current;
 const prevOtp = prevOtpCodeRef.current;

 prevBookingStatusRef.current = bookingStatus;
 prevOtpCodeRef.current = otpCode;

 const shouldShow =
 otpCode && (bookingStatus ==='driver_arrived'|| bookingStatus ==='in_progress');
 if (!shouldShow) return;

 const safeOtpCode = extractOtpCode(otpCode);
 if (!safeOtpCode) return;

 const statusJustChanged = prevStatus !== bookingStatus;
 const otpJustAppeared = !prevOtp && !!otpCode;

 if (statusJustChanged || otpJustAppeared) {
 openPickupOtpPopupIfNeeded(safeOtpCode);
 }
 }, [bookingStatus, otpCode, openPickupOtpPopupIfNeeded]);

 // Helper function to cancel booking when user finds another driver
 const cancelBookingForAnotherDriver = async () => {
 const bookingId = booking?.id || booking?._id;
 if (bookingId) {
 try {
 console.log('❌ Cancelling booking - user selected another driver after timeout:', bookingId);
 const token = localStorage.getItem("uttaapp_token");
 await axios.patch(
 Api.Booking.updateStatus(bookingId),
 { status:'cancelled'},
 {
 headers: {'Authorization':`Bearer ${token}`,'Content-Type':'application/json'}
 }
 );
 console.log('✅ Booking cancelled successfully - user selected another driver');
 } catch (error) {
 console.error('⚠️ Error cancelling booking:', error);
 // Continue navigation even if cancellation fails
 }
 }
 };


 // Helper function to safely extract OTP code from string or object
 const extractOtpCode = (otp) => {
 if (!otp) return null;
 // If it's already a string/number, return it
 if (typeof otp ==='string'|| typeof otp ==='number') {
 return String(otp);
 }
 // If it's an object, extract the code property
 if (typeof otp ==='object'&& otp !== null) {
 return otp.code ? String(otp.code) : null;
 }
 return null;
 };

 const getDriverDisplayRating = (driverData, fallbackBookingRating = null) => {
 const ratingCandidate =
 driverData?.averageRating ??
 driverData?.rating?.rating ??
 driverData?.rating ??
 fallbackBookingRating?.rating ??
 fallbackBookingRating;

 if (typeof ratingCandidate ==="number") {
 return ratingCandidate.toFixed(1);
 }

 if (typeof ratingCandidate ==="string"&& ratingCandidate.trim()) {
 const parsed = Number(ratingCandidate);
 return Number.isFinite(parsed) ? parsed.toFixed(1) : ratingCandidate;
 }

 return"4.5";
 };

 // Helper function to get image URL (for driver profile picture and pickup photo)
 const getImageUrl = (imagePath) => {
 if (!imagePath) {
 return null;
 }

 // If it's a full URL, return as is
 if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
 return imagePath;
 }

 // Normalize path - remove leading slashes and backslashes
 let normalizedPath = imagePath.replace(/^[/\\]+/,"").replace(/\\/g,"/");

 // Construct backend URL using centralized API helper
 const baseUrl = getApiUrl();
 const imageUrl =`${baseUrl}/${normalizedPath}`;

 return imageUrl;
 };

 // Helper function to open Google Maps with route from driver location to pickup location
 // When driver arrives (driver_arrived), show route from pickup to dropoff
 const openGoogleMaps = async () => {
 try {
 // Get pickup location address from BookingLocationForm (Location Address input)
 // This is the location filled using Google Place API
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 const pickupAddress = 
 booking?.pickupLocation || 
 pickupLocation || 
 booking?.pickupLocationName ||'';
 
 // Get dropoff location
 const dropoffAddress = 
 booking?.dropoffLocation || 
 dropoffLocation ||'';
 
 // If driver has arrived (OTP verified), show route from pickup to dropoff
 if (bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress") {
 console.log('🚗 Driver arrived - showing route from pickup to dropoff');
 
 if (!pickupAddress) {
 setMessageModal({ show: true, message:'Pickup location is missing. Cannot open map.', type:'error', title:'Error'});
 return;
 }
 
 if (!dropoffAddress) {
 setMessageModal({ show: true, message:'Dropoff location is missing. Cannot open map.', type:'error', title:'Error'});
 return;
 }
 
 // Get coordinates for pickup and dropoff
 let pickupLat, pickupLng, dropoffLat, dropoffLng;
 
 // Try to get pickup coordinates
 if (pickupCoordinates && pickupCoordinates.latitude && pickupCoordinates.longitude) {
 pickupLat = pickupCoordinates.latitude;
 pickupLng = pickupCoordinates.longitude;
 } else if (booking?.pickupLatitude && booking?.pickupLongitude) {
 pickupLat = booking.pickupLatitude;
 pickupLng = booking.pickupLongitude;
 } else {
 // Fetch from place database
 try {
 const token = localStorage.getItem("uttaapp_token");
 const pickupResponse = await axios.get(
 Api.Place.searchPlaces(pickupAddress),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 
 if (pickupResponse.data.success && pickupResponse.data.places && pickupResponse.data.places.length > 0) {
 const matchingPlace = pickupResponse.data.places.find(
 place => 
 place.formattedAddress === pickupAddress || 
 place.name === pickupAddress ||
 place.formattedAddress?.includes(pickupAddress) ||
 pickupAddress.includes(place.formattedAddress || place.name ||'')
 ) || pickupResponse.data.places[0];
 
 if (matchingPlace && matchingPlace.latitude && matchingPlace.longitude) {
 pickupLat = matchingPlace.latitude;
 pickupLng = matchingPlace.longitude;
 setPickupCoordinates({ latitude: pickupLat, longitude: pickupLng });
 }
 }
 } catch (error) {
 console.warn('⚠️ Could not fetch pickup coordinates:', error);
 }
 }
 
 // Try to get dropoff coordinates
 if (booking?.dropoffLatitude && booking?.dropoffLongitude) {
 dropoffLat = booking.dropoffLatitude;
 dropoffLng = booking.dropoffLongitude;
 } else {
 // Fetch from place database
 try {
 const token = localStorage.getItem("uttaapp_token");
 const dropoffResponse = await axios.get(
 Api.Place.searchPlaces(dropoffAddress),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 
 if (dropoffResponse.data.success && dropoffResponse.data.places && dropoffResponse.data.places.length > 0) {
 const matchingPlace = dropoffResponse.data.places.find(
 place => 
 place.formattedAddress === dropoffAddress || 
 place.name === dropoffAddress ||
 place.formattedAddress?.includes(dropoffAddress) ||
 dropoffAddress.includes(place.formattedAddress || place.name ||'')
 ) || dropoffResponse.data.places[0];
 
 if (matchingPlace && matchingPlace.latitude && matchingPlace.longitude) {
 dropoffLat = matchingPlace.latitude;
 dropoffLng = matchingPlace.longitude;
 }
 }
 } catch (error) {
 console.warn('⚠️ Could not fetch dropoff coordinates:', error);
 }
 }
 
 // If we have coordinates for both, use coordinate-based URL (shows polyline)
 if (pickupLat && pickupLng && dropoffLat && dropoffLng) {
 const googleMapsUrl =`https://www.google.com/maps/dir/?api=1&origin=${pickupLat},${pickupLng}&destination=${dropoffLat},${dropoffLng}`;
 window.open(googleMapsUrl,'_blank');
 console.log('🗺️ Opening Google Maps with polyline from pickup to dropoff:', { 
 pickup: { lat: pickupLat, lng: pickupLng, address: pickupAddress },
 dropoff: { lat: dropoffLat, lng: dropoffLng, address: dropoffAddress }
 });
 } else {
 // Fallback: Use address-based navigation
 const encodedPickup = encodeURIComponent(pickupAddress);
 const encodedDropoff = encodeURIComponent(dropoffAddress);
 const googleMapsUrl =`https://www.google.com/maps/dir/?api=1&origin=${encodedPickup}&destination=${encodedDropoff}`;
 window.open(googleMapsUrl,'_blank');
 console.log('🗺️ Opening Google Maps with route from pickup to dropoff (address-based):', { 
 pickup: pickupAddress,
 dropoff: dropoffAddress
 });
 }
 return; // Exit early for driver_arrived case
 }
 
 // For"accepted"status: Show route from driver location to pickup location
 if (!pickupAddress) {
 setMessageModal({ show: true, message:'Pickup location is missing. Cannot open map.', type:'error', title:'Error'});
 return;
 }

 console.log('🗺️ Opening Google Maps - Pickup location:', pickupAddress);
 console.log('📍 Driver location state:', driverLocation);

 // Request driver location if not available
 if (!driverLocation || !driverLocation.latitude || !driverLocation.longitude) {
 console.log('📍 Driver location not available, requesting...');
 const socket = getSocket();
 if (socket && driver) {
 const driverId = driver.driverId || driver.id || driver._id;
 if (driverId) {
 socket.emit('request-driver-location', { driverId });
 console.log('📍 Requested driver location for:', driverId);
 
 // Wait a bit for location to arrive, then proceed
 await new Promise(resolve => setTimeout(resolve, 1000));
 }
 }
 }

 // If driver location is available, show route from driver to pickup
 if (driverLocation && driverLocation.latitude && driverLocation.longitude) {
 const driverLat = driverLocation.latitude;
 const driverLng = driverLocation.longitude;
 
 console.log('✅ Using driver location as origin:', { lat: driverLat, lng: driverLng });
 
 // Get pickup location coordinates
 let pickupLat, pickupLng;
 
 // First, try to get coordinates from state
 if (pickupCoordinates && pickupCoordinates.latitude && pickupCoordinates.longitude) {
 pickupLat = pickupCoordinates.latitude;
 pickupLng = pickupCoordinates.longitude;
 console.log('✅ Using pickup coordinates from state:', { lat: pickupLat, lng: pickupLng });
 } 
 // Try to get from booking data
 else if (booking?.pickupLatitude && booking?.pickupLongitude) {
 pickupLat = booking.pickupLatitude;
 pickupLng = booking.pickupLongitude;
 console.log('✅ Using pickup coordinates from booking:', { lat: pickupLat, lng: pickupLng });
 }
 // Try to fetch from place database using pickup address (from BookingLocationForm)
 else {
 try {
 console.log('🔍 Fetching coordinates for pickup location from place database:', pickupAddress);
 const token = localStorage.getItem("uttaapp_token");
 const searchResponse = await axios.get(
 Api.Place.searchPlaces(pickupAddress),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 
 if (searchResponse.data.success && searchResponse.data.places && searchResponse.data.places.length > 0) {
 // Find matching place by address (exact match first, then partial)
 const exactMatch = searchResponse.data.places.find(
 place => place.formattedAddress === pickupAddress || place.name === pickupAddress
 );
 
 const matchingPlace = exactMatch || searchResponse.data.places.find(
 place => 
 place.formattedAddress?.includes(pickupAddress) ||
 pickupAddress.includes(place.formattedAddress || place.name ||'')
 ) || searchResponse.data.places[0]; // Use first result as fallback
 
 if (matchingPlace && matchingPlace.latitude && matchingPlace.longitude) {
 pickupLat = matchingPlace.latitude;
 pickupLng = matchingPlace.longitude;
 console.log('✅ Found coordinates from place database:', { lat: pickupLat, lng: pickupLng });
 // Update state for future use
 setPickupCoordinates({ latitude: pickupLat, longitude: pickupLng });
 } else {
 console.warn('⚠️ Place found but no coordinates available');
 }
 } else {
 console.warn('⚠️ No places found in database for:', pickupAddress);
 }
 } catch (error) {
 console.warn('⚠️ Could not fetch coordinates from place database:', error);
 }
 }

 // If we have coordinates for both driver and pickup, use coordinate-based URL (shows polyline)
 if (pickupLat && pickupLng) {
 // Use coordinates for both driver and pickup - shows polyline
 // Format: https://www.google.com/maps/dir/?api=1&origin={lat},{lng}&destination={lat},{lng}
 const googleMapsUrl =`https://www.google.com/maps/dir/?api=1&origin=${driverLat},${driverLng}&destination=${pickupLat},${pickupLng}`;
 window.open(googleMapsUrl,'_blank');
 console.log('🗺️ Opening Google Maps with polyline from driver to pickup:', { 
 driver: { lat: driverLat, lng: driverLng },
 pickup: { lat: pickupLat, lng: pickupLng },
 pickupAddress
 });
 } else {
 // Fallback: Use address-based navigation (Google Maps will geocode and show route)
 // Origin: Driver's current location (GPS coordinates)
 // Destination: Pickup location address (from BookingLocationForm)
 const encodedPickup = encodeURIComponent(pickupAddress);
 const googleMapsUrl =`https://www.google.com/maps/dir/?api=1&origin=${driverLat},${driverLng}&destination=${encodedPickup}`;
 window.open(googleMapsUrl,'_blank');
 console.log('🗺️ Opening Google Maps with driver location (GPS) to pickup address:', { 
 driver: { lat: driverLat, lng: driverLng },
 pickup: pickupAddress
 });
 }
 } else {
 // Fallback: Use address-based navigation if driver location not available
 // This should rarely happen, but handle gracefully
 console.warn('⚠️ Driver location not available, using address-based navigation');
 const dropoff = 
 booking?.dropoffLocation || 
 dropoffLocation ||'';

 // Encode locations for URL
 const encodedPickup = encodeURIComponent(pickupAddress);
 const encodedDropoff = dropoff ? encodeURIComponent(dropoff) :'';

 // Google Maps Directions URL
 // Note: Without driver location, we can only show pickup to dropoff
 const googleMapsUrl = dropoff 
 ?`https://www.google.com/maps/dir/?api=1&origin=${encodedPickup}&destination=${encodedDropoff}`:`https://www.google.com/maps/dir/?api=1&destination=${encodedPickup}`;

 // Open in new tab
 window.open(googleMapsUrl,'_blank');
 console.log('🗺️ Opening Google Maps with route (no driver location):', { pickup: pickupAddress, dropoff });
 setMessageModal({ show: true, message:'Driver location is not available yet. Please wait a moment and try again.', type:'warning', title:'Warning'});
 }
 } catch (error) {
 console.error('❌ Error opening Google Maps:', error);
 setMessageModal({ show: true, message:'Failed to open Google Maps. Please try again.', type:'error', title:'Error'});
 }
 };

 // CRITICAL: Prevent browser back navigation from BookingConfirmationPage
 useEffect(() => {
 // Push current state to history to prevent back navigation
 window.history.pushState(null,'', window.location.href);
 
 const handlePopState = (_event) => {
 // Prevent back navigation by pushing state again
 window.history.pushState(null,'', window.location.href);
 console.log('🚫 Back navigation prevented from BookingConfirmationPage');
 };
 
 // Listen to popstate event (triggered by back/forward button)
 window.addEventListener('popstate', handlePopState);
 
 // Cleanup
 return () => {
 window.removeEventListener('popstate', handlePopState);
 };
 }, []);

 // CRITICAL: Check if user is on NearbyDriversPage and redirect them back
 // This prevents BookingConfirmationPage from loading when user refreshes NearbyDriversPage
 useEffect(() => {
 const isOnNearbyDriversPage = sessionStorage.getItem('is_on_nearby_drivers_page') ==='true';
 if (isOnNearbyDriversPage && !locationBooking && !bookingIdFromQuery) {
 console.log('🚫 User is on NearbyDriversPage - redirecting back to NearbyDriversPage');
 console.log('🚫 This prevents BookingConfirmationPage from loading when refreshing NearbyDriversPage');
 
 // Try to restore navigation state from localStorage
 try {
 const savedNavState = localStorage.getItem('nearby_drivers_nav_state');
 if (savedNavState) {
 const parsed = JSON.parse(savedNavState);
 console.log('✅ Found saved navigation state, redirecting to NearbyDriversPage');
 navigate('/nearby-drivers', { state: parsed, replace: true });
 return;
 }
 } catch (e) {
 console.warn('⚠️ Could not restore navigation state:', e);
 }
 
 // If no saved state, redirect to dashboard
 console.log('⚠️ No saved navigation state found, redirecting to dashboard');
 navigate('/dashboard', { replace: true });
 }
 }, [navigate, locationBooking, bookingIdFromQuery]);

 // Fetch booking data on mount if not in location.state (page refresh scenario)
 useEffect(() => {
 const fetchBookingData = async () => {
 // CRITICAL: Check if user is on NearbyDriversPage - if so, don't load booking data
 // This prevents redirect when refreshing NearbyDriversPage
 const isOnNearbyDriversPage = sessionStorage.getItem('is_on_nearby_drivers_page') ==='true';
 if (isOnNearbyDriversPage && !locationBooking && !bookingIdFromQuery) {
 console.log('🚫 User is on NearbyDriversPage - preventing BookingConfirmationPage from loading');
 console.log('🚫 This prevents redirect when refreshing NearbyDriversPage');
 setLoading(false);
 setError('Please complete driver selection on Nearby Drivers page');
 return;
 }
 
 // If we have booking data from location.state, use it and save to localStorage
 if (locationBooking) {
 console.log("✅ Using booking data from location.state");
 console.log("📍 Location state data:", {
 bookingPickupLocation: locationBooking.pickupLocation,
 locationPickup: locationPickup,
 bookingPickupLocationName: locationBooking.pickupLocationName
 });
 
 // CRITICAL: Always fetch full booking data to ensure we have correct pickupLocation (Google Places address)
 const bookingId = locationBooking.id || locationBooking._id;
 if (bookingId) {
 console.log("📋 Fetching full booking data to ensure correct pickupLocation...");
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: {'Authorization':`Bearer ${token}`},
 // Cache buster to avoid browser/proxy returning stale status on soft refresh
 params: { _ts: Date.now() }
 }
 );
 
 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 console.log("✅ Fetched full booking data:", {
 pickupLocation: fetchedBooking.pickupLocation,
 pickupLocationName: fetchedBooking.pickupLocationName,
 dropoffLocation: fetchedBooking.dropoffLocation
 });
 
 // Use fetched booking data (has correct pickupLocation from backend)
 setBooking(fetchedBooking);
 setDriver(locationDriver);
 // Priority: fetchedBooking.pickupLocation (Google Places) > locationPickup (NEVER use pickupLocationName for state)
 const finalPickupLocation = fetchedBooking.pickupLocation || locationPickup || null;
 console.log("📍 Setting pickupLocation state to:", finalPickupLocation);
 setPickupLocation(finalPickupLocation);
 setDropoffLocation(fetchedBooking.dropoffLocation || locationDropoff || null);
 
 // CRITICAL: Restore booking status from fetched booking data
 // For reserve bookings:"accepted"means driver accepted,"driver_arrived"means driver arrived
 // For regular bookings:"accepted"means trip started,"driver_arrived"means driver arrived
 const apiStatus = fetchedBooking.status;
 const locationStatus = locationBooking?.status;
 const currentStatus = bookingStatus; // Current state (might be from initial load)
 
 // Status priority order (never downgrade from accepted to pending)
 const statusPriority = {"pending": 0,"accepted": 1,"driver_arrived": 2,"in_progress": 3,"completed": 4,"rejected": -1,"cancelled": -1
 };
 
 const getStatusPriority = (status) => statusPriority[status] || 0;
 
 // CRITICAL: Always prefer API status if available and valid
 let restoredStatus;
 if (apiStatus && apiStatus !=="pending") {
 restoredStatus = apiStatus;
 } else if (apiStatus ==="pending"&& (locationStatus || currentStatus) && getStatusPriority(locationStatus || currentStatus) >= 1) {
 // API says pending but we have accepted/later status - keep the higher status
 restoredStatus = locationStatus || currentStatus;
 console.warn("⚠️ API returned'pending'but we have higher status - keeping:", restoredStatus);
 } else if (locationStatus && locationStatus !=="pending") {
 restoredStatus = locationStatus;
 } else if (currentStatus && currentStatus !=="pending") {
 restoredStatus = currentStatus;
 } else {
 restoredStatus = apiStatus || locationStatus || currentStatus ||"pending";
 }
 
 const isReserveBooking = fetchedBooking.bookingType ==="reserve"|| locationBooking?.bookingType ==="reserve";
 
 console.log("📊 Restoring booking status from API (location.state flow):", restoredStatus);
 console.log("📊 Status sources:", {
 apiStatus: apiStatus,
 locationStatus: locationStatus,
 currentStatus: currentStatus,
 finalStatus: restoredStatus,
 apiPriority: getStatusPriority(apiStatus ||""),
 locationPriority: getStatusPriority(locationStatus ||""),
 currentPriority: getStatusPriority(currentStatus ||""),
 note: apiStatus && apiStatus !=="pending"?"Using API status (source of truth)": 
 locationStatus && locationStatus !=="pending"?"Using location.state status":
 currentStatus && currentStatus !=="pending"?"Keeping current status":"Using pending (default)"});
 console.log("📅 Is reserve booking:", isReserveBooking);
 if (isReserveBooking) {
 console.log("📅 Reserve booking - status should be preserved:", restoredStatus);
 }
 
 // CRITICAL: Always set the status from API to ensure UI reflects correct state
 setBookingStatus(restoredStatus);
 setHasServerSynced(true);
 
 // Set accepted fare if available
 if (fetchedBooking.fare || fetchedBooking.acceptedFare) {
 setAcceptedFare(fetchedBooking.fare || fetchedBooking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 const bookingStatusForOtp = restoredStatus || fetchedBooking.status;
 if ((bookingStatusForOtp ==="driver_arrived"|| bookingStatusForOtp ==="in_progress")) {
 const extractedOtp = fetchedBooking.otp?.code || fetchedBooking.otp || fetchedBooking.otpCode;
 if (extractedOtp) {
 const safeOtp = extractOtpCode(extractedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Setting OTP from initial data (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Booking status is", bookingStatusForOtp,"- OTP will be generated when driver arrives");
 }
 
 setLoading(false);
 
 // Save to localStorage with status
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: locationDriver,
 // Priority: fetchedBooking.pickupLocation (Google Places) > locationPickup
 pickupLocation: fetchedBooking.pickupLocation || locationPickup || null,
 dropoffLocation: fetchedBooking.dropoffLocation || locationDropoff || null,
 bookingId,
 status: restoredStatus, // Save status for refresh recovery
 })
 );
 return;
 }
 } catch (error) {
 console.error("Error fetching full booking details:", error);
 // Continue with location.state data if fetch fails
 }
 }
 
 // Fallback: Use location.state data if fetch fails or bookingId not available
 setBooking(locationBooking);
 setDriver(locationDriver);
 // Priority: booking.pickupLocation (Google Places) > locationPickup (NEVER use pickupLocationName for state)
 const finalPickupLocation = locationBooking.pickupLocation || locationPickup || null;
 console.log("📍 Setting pickupLocation state to:", finalPickupLocation);
 setPickupLocation(finalPickupLocation);
 // Priority: booking.dropoffLocation > locationDropoff
 setDropoffLocation(
 locationBooking.dropoffLocation || 
 locationDropoff || 
 null
 );
 
 // CRITICAL: Restore booking status from location.state booking data
 // For reserve bookings:"accepted"means driver accepted,"driver_arrived"means driver arrived
 // For regular bookings:"accepted"means trip started,"driver_arrived"means driver arrived
 const restoredStatus = locationBooking.status ||"pending";
 const isReserveBooking = locationBooking.bookingType ==="reserve";
 console.log("📊 Restoring booking status from location.state:", restoredStatus);
 console.log("📅 Is reserve booking:", isReserveBooking);
 if (isReserveBooking) {
 console.log("📅 Reserve booking - status should be preserved:", restoredStatus);
 }
 setBookingStatus(restoredStatus);
 
 // Set accepted fare if available
 if (locationBooking.fare || locationBooking.acceptedFare) {
 setAcceptedFare(locationBooking.fare || locationBooking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (restoredStatus ==="driver_arrived"|| restoredStatus ==="in_progress") {
 const extractedOtp = locationBooking.otp?.code || locationBooking.otp || locationBooking.otpCode;
 if (extractedOtp) {
 const safeOtp = extractOtpCode(extractedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Setting OTP from location.state (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Booking status is", restoredStatus,"- OTP will be generated when driver arrives");
 }
 
 setLoading(false);

 // Save to localStorage for page refresh recovery with status
 if (bookingId) {
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: locationBooking,
 driver: locationDriver,
 // Priority: booking.pickupLocation (Google Places) > locationPickup > booking.pickupLocationName
 pickupLocation: locationBooking.pickupLocation || locationPickup || locationBooking.pickupLocationName || null,
 dropoffLocation: locationBooking.dropoffLocation || locationDropoff || null,
 bookingId,
 status: restoredStatus, // Save status for refresh recovery
 })
 );
 }
 return;
 }

 // Check for trip_started_booking first (from socket notification)
 const tripStartedData = localStorage.getItem("trip_started_booking");
 if (tripStartedData) {
 try {
 const parsed = JSON.parse(tripStartedData);
 const bookingId =
 parsed.bookingId || parsed.booking?.id || parsed.booking?._id;

 if (bookingId && parsed.booking) {
 console.log("🚗 Using trip_started booking data from localStorage:",
 bookingId
 );
 // For reserve bookings, set status to driver_arrived when trip starts
 // For regular bookings, set to accepted
 const initialStatus =
 parsed.booking.bookingType ==="reserve"?"driver_arrived":"accepted";

 // CRITICAL: Always fetch full booking data from API to ensure we have correct pickupLocation (Google Places address)
 console.log("📋 Fetching full booking data for trip_started to ensure correct pickupLocation...");
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: {'Authorization':`Bearer ${token}`}
 }
 );
 
 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 console.log("✅ Fetched full booking data for trip_started:", {
 pickupLocation: fetchedBooking.pickupLocation,
 pickupLocationName: fetchedBooking.pickupLocationName,
 dropoffLocation: fetchedBooking.dropoffLocation
 });
 
 // Use fetched booking data (has correct pickupLocation from backend)
 // CRITICAL: For reserve bookings, use actual status from API (could be"accepted"or"driver_arrived")
 // For regular bookings, use status from API or initialStatus
 const actualStatus = fetchedBooking.status || initialStatus;
 console.log("📊 Actual booking status from API (trip_started):", actualStatus);
 console.log("📅 Is reserve booking:", parsed.booking.bookingType ==="reserve");
 
 const bookingWithStatus = {
 ...fetchedBooking,
 status: actualStatus,
 };

 setBooking(bookingWithStatus);
 // Priority: fetchedBooking.pickupLocation (Google Places) - NEVER use pickupLocationName for state
 const finalPickupLocation = fetchedBooking.pickupLocation || null;
 console.log("📍 Setting pickupLocation state to:", finalPickupLocation);
 setPickupLocation(finalPickupLocation);
 setDropoffLocation(fetchedBooking.dropoffLocation || null);
 setBookingStatus(actualStatus);
 
 // Set accepted fare if available
 if (fetchedBooking.fare || fetchedBooking.acceptedFare) {
 setAcceptedFare(fetchedBooking.fare || fetchedBooking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (actualStatus ==="driver_arrived"|| actualStatus ==="in_progress") {
 const extractedOtp = fetchedBooking.otp?.code || fetchedBooking.otp || fetchedBooking.otpCode;
 if (extractedOtp) {
 const safeOtp = extractOtpCode(extractedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Setting OTP from trip_started data (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Booking status is", actualStatus,"- OTP will be generated when driver arrives");
 }
 
 // Save to localStorage with actual status
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: parsed.driver || null,
 // Priority: fetchedBooking.pickupLocation (Google Places)
 pickupLocation: fetchedBooking.pickupLocation || null,
 dropoffLocation: fetchedBooking.dropoffLocation || null,
 bookingId,
 status: actualStatus, // Save actual status for refresh recovery
 })
 );
 } else {
 // Fallback: Use parsed.booking if API fetch fails
 console.warn("⚠️ API fetch failed, using localStorage data");
 // Use status from parsed booking or initialStatus
 const fallbackStatus = parsed.booking?.status || initialStatus;
 console.log("📊 Using fallback status (trip_started):", fallbackStatus);
 
 const bookingWithStatus = {
 ...parsed.booking,
 status: fallbackStatus,
 };
 setBooking(bookingWithStatus);
 const finalPickupLocation = parsed.booking.pickupLocation || null;
 setPickupLocation(finalPickupLocation);
 setDropoffLocation(parsed.booking.dropoffLocation || null);
 setBookingStatus(fallbackStatus);
 
 // Set accepted fare if available
 if (parsed.booking.fare || parsed.booking.acceptedFare) {
 setAcceptedFare(parsed.booking.fare || parsed.booking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (fallbackStatus ==="driver_arrived"|| fallbackStatus ==="in_progress") {
 const extractedOtp = parsed.booking.otp?.code || parsed.booking.otp || parsed.booking.otpCode || parsed.otp;
 if (extractedOtp) {
 const safeOtp = extractOtpCode(extractedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Setting OTP from trip_started fallback (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Booking status is", fallbackStatus,"- OTP will be generated when driver arrives");
 }
 }
 } catch (error) {
 console.error("Error fetching full booking details for trip_started:", error);
 // Fallback: Use parsed.booking if API fetch fails
 // Use status from parsed booking or initialStatus
 const fallbackStatus = parsed.booking?.status || initialStatus;
 console.log("📊 Using fallback status (trip_started error):", fallbackStatus);
 
 const bookingWithStatus = {
 ...parsed.booking,
 status: fallbackStatus,
 };
 setBooking(bookingWithStatus);
 const finalPickupLocation = parsed.booking.pickupLocation || null;
 setPickupLocation(finalPickupLocation);
 setDropoffLocation(parsed.booking.dropoffLocation || null);
 setBookingStatus(fallbackStatus);
 
 // Set accepted fare if available
 if (parsed.booking.fare || parsed.booking.acceptedFare) {
 setAcceptedFare(parsed.booking.fare || parsed.booking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (fallbackStatus ==="driver_arrived"|| fallbackStatus ==="in_progress") {
 const extractedOtp = parsed.booking.otp?.code || parsed.booking.otp || parsed.booking.otpCode || parsed.otp;
 if (extractedOtp) {
 const safeOtp = extractOtpCode(extractedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Setting OTP from trip_started error fallback (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Booking status is", fallbackStatus,"- OTP will be generated when driver arrives");
 }
 }

 // Ensure negotiation popup is closed when trip starts
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);

 if (parsed.booking.fare) {
 setAcceptedFare(parsed.booking.fare);
 // If fare is set, mark negotiation as accepted to prevent popup
 setNegotiationAccepted(true);
 console.log("💰 Fare found in trip_started booking - marking negotiation as accepted");
 }

 // Extract OTP from trip_started booking data
 const tripStartedOtp =
 parsed.booking.otp?.code || parsed.booking.otp || parsed.otp;
 if (tripStartedOtp) {
 console.log("🔐 Setting OTP from trip_started localStorage data:",
 tripStartedOtp
 );
 const extractedOtp = extractOtpCode(tripStartedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 }
 } else {
 // If OTP not in localStorage, fetch from API
 const fetchOtpFromApi = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 if (response.data.success && response.data.booking) {
 const fetchedOtp =
 response.data.booking.otp?.code ||
 response.data.booking.otp ||
 response.data.booking.otpCode;
 if (fetchedOtp) {
 console.log("🔐 Setting OTP from API after trip_started:",
 fetchedOtp
 );
 const extractedOtp = extractOtpCode(fetchedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 }
 }
 }
 } catch (error) {
 console.error("Error fetching OTP from API:", error);
 }
 };
 fetchOtpFromApi();
 }

 setLoading(false);
 // Clear the trip_started_booking after using it
 localStorage.removeItem("trip_started_booking");
 return;
 }
 } catch (error) {
 console.error("❌ Error parsing trip_started_booking data:", error);
 localStorage.removeItem("trip_started_booking");
 }
 }

 // CRITICAL: Direct URL access / refresh support:
 // If bookingId is present in URL query params, fetch from backend (no cache).
 if (bookingIdFromQuery) {
 try {
 console.log("🔄 Fetching booking from query param bookingId:", bookingIdFromQuery);
 setLoading(true);
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(Api.Booking.getBooking(bookingIdFromQuery), {
 headers: {
 Authorization:`Bearer ${token}`},
 params: { _ts: Date.now() }
 });

 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 setBooking(fetchedBooking);
 setPickupLocation(fetchedBooking.pickupLocation || null);
 setDropoffLocation(fetchedBooking.dropoffLocation || null);
 setBookingStatus(fetchedBooking.status ||"pending");
 setHasServerSynced(true);

 // Persist minimal data for subsequent refreshes
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: null,
 pickupLocation: fetchedBooking.pickupLocation || null,
 dropoffLocation: fetchedBooking.dropoffLocation || null,
 bookingId: bookingIdFromQuery,
 status: fetchedBooking.status ||"pending",
 })
 );

 setLoading(false);
 return;
 }
 } catch (err) {
 console.error("❌ Error fetching booking from query param:", err);
 // fall through to localStorage recovery / error
 } finally {
 setLoading(false);
 }
 }

 // Try to get booking ID from localStorage
 const savedData = localStorage.getItem("current_booking_data");
 if (savedData) {
 try {
 const parsed = JSON.parse(savedData);
 const bookingId =
 bookingIdFromQuery || parsed.bookingId || parsed.booking?.id || parsed.booking?._id;

 if (bookingId) {
 console.log("🔄 Recovering booking data from localStorage, fetching from API:",
 bookingId
 );
 console.log("📊 Saved status in localStorage:", parsed.status || parsed.booking?.status);
 
 // CRITICAL: IMMEDIATELY restore ALL booking data from localStorage BEFORE API call
 // This ensures UI shows correct status immediately on normal refresh
 const savedBooking = parsed.booking || {};
 const savedStatus = parsed.status || parsed.booking?.status ||"pending";
 const savedDriver = parsed.driver || null;
 const savedPickupLocation = parsed.pickupLocation || savedBooking.pickupLocation || null;
 const savedDropoffLocation = parsed.dropoffLocation || savedBooking.dropoffLocation || null;
 
 console.log("📊 Immediately restoring booking data from localStorage:", {
 status: savedStatus,
 hasBooking: !!savedBooking,
 hasDriver: !!savedDriver,
 pickupLocation: savedPickupLocation
 });
 
 // IMMEDIATELY restore booking and status to show correct UI
 if (savedBooking && Object.keys(savedBooking).length > 0) {
 setBooking(savedBooking);
 }
 if (savedDriver) {
 setDriver(savedDriver);
 }
 if (savedPickupLocation) {
 setPickupLocation(savedPickupLocation);
 }
 if (savedDropoffLocation) {
 setDropoffLocation(savedDropoffLocation);
 }
 
 // CRITICAL: Restore status immediately - this prevents"waiting"status flash
 console.log("📊 Immediately restoring status from localStorage:", savedStatus);
 setBookingStatus(savedStatus);
 
 // Restore fare if available
 if (savedBooking.fare || savedBooking.acceptedFare) {
 setAcceptedFare(savedBooking.fare || savedBooking.acceptedFare);
 setNegotiationAccepted(true);
 }
 
 // CRITICAL: Restore OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT restored when driver accepts (status: accepted)
 if (savedStatus ==="driver_arrived"|| savedStatus ==="in_progress") {
 const savedOtp = savedBooking.otp?.code || savedBooking.otp || savedBooking.otpCode;
 if (savedOtp) {
 const safeOtp = extractOtpCode(savedOtp);
 if (safeOtp) {
 setOtpCode(safeOtp);
 console.log("🔐 Restoring OTP from localStorage (driver arrived):", safeOtp);
 }
 }
 } else {
 console.log("📅 Saved status is", savedStatus,"- OTP will be generated when driver arrives");
 }
 
 // Set loading to false initially since we have data from localStorage
 // Will set to true only if we need to fetch fresh data
 setLoading(false);

 // Fetch booking from API
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: {
 Authorization:`Bearer ${token}`,
 },
 // Cache buster to avoid browser/proxy returning stale status on soft refresh
 params: { _ts: Date.now() }
 }
 );

 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 console.log("✅ Fetched booking from API:", fetchedBooking);
 console.log("📊 Booking status from API:", fetchedBooking.status);
 console.log("📅 Booking type:", fetchedBooking.bookingType ||"book-now");
 
 const isReserveBooking = fetchedBooking.bookingType ==="reserve";
 if (isReserveBooking) {
 console.log("📅 Reserve booking detected - status should be preserved:", fetchedBooking.status);
 }

 setBooking(fetchedBooking);
 // Priority: pickupLocation (Google Places address) - NEVER use pickupLocationName for state
 console.log("📍 Fetched booking data:", {
 pickupLocation: fetchedBooking.pickupLocation,
 pickupLocationName: fetchedBooking.pickupLocationName
 });
 const finalPickupLocation = fetchedBooking.pickupLocation || null;
 console.log("📍 Setting pickupLocation state to:", finalPickupLocation);
 setPickupLocation(finalPickupLocation);
 setDropoffLocation(fetchedBooking.dropoffLocation ||"");
 
 // CRITICAL: Restore booking status from API - use actual status from backend
 // For reserve bookings:"accepted"means driver accepted,"driver_arrived"means driver arrived
 // For regular bookings:"accepted"means trip started,"driver_arrived"means driver arrived
 const apiStatus = fetchedBooking.status;
 // Use the savedStatus we already extracted above (from localStorage)
 const currentStatus = bookingStatus; // Current state (already restored from localStorage above)
 
 // Status priority order (never downgrade from accepted to pending)
 const statusPriority = {"pending": 0,"accepted": 1,"driver_arrived": 2,"in_progress": 3,"completed": 4,"rejected": -1,"cancelled": -1
 };
 
 const getStatusPriority = (status) => statusPriority[status] || 0;
 
 // CRITICAL: Always prefer API status if available and valid
 let restoredStatus;
 if (apiStatus && apiStatus !=="pending") {
 // API has a valid non-pending status - use it
 restoredStatus = apiStatus;
 } else if (apiStatus ==="pending"&& (savedStatus || currentStatus) && getStatusPriority(savedStatus || currentStatus) >= 1) {
 // API says pending but we have accepted/later status - keep the higher status
 restoredStatus = savedStatus || currentStatus;
 console.warn("⚠️ API returned'pending'but we have higher status - keeping:", restoredStatus);
 } else if (savedStatus && savedStatus !=="pending") {
 // Use saved status if API doesn't have valid status
 restoredStatus = savedStatus;
 } else if (currentStatus && currentStatus !=="pending") {
 // Keep current status if it's already accepted or later
 restoredStatus = currentStatus;
 } else {
 // Only use pending if nothing else is available
 restoredStatus = apiStatus || savedStatus || currentStatus ||"pending";
 }
 
 console.log("📊 Restoring booking status from API (page refresh):", restoredStatus);
 console.log("📊 Status sources:", {
 apiStatus: apiStatus,
 savedStatus: savedStatus,
 currentStatus: currentStatus,
 finalStatus: restoredStatus,
 apiPriority: getStatusPriority(apiStatus ||""),
 savedPriority: getStatusPriority(savedStatus ||""),
 currentPriority: getStatusPriority(currentStatus ||""),
 note: apiStatus && apiStatus !=="pending"?"Using API status (source of truth)": 
 savedStatus && savedStatus !=="pending"?"Using saved status (API pending)":
 currentStatus && currentStatus !=="pending"?"Keeping current status (no valid API/saved)":"Using pending (default)"});
 
 // CRITICAL: Always set the status to ensure UI reflects correct state
 // This is especially important for"accepted"status after page refresh
 setBookingStatus(restoredStatus);
 setHasServerSynced(true);

 // Set accepted fare if available
 if (fetchedBooking.fare || fetchedBooking.acceptedFare) {
 setAcceptedFare(
 fetchedBooking.fare || fetchedBooking.acceptedFare
 );
 // If fare is set, mark negotiation as accepted to prevent popup
 setNegotiationAccepted(true);
 console.log("💰 Fare found in booking - marking negotiation as accepted");
 }
 // CRITICAL: Extract OTP ONLY if driver has arrived (status: driver_arrived or in_progress)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (restoredStatus ==="driver_arrived"|| restoredStatus ==="in_progress") {
 const extractedOtp =
 fetchedBooking.otp?.code ||
 fetchedBooking.otp ||
 fetchedBooking.otpCode;
 if (extractedOtp) {
 console.log("🔐 Setting OTP code from fetched booking (driver arrived):",
 extractedOtp
 );
 setOtpCode(extractedOtp);
 } else {
 console.log("⚠️ No OTP found in fetched booking (driver arrived)");
 }
 } else {
 console.log("📅 Booking status is", restoredStatus,"- OTP will be generated when driver arrives");
 }

 // Fetch driver data if driverId exists
 if (fetchedBooking.driverId) {
 try {
 const driverResponse = await axios.get(
 Api.Driver.profile(fetchedBooking.driverId),
 {
 headers: {
 Authorization:`Bearer ${token}`},
 params: { _ts: Date.now() }
 }
 );

 if (
 driverResponse.data.success &&
 driverResponse.data.driver
 ) {
 console.log("✅ Fetched driver from API:",
 driverResponse.data.driver
 );
 const fetchedDriver = driverResponse.data.driver;
 setDriver(fetchedDriver);

 // Update localStorage with fresh data including driver and status
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: fetchedDriver,
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 pickupLocation:
 fetchedBooking.pickupLocation ||
 fetchedBooking.pickupLocationName ||"",
 dropoffLocation: fetchedBooking.dropoffLocation ||"",
 bookingId,
 status: fetchedBooking.status ||"pending", // Save status for refresh recovery
 })
 );
 }
 } catch (driverError) {
 console.error("❌ Error fetching driver:", driverError);
 // Continue without driver data
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: null,
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 pickupLocation:
 fetchedBooking.pickupLocation ||
 fetchedBooking.pickupLocationName ||"",
 dropoffLocation: fetchedBooking.dropoffLocation ||"",
 bookingId,
 status: fetchedBooking.status ||"pending", // Save status for refresh recovery
 })
 );
 }
 } else {
 // Update localStorage with fresh data (no driver) and status
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: fetchedBooking,
 driver: null,
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 pickupLocation:
 fetchedBooking.pickupLocation ||
 fetchedBooking.pickupLocationName ||"",
 dropoffLocation: fetchedBooking.dropoffLocation ||"",
 bookingId,
 status: fetchedBooking.status ||"pending", // Save status for refresh recovery
 })
 );
 }

 setLoading(false);
 return;
 }
 }
 } catch (error) {
 console.error("❌ Error parsing saved booking data:", error);
 }
 }

 // No booking data found
 console.log("⚠️ No booking data found in location.state or localStorage");
 setLoading(false);
 setError("No booking data found");
 };

 fetchBookingData();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, []); // Run only on mount

 // Timer for elapsed time
 useEffect(() => {
 const timer = setInterval(() => {
 setElapsedTime((prev) => prev + 1);
 }, 1000);

 return () => clearInterval(timer);
 }, []);

 // Track when booking becomes pending to start timeout timer
 useEffect(() => {
 if (bookingStatus ==="pending"&& booking && !bookingStartTime) {
 // Booking just became pending - record start time
 const startTime = Date.now();
 setBookingStartTime(startTime);
 console.log("⏰ Booking is pending - starting timeout timer");
 } else if (bookingStatus !=="pending") {
 // Booking is no longer pending - reset timer
 if (bookingStartTime) {
 console.log("✅ Booking is no longer pending - resetting timeout timer");
 setBookingStartTime(null);
 setShowFindAnotherDriver(false);
 }
 }
 }, [bookingStatus, booking, bookingStartTime]);

 // Ensure booking is loaded when rating modal should be shown
 useEffect(() => {
 if (showRatingModal && !booking && bookingId) {
 console.log("🔄 Rating modal should show but booking is missing - fetching booking data");
 const fetchBookingForRating = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 if (!token) {
 console.error("❌ No auth token found");
 return;
 }
 const response = await axios.get(Api.Booking.getBooking(bookingId), {
 headers: {
 Authorization:`Bearer ${token}`},
 params: { _ts: Date.now() }
 });
 if (response.data.success && response.data.booking) {
 console.log("✅ Booking fetched for rating modal");
 setBooking(response.data.booking);
 }
 } catch (error) {
 console.error("❌ Error fetching booking for rating modal:", error);
 }
 };
 fetchBookingForRating();
 }
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [showRatingModal, booking]); // bookingId is derived from booking, so we don't need it in deps

 // Check timeout and show"Find Another Driver"button
 useEffect(() => {
 if (!booking || bookingStatus !=="pending"|| !bookingStartTime) {
 return; // Don't check if booking is not pending or timer not started
 }

 // Determine timeout duration based on booking type
 const isReserveBooking = booking.bookingType ==="reserve";
 const timeoutDuration = isReserveBooking ? 10 * 60 * 1000 : 5 * 60 * 1000; // 10 min for reserve, 5 min for regular
 const timeoutMinutes = isReserveBooking ? 10 : 5;

 const checkTimeout = () => {
 const currentTime = Date.now();
 const elapsed = currentTime - bookingStartTime;

 // Check if booking is still pending
 if (bookingStatus ==="pending"&& elapsed >= timeoutDuration) {
 console.log(`⏰ Timeout reached (${timeoutMinutes} minutes) - showing Find Another Driver button`);
 setShowFindAnotherDriver(true);
 }
 };

 // Check immediately
 checkTimeout();

 // Check every second
 const interval = setInterval(checkTimeout, 1000);

 return () => clearInterval(interval);
 }, [booking, bookingStatus, bookingStartTime]);

 // Send push notification when"Find Another Driver"button appears
 useEffect(() => {
 if (!showFindAnotherDriver || !booking || notificationSent) {
 return; // Don't send if button not shown, no booking, or already sent
 }

 const bookingId = booking.id || booking._id;
 if (!bookingId || !user) {
 return;
 }

 // Send notification only once
 const sendNotification = async () => {
 try {
 console.log('📱 Sending push notification for Find Another Driver button');
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.post(
 Api.Booking.sendFindAnotherDriverNotification(bookingId),
 {},
 {
 headers: {
 Authorization:`Bearer ${token}`,'Content-Type':'application/json'}
 }
 );

 if (response.data.success) {
 console.log('✅ Find Another Driver push notification sent successfully');
 setNotificationSent(true); // Mark as sent to prevent duplicates
 } else {
 console.warn('⚠️ Failed to send Find Another Driver notification:', response.data.message);
 }
 } catch (error) {
 console.error('❌ Error sending Find Another Driver notification:', error);
 // Don't show error to user - notification is optional
 }
 };

 // Send notification with a small delay to ensure state is ready
 const timer = setTimeout(sendNotification, 500);
 return () => clearTimeout(timer);
 }, [showFindAnotherDriver, booking, user, notificationSent]);

 // CRITICAL: Save booking status to localStorage whenever it changes
 // This ensures status persists across page refreshes
 useEffect(() => {
 if (!booking || !bookingStatus) return;
 // CRITICAL: Only persist after we have synced from backend on this page load.
 // This prevents transient"pending"writes from overwriting an accepted status during soft refresh.
 if (!hasServerSynced) return;
 
 const bookingId = booking.id || booking._id;
 if (!bookingId) return;
 
 // Only save valid statuses (not pending unless it's the initial state)
 const validStatuses = ["accepted","driver_arrived","in_progress","completed","rejected","cancelled"];
 // Do not persist"pending"once we are server-synced; it is too risky for soft refresh.
 if (!validStatuses.includes(bookingStatus)) {
 return; // Don't save invalid statuses
 }
 
 try {
 // Get existing localStorage data
 const savedData = localStorage.getItem("current_booking_data");
 if (savedData) {
 const parsed = JSON.parse(savedData);
 // Update status in localStorage
 const updatedData = {
 ...parsed,
 status: bookingStatus,
 booking: {
 ...(parsed.booking || {}),
 status: bookingStatus
 }
 };
 localStorage.setItem("current_booking_data", JSON.stringify(updatedData));
 console.log("💾 Saved booking status to localStorage:", bookingStatus);
 } else if (booking) {
 // If no saved data, create new entry with current booking and status
 localStorage.setItem("current_booking_data",
 JSON.stringify({
 booking: {
 ...booking,
 status: bookingStatus
 },
 bookingId,
 status: bookingStatus
 })
 );
 console.log("💾 Created new localStorage entry with status:", bookingStatus);
 }
 } catch (error) {
 console.error("❌ Error saving status to localStorage:", error);
 }
 }, [bookingStatus, booking, hasServerSynced]);

 // CRITICAL: OTP is generated ONLY when driver arrives (status: driver_arrived)
 // OTP is NOT generated when driver accepts (status: accepted)
 // This applies to both regular and reserve bookings

 // CRITICAL: Fetch OTP ONLY when driver arrives (status: driver_arrived)
 // This applies to both regular and reserve bookings
 // OTP is NOT generated when driver accepts (status: accepted)
 useEffect(() => {
 // Only proceed if status is driver_arrived and we don't have OTP yet
 if (!booking || bookingStatus !=="driver_arrived") {
 return;
 }

 // If we already have OTP, don't fetch again
 if (otpCode) {
 console.log("🔐 OTP already exists, skipping fetch:", otpCode);
 return;
 }

 const bookingId = booking.id || booking._id;
 if (!bookingId) return;

 // Check if OTP exists in current booking object first
 const existingOtp = booking.otp?.code || booking.otp || booking.otpCode;
 if (existingOtp) {
 console.log("🔐 Found OTP in booking object (driver arrived):", existingOtp);
 const extractedOtp = extractOtpCode(existingOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 console.log("✅ OTP set from booking object:", extractedOtp);
 }
 return;
 }

 // If OTP not found, fetch from API with retry mechanism
 const fetchOtpForDriverArrived = async (retryCount = 0, actualBookingId = null) => {
 try {
 const bookingIdToFetch = actualBookingId || bookingId;
 console.log(`🔍 Fetching OTP for booking (driver arrived) - Attempt ${retryCount + 1}:`, bookingIdToFetch);
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(Api.Booking.getBooking(bookingIdToFetch), {
 headers: { Authorization:`Bearer ${token}`},
 }).catch((fetchError) => {
 // Handle 404 - bookingId might be a rideId, try to find actual bookingId
 if (fetchError.response?.status === 404 && !actualBookingId) {
 console.log('⚠️ Booking not found (404) - bookingId might be rideId, trying to find actual booking...');
 return { data: { success: false, booking: null }, is404: true };
 }
 throw fetchError;
 });

 // Handle 404 case - try to find actual bookingId
 if (response.is404) {
 const rideId = booking.rideId || bookingId;
 
 // Strategy 1: Try fetching ride data first
 try {
 console.log('🔍 Strategy 1: Fetching ride data...');
 const rideResponse = await axios.get(Api.Ride.details(rideId), {
 headers: { Authorization:`Bearer ${token}`},
 });
 
 if (rideResponse.data.success && rideResponse.data.ride) {
 const ride = rideResponse.data.ride;
 console.log('✅ Fetched ride data:', ride._id);
 
 // If ride has driverId assigned, try to find booking with this rideId
 if (ride.driverId) {
 console.log('🔍 Ride has driverId:', ride.driverId);
 }
 }
 } catch (rideError) {
 console.log('⚠️ Could not fetch ride data:', rideError.message);
 }
 
 // Strategy 2: Try to find booking in user's bookings
 try {
 console.log('🔍 Strategy 2: Fetching user bookings to find actual bookingId...');
 const userBookingsResponse = await axios.get(Api.Booking.myBookings, {
 headers: { Authorization:`Bearer ${token}`},
 });
 
 if (userBookingsResponse.data.success && userBookingsResponse.data.bookings) {
 console.log(`📋 Found ${userBookingsResponse.data.bookings.length} user bookings`);
 
 // Find booking with matching rideId, bookingId, or status driver_arrived
 const matchingBooking = userBookingsResponse.data.bookings.find(b => {
 const bId = String(b._id || b.id ||'');
 const bRideId = String(b.rideId ||'');
 const searchBookingId = String(bookingId ||'');
 const searchRideId = String(rideId ||'');
 
 return (
 bId === searchBookingId ||
 bId === searchRideId ||
 bRideId === searchRideId ||
 bRideId === searchBookingId ||
 (b.status ==='driver_arrived'&& bRideId === searchRideId) // Match by status and rideId
 );
 });
 
 if (matchingBooking) {
 const foundBookingId = matchingBooking._id || matchingBooking.id;
 console.log('✅ Found actual bookingId in user bookings:', foundBookingId);
 
 // Retry with actual bookingId
 return fetchOtpForDriverArrived(0, foundBookingId);
 } else {
 console.log('⚠️ No matching booking found in user bookings');
 }
 }
 } catch (bookingsError) {
 console.error('❌ Error fetching user bookings:', bookingsError);
 }
 
 // If we couldn't find actual bookingId, retry with original bookingId
 if (retryCount < 2) {
 const delay = (retryCount + 1) * 2000; // 2s, 4s
 console.log(`⚠️ Could not find actual bookingId - retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
 setTimeout(() => {
 fetchOtpForDriverArrived(retryCount + 1);
 }, delay);
 } else {
 console.log("⚠️ OTP not found after fallback attempts - may need to wait for backend generation");
 }
 return;
 }

 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 const fetchedOtp =
 fetchedBooking.otp?.code ||
 fetchedBooking.otp ||
 fetchedBooking.otpCode;

 if (fetchedOtp) {
 console.log("🔐 Setting OTP from API (driver arrived):",
 fetchedOtp
 );
 const extractedOtp = extractOtpCode(fetchedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 console.log("✅ OTP successfully set:", extractedOtp);

 // Also update booking object with OTP and correct bookingId
 setBooking((prev) => ({
 ...prev,
 _id: fetchedBooking._id || prev._id,
 id: fetchedBooking.id || prev.id,
 otp: fetchedBooking.otp || { code: fetchedOtp },
 }));
 }
 } else {
 // OTP not found yet - retry up to 3 times with increasing delays
 if (retryCount < 3) {
 const delay = (retryCount + 1) * 1000; // 1s, 2s, 3s
 console.log(`⚠️ No OTP found in API response - retrying in ${delay}ms (attempt ${retryCount + 1}/3)`);
 setTimeout(() => {
 fetchOtpForDriverArrived(retryCount + 1, actualBookingId);
 }, delay);
 } else {
 console.log("⚠️ OTP not found after 3 attempts - may need to wait for backend generation");
 }
 }
 }
 } catch (error) {
 console.error("Error fetching OTP (driver arrived):", error);
 // Retry on error up to 2 times
 if (retryCount < 2) {
 const delay = (retryCount + 1) * 2000; // 2s, 4s
 console.log(`⚠️ Error fetching OTP - retrying in ${delay}ms (attempt ${retryCount + 1}/2)`);
 setTimeout(() => {
 fetchOtpForDriverArrived(retryCount + 1, actualBookingId);
 }, delay);
 }
 }
 };

 // Initial fetch with small delay to allow socket events to arrive first
 const timeoutId = setTimeout(() => {
 fetchOtpForDriverArrived(0);
 }, 500);
 return () => clearTimeout(timeoutId);
 }, [booking, bookingStatus, otpCode]);

 // Resend countdown timer
 useEffect(() => {
 let interval = null;
 if (resendCountdown > 0) {
 interval = setInterval(() => {
 setResendCountdown((prev) => {
 if (prev <= 1) {
 return 0;
 }
 return prev - 1;
 });
 }, 1000);
 }
 return () => {
 if (interval) clearInterval(interval);
 };
 }, [resendCountdown]);

 // Debug: Track OTP changes
 useEffect(() => {
 if (otpCode) {
 console.log('🔐 OTP Code state updated:', otpCode);
 }
 }, [otpCode]);

 // Format time for countdown display
 const formatTime = (seconds) => {
 const mins = Math.floor(seconds / 60);
 const secs = seconds % 60;
 return`${mins}:${secs.toString().padStart(2,'0')}`;
 };

 // Handle resend OTP - fetch latest booking data to get updated OTP
 const handleResendOTP = async () => {
 if (resendCountdown > 0 || resending) return;

 const bookingId = booking?.id || booking?._id;
 if (!bookingId) {
 setResendMessage('Booking ID is missing');
 return;
 }

 setResending(true);
 setResendMessage(null);

 try {
 const token = localStorage.getItem("uttaapp_token");
 console.log('🔄 Fetching latest booking data for OTP update...');
 const response = await axios.get(Api.Booking.getBooking(bookingId), {
 headers: { Authorization:`Bearer ${token}`},
 });

 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 const fetchedOtp =
 fetchedBooking.otp?.code ||
 fetchedBooking.otp ||
 fetchedBooking.otpCode;

 console.log('📥 Fetched booking data:', {
 hasOtp: !!fetchedOtp,
 otpType: typeof fetchedOtp,
 otpValue: fetchedOtp
 });

 if (fetchedOtp) {
 const extractedOtp = extractOtpCode(fetchedOtp);
 console.log('🔐 Extracted OTP:', extractedOtp);
 
 if (extractedOtp) {
 const oldOtp = otpCode;
 
 // Update OTP code state
 setOtpCode(extractedOtp);
 
 // Update prevOtpCodeRef for popup logic
 prevOtpCodeRef.current = extractedOtp;
 
 // Update booking object with latest OTP
 setBooking((prev) => {
 const updated = {
 ...prev,
 otp: fetchedBooking.otp || { code: extractedOtp },
 otpCode: extractedOtp
 };
 console.log('📋 Updated booking object with new OTP');
 return updated;
 });

 console.log('✅ OTP updated:', { old: oldOtp, new: extractedOtp });
 setResendMessage('OTP updated successfully');
 
 // Start 60 second countdown
 setResendCountdown(60);
 
 // Clear success message after 5 seconds
 setTimeout(() => {
 setResendMessage(null);
 }, 5000);
 } else {
 console.warn('⚠️ Could not extract OTP from fetched data');
 setResendMessage('OTP not found in response');
 }
 } else {
 console.warn('⚠️ No OTP found in fetched booking data');
 setResendMessage('OTP not available yet. Please wait for driver to arrive.');
 }
 } else {
 console.error('❌ Failed to fetch booking data:', response.data);
 setResendMessage('Failed to fetch booking data');
 }
 } catch (error) {
 console.error('❌ Error resending OTP:', error);
 setResendMessage(
 error.response?.data?.message ||'Failed to update OTP. Please try again.');
 } finally {
 setResending(false);
 }
 };

 // Poll booking status periodically as fallback (in case socket events fail)
 // CRITICAL: Use bookingId instead of booking object to prevent infinite loops
 useEffect(() => {
 if (!booking) return;

 const bookingId = booking.id || booking._id;
 if (!bookingId) return;

 // CRITICAL: Stop polling when trip has started or booking is in final state
 // This prevents infinite loops when booking status updates
 const tripStarted = ["driver_arrived","accepted","in_progress"].includes(bookingStatus);
 const finalState = ["rejected","completed","cancelled"].includes(bookingStatus);
 
 if (tripStarted || finalState) {
 console.log("⏹ Stopping polling - trip started or final state:", bookingStatus);
 return; // Don't poll if trip has started or booking is in final state
 }

 // Use ref to track last fetched status to prevent duplicate updates
 let lastFetchedStatus = bookingStatus;

 const pollBookingStatus = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(Api.Booking.getBooking(bookingId), {
 headers: { Authorization:`Bearer ${token}`},
 });

 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 const fetchedStatus = fetchedBooking.status;
 const cancelledBy = fetchedBooking.negotiation?.cancelledBy || fetchedBooking.cancelledBy;

 // CRITICAL: Handle driver cancellation FIRST (even if trip has started)
 if (fetchedStatus ==="cancelled"&& cancelledBy ==="driver"&& bookingStatus !=="cancelled") {
 console.log("🔄 Polling detected driver cancellation - updating UI");
 setBookingStatus("cancelled");
 setCanCancel(false);
 setBooking(fetchedBooking);
 
 // Store cancelled driver ID to exclude them in NearbyDriversPage
 if (fetchedBooking?.driverId) {
 try {
 localStorage.setItem('cancelled_driver_id', String(fetchedBooking.driverId));
 console.log('💾 Stored cancelled driver ID:', fetchedBooking.driverId);
 } catch (e) {
 console.warn('⚠️ Could not store cancelled driver ID:', e);
 }
 }
 
 // Show popup notification
 setMessageModal({
 show: true,
 message:'Your ride has been cancelled. The driver has rejected your ride.',
 type:'warning',
 title:'Ride Cancelled by Driver'});
 
 // Show"Find Another Driver"button
 setShowFindAnotherDriver(true);
 
 // Close negotiation popup if open
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 
 lastFetchedStatus ="cancelled";
 return; // Exit early after handling cancellation
 }

 // CRITICAL: Stop polling if trip has started OR completed OR cancelled (prevent infinite loop)
 if (["driver_arrived","in_progress","completed","cancelled"].includes(fetchedStatus)) {
 // But continue polling if status is"accepted"to catch cancellations
 if (fetchedStatus ==="accepted") {
 // Continue polling to catch potential driver cancellations
 console.log("⏹ Trip accepted - continuing polling to catch cancellations");
 } else {
 console.log("⏹ Trip status:", fetchedStatus,"- stopping polling");
 return;
 }
 }

 // If booking is rejected and we haven't shown popup yet
 if (
 fetchedStatus ==="rejected"&&
 bookingStatus !=="rejected"&&
 !showRejectionPopup &&
 lastFetchedStatus !=="rejected") {
 console.log("🔄 Polling detected rejected status - showing popup");
 setBookingStatus("rejected");
 setBooking(fetchedBooking);
 setShowRejectionPopup(true);
 lastFetchedStatus ="rejected";
 } else if (
 fetchedStatus !== bookingStatus &&
 fetchedStatus !=="pending"&&
 fetchedStatus !== lastFetchedStatus
 ) {
 // Update status if it changed (but not for pending to avoid unnecessary updates)
 // CRITICAL: Only update if status actually changed to prevent loops
 console.log("🔄 Polling detected status change:", bookingStatus,"->", fetchedStatus);
 setBookingStatus(fetchedStatus);
 // CRITICAL: Only update booking if status actually changed
 // Use functional update to prevent dependency on booking object
 setBooking((prev) => {
 // Only update if status actually changed
 if (prev?.status !== fetchedStatus) {
 return fetchedBooking;
 }
 return prev; // Return previous to prevent unnecessary re-renders
 });
 lastFetchedStatus = fetchedStatus;

 // CRITICAL: OTP is ONLY extracted when driver arrives (status: driver_arrived)
 // OTP is NOT extracted when driver accepts (status: accepted)
 if (fetchedStatus ==="driver_arrived"&& !otpCode) {
 const fetchedOtp =
 fetchedBooking.otp?.code ||
 fetchedBooking.otp ||
 fetchedBooking.otpCode;
 if (fetchedOtp) {
 console.log("🔐 Setting OTP from polling for driver_arrived booking:",
 fetchedOtp
 );
 const extractedOtp = extractOtpCode(fetchedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 otpReceivedTimeRef.current = Date.now();
 console.log("✅ OTP successfully set from polling:", extractedOtp);
 
 // Also update booking object with OTP
 setBooking((prev) => ({
 ...prev,
 otp: fetchedBooking.otp || { code: fetchedOtp },
 }));
 }
 } else {
 console.log("⚠️ OTP not found in polling response - will retry via useEffect");
 }
 }
 
 // Check if status changed from driver_arrived to in_progress (OTP verified)
 if (bookingStatus ==="driver_arrived"&& fetchedStatus ==="in_progress"&& !otpVerificationPopupShownRef.current) {
 console.log("✅ OTP verified successfully - showing success popup (polling detected status change)");
 console.log("📊 Closing pickup OTP popup and showing success popup");
 // Close the pickup OTP popup
 setShowPickupOtpPopup(false);
 setShowOtpSuccessPopup(true);
 otpVerificationPopupShownRef.current = true;
 }
 }
 }
 } catch (error) {
 console.error("Error polling booking status:", error);
 }
 };

 // Poll every 5 seconds only if booking is still pending (not started)
 const pollInterval = setInterval(pollBookingStatus, 5000);

 return () => clearInterval(pollInterval);
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [booking?.id, booking?._id, bookingStatus, showRejectionPopup, otpCode]); // CRITICAL: Use booking.id instead of booking object to prevent infinite loops

 // Check if booking already has negotiation data and show popup
 useEffect(() => {
 // CRITICAL: Never show negotiation popup if:
 // 1. Negotiation was already accepted (user accepted the fare)
 // 2. Fare is already set (negotiation completed)
 // 3. Driver has already arrived (trip started)
 // 4. Ride is completed
 // 5. Ride is in progress
 // 6. Payment is already completed
 // 7. Booking status is'accepted'(when trip starts, status becomes accepted/driver_arrived)

 // If negotiation was already accepted, NEVER show popup again
 if (negotiationAccepted) {
 if (showNegotiationPopup) {
 console.log("🚫 Negotiation already accepted - closing popup permanently");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return; // Exit early - don't show popup
 }

 // If fare is already accepted, don't show negotiation popup
 if (acceptedFare || booking?.fare) {
 if (showNegotiationPopup) {
 console.log("💰 Fare already accepted - closing negotiation popup");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return; // Exit early - fare is already set
 }

 const paymentCompleted =
 booking?.payment?.status ==="completed"||
 booking?.paymentStatus ==="completed"||
 paymentStatus?.status ==="completed";

 // Check both bookingStatus state and booking.status to ensure we catch all cases
 const currentStatus = bookingStatus || booking?.status;
 const tripStarted = ["driver_arrived","in_progress","accepted"].includes(
 currentStatus
 );

 // If trip has started, close any open negotiation popup
 if (tripStarted || currentStatus ==="completed") {
 if (showNegotiationPopup) {
 console.log("🚪 Closing negotiation popup - trip has started or completed. Status:",
 currentStatus
 );
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return; // Don't show popup if trip has started
 }

 // Only show popup if negotiation data exists AND fare is not yet accepted
 if (
 booking?.negotiation?.currentAmount &&
 booking?.negotiation?.proposedBy ==="driver"&&
 !tripStarted &&
 currentStatus !=="completed"&&
 !paymentCompleted &&
 !acceptedFare &&
 !booking?.fare
 ) {
 console.log("💰 Booking has driver proposal - showing negotiation popup:",
 booking.negotiation.currentAmount
 );
 const negotiationBookingData = {
 id: booking.id || booking._id,
 _id: booking._id || booking.id,
 username: booking.username || user?.username,
 driverId: driver?.driverId || booking.driverId,
 driverName: driver?.name || booking.driverName ||"Driver",
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 // NEVER use pickupLocationName if pickupLocation exists
 pickupLocation: booking.pickupLocation || pickupLocation || booking.pickupLocationName ||"",
 dropoffLocation: dropoffLocation || booking.dropoffLocation ||"",
 paymentMethod: booking.paymentMethod ||"cash",
 vehicleType: booking.vehicleType || driver?.vehicleType ||"",
 bookingType: booking.bookingType ||"book-now",
 scheduledStartDate: booking.scheduledStartDate || null,
 scheduledStartTime: booking.scheduledStartTime || null,
 scheduledEndDate: booking.scheduledEndDate || null,
 negotiation: {
 currentAmount: booking.negotiation.currentAmount,
 proposedBy:"driver",
 },
 };
 console.log("📦 Setting existing negotiation data:",
 negotiationBookingData
 );
 setNegotiationBooking(negotiationBookingData);
 setShowNegotiationPopup(true);
 } else if (paymentCompleted) {
 // Payment is completed - ensure negotiation popup is closed
 if (showNegotiationPopup) {
 console.log("💰 Payment completed - closing negotiation popup");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 }
 }, [
 booking,
 bookingStatus,
 driver,
 user,
 pickupLocation,
 dropoffLocation,
 paymentStatus,
 showNegotiationPopup,
 negotiationAccepted,
 acceptedFare,
 ]);

 // Track driver location when booking is accepted (driver accepted fare)
 useEffect(() => {
 if (!driver || !booking) return;
 
 // Only track when status is"accepted"or later
 if (!["accepted","driver_arrived","in_progress"].includes(bookingStatus)) {
 return;
 }

 const driverId = driver.driverId || driver.id || driver._id;
 if (!driverId) {
 console.warn('⚠️ No driver ID available for location tracking');
 return;
 }

 const socket = getSocket();
 if (!socket) {
 console.warn('⚠️ Socket not available for driver location tracking');
 return;
 }

 console.log('📍 Starting driver location tracking for driver:', driverId);
 
 // Listen for driver location updates
 const handleDriverLocationUpdate = (data) => {
 if (data.driverId === driverId || data.driverId === String(driverId)) {
 console.log('📍 Driver location update received:', data);
 setDriverLocation({
 latitude: data.latitude,
 longitude: data.longitude,
 timestamp: data.timestamp || new Date().toISOString()
 });
 }
 };

 socket.on('driver-location-update', handleDriverLocationUpdate);

 // Request initial driver location immediately
 socket.emit('request-driver-location', { driverId });
 console.log('📍 Requested initial driver location for:', driverId);
 
 // Also request periodically (every 5 seconds) to ensure we get updates
 let locationRequestInterval = null;
 locationRequestInterval = setInterval(() => {
 socket.emit('request-driver-location', { driverId });
 console.log('📍 Periodic driver location request for:', driverId);
 }, 5000);

 // Also try to get pickup coordinates from booking
 if (booking.pickupLatitude && booking.pickupLongitude) {
 setPickupCoordinates({
 latitude: booking.pickupLatitude,
 longitude: booking.pickupLongitude
 });
 } else if (booking.pickupLocation || booking.pickupLocationName) {
 // Try to fetch coordinates from place database
 const fetchPickupCoordinates = async () => {
 try {
 const pickupAddress = booking.pickupLocation || booking.pickupLocationName;
 console.log('🔍 Fetching coordinates for pickup location:', pickupAddress);
 const token = localStorage.getItem("uttaapp_token");
 const searchResponse = await axios.get(
 Api.Place.searchPlaces(pickupAddress),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 
 if (searchResponse.data.success && searchResponse.data.places && searchResponse.data.places.length > 0) {
 // Find matching place by address
 const matchingPlace = searchResponse.data.places.find(
 place => 
 place.formattedAddress === pickupAddress || 
 place.name === pickupAddress ||
 place.formattedAddress?.includes(pickupAddress) ||
 pickupAddress.includes(place.formattedAddress || place.name ||'')
 );
 
 if (matchingPlace && matchingPlace.latitude && matchingPlace.longitude) {
 console.log('✅ Found pickup coordinates from place database:', { 
 lat: matchingPlace.latitude, 
 lng: matchingPlace.longitude 
 });
 setPickupCoordinates({ 
 latitude: matchingPlace.latitude, 
 longitude: matchingPlace.longitude 
 });
 } else {
 console.log('📍 Pickup coordinates not found in place database, will use address-based navigation');
 }
 }
 } catch (error) {
 console.warn('⚠️ Could not fetch pickup coordinates from place database:', error);
 // Will use address-based navigation as fallback
 }
 };
 
 fetchPickupCoordinates();
 }

 // Cleanup
 return () => {
 console.log('📍 Stopping driver location tracking');
 socket.off('driver-location-update', handleDriverLocationUpdate);
 if (locationRequestInterval) {
 clearInterval(locationRequestInterval);
 }
 };
 }, [driver, booking, bookingStatus]);

 // Check if booking is completed and payment is pending
 useEffect(() => {
 if (bookingStatus ==="completed"&& booking) {
 const bookingPaymentStatus =
 booking.payment?.status || booking.paymentStatus;
 if (!bookingPaymentStatus || bookingPaymentStatus ==="pending") {
 // Payment is pending, show payment modal after a delay
 const timer = setTimeout(() => {
 setShowPaymentModal(true);
 }, 1500);
 return () => clearTimeout(timer);
 } else {
 setPaymentStatus(booking.payment || { status: bookingPaymentStatus });
 }

 if (
 bookingPaymentStatus ==="completed"&&
 isPaymentFullySettled(booking, booking.payment || { status: bookingPaymentStatus })
 ) {
 setTimeout(() => {
 clearAllBookingStorage();
 console.log("🧹 Cleared all booking data from storage after payment fully settled");
 }, 5000);
 }
 }
 }, [bookingStatus, booking]);

 // CRITICAL: Auto-navigate to dashboard when ride is completed, payment is done, and rating is complete/skipped
 // This ensures the entire ride flow stack is cleared
 useEffect(() => {
 if (bookingStatus ==="completed"&& booking) {
 if (
 isPaymentFullySettled(booking, paymentStatus) &&
 hasRated
 ) {
 const currentPath = window.location.pathname;
 // Only navigate if we're still on a ride-related page
 if (currentPath.includes("/booking-confirmation") || 
 currentPath.includes("/ride-tracking") ||
 currentPath.includes("/payment")) {
 console.log("✅ Ride completed, payment done, rating complete - navigating to dashboard and clearing stack");
 const timer = setTimeout(() => {
 // Set flag to indicate ride completion - this will lock back button on homepage
 sessionStorage.setItem('user_ride_completed','true');
 localStorage.setItem('user_ride_completed','true');
 // Clear ALL booking-related storage to ensure fresh start for next ride
 clearAllBookingStorage();
 // Use replace: true to clear the entire ride flow stack
 navigate("/dashboard", { replace: true });
 console.log("🧹 Cleared ride flow stack - back button will not return to ride screens");
 console.log("🔒 Set ride completion flag - homepage back button will be locked");
 }, 2000); // Small delay to allow user to see completion message
 
 return () => clearTimeout(timer);
 }
 }
 }
 }, [bookingStatus, booking, paymentStatus, hasRated, navigate]);

 // Initialize Socket.IO for negotiation updates
 useEffect(() => {
 const token = localStorage.getItem("uttaapp_token");
 if (!token || !booking) return;

 // Get booking ID (support both id and _id)
 const bookingId = booking.id || booking._id;
 if (!bookingId) {
 console.error("❌ No booking ID found");
 return;
 }

 console.log("🔌 Setting up Socket.IO for negotiation, booking ID:",
 bookingId
 );
 console.log("📦 Current booking status:", bookingStatus);
 console.log("📦 Current booking object:", booking);

 // Enhanced Socket.IO configuration for production compatibility
 const apiUrl = getApiUrl();
 console.log("🔌 Initializing Socket.IO for booking:", {
 apiUrl,
 bookingId,
 hasToken: !!token
 });

 // CRITICAL: Production-ready Socket.IO configuration
 const isProduction = apiUrl.includes('https://');
 
 const newSocket = io(apiUrl, {
 auth: { token },
 // CRITICAL: WebSocket first for production HTTPS (better performance)
 transports: isProduction ? ['websocket','polling'] : ['websocket','polling'],
 // Reconnection settings
 reconnection: true,
 reconnectionDelay: 1000,
 reconnectionDelayMax: 5000,
 reconnectionAttempts: 10,
 // Timeout settings for production
 timeout: isProduction ? 20000 : 10000,
 // CRITICAL: Don't force new connection (reuse existing socket connection)
 forceNew: false,
 // Auto-connect
 autoConnect: true,
 // Enable multiplexing
 multiplex: false,
 // CRITICAL: Enable credentials for CORS
 withCredentials: true,
 // CRITICAL: Enable compression
 compression: true
 });

 newSocket.on("connect", () => {
 console.log("✅ User Socket.IO connected for negotiation");
 console.log("🔌 Socket ID:", newSocket.id);
 console.log("📡 Transport:", newSocket.io.engine?.transport?.name ||'unknown');
 console.log("🌐 API URL:", apiUrl);
 console.log("🔐 Using HTTPS:", apiUrl.includes('https://'));
 
 // CRITICAL: Join booking room for real-time updates
 const bookingIdStr = String(bookingId);
 newSocket.emit("join-booking-room", bookingIdStr);
 console.log("📥 User joining booking room:", bookingIdStr);
 
 // Also join ride room if rideId exists and is different (for"notify-all-drivers"flow)
 const rideId = booking.rideId;
 if (rideId && String(rideId) !== bookingIdStr) {
 newSocket.emit("join-booking-room", String(rideId));
 console.log("📥 User also joining ride room:", rideId);
 }
 
 // Listen for room join confirmation
 newSocket.on("booking-room-joined", (data) => {
 if (data.success) {
 console.log("✅ Successfully joined booking room:", data.bookingId);
 console.log("📋 Room name:", data.roomName);
 } else {
 console.error("❌ Failed to join booking room:", data.message);
 // Retry joining after a delay
 setTimeout(() => {
 console.log("🔄 Retrying to join booking room:", bookingIdStr);
 newSocket.emit("join-booking-room", bookingIdStr);
 }, 1000);
 }
 });
 
 // Verify room join after a short delay
 setTimeout(() => {
 const rooms = Array.from(newSocket.rooms || []);
 console.log("🔍 Socket rooms:", rooms);
 const expectedRoom =`booking-${bookingIdStr}`;
 if (rooms.includes(expectedRoom)) {
 console.log("✅ Verified: Socket is in booking room:", expectedRoom);
 } else {
 console.warn("⚠️ Socket not in expected booking room:", expectedRoom);
 console.warn("⚠️ Current rooms:", rooms);
 // Retry joining
 newSocket.emit("join-booking-room", bookingIdStr);
 }
 }, 1000);
 });

 newSocket.on("connect_error", (error) => {
 console.error("❌ Socket.IO connection error:", {
 message: error.message,
 type: error.type,
 description: error.description,
 apiUrl,
 bookingId
 });
 
 // Log CORS-specific errors
 if ((error.message && error.message.includes('CORS')) || error.message.includes('Origin')) {
 console.error("🚨 CORS Error detected in Socket.IO connection!");
 console.error("This might be a CORS configuration issue.");
 console.error("Check backend Socket.IO CORS settings.");
 }
 });

 newSocket.on("disconnect", (reason) => {
 console.log("❌ User Socket.IO disconnected:", reason);
 
 // Auto-reconnect on server disconnect
 if (reason ==='io server disconnect') {
 console.log("🔄 Server disconnected. Reconnecting...");
 setTimeout(() => {
 newSocket.connect();
 }, 1000);
 }
 });

 newSocket.on("reconnect", (attemptNumber) => {
 console.log(`✅ Socket.IO reconnected after ${attemptNumber} attempts`);
 // Rejoin booking room after reconnection
 newSocket.emit("join-booking-room", bookingId);
 console.log("📥 Rejoined booking room after reconnect:", bookingId);
 });

 newSocket.on("reconnect_error", (error) => {
 console.error("❌ Socket.IO reconnection error:", error.message);
 });

 // Listen for negotiation updates from driver
 newSocket.on("negotiation-update", (data) => {
 console.log("💰 User received negotiation update:", data);
 console.log("🔍 Comparing booking IDs - Received:",
 data.bookingId,"Current:",
 bookingId
 );

 // Convert both to strings for comparison (support both id and _id formats)
 const receivedBookingId = String(
 data.bookingId || data.booking?.id || data.booking?._id ||"");
 const currentBookingId = String(bookingId);

 // Also check if booking IDs match by comparing last few characters (in case of format differences)
 const receivedIdShort = receivedBookingId.slice(-10);
 const currentIdShort = currentBookingId.slice(-10);
 const idsMatch =
 receivedBookingId === currentBookingId ||
 receivedIdShort === currentIdShort;

 console.log("🔍 ID comparison - Full match:",
 receivedBookingId === currentBookingId,"Short match:",
 receivedIdShort === currentIdShort
 );

 // Check if this update is for our booking
 if (idsMatch) {
 // Handle driver cancellation FIRST (before other checks)
 if (data.status ==='cancelled'&& data.cancelledBy ==='driver') {
 console.log('❌ Ride cancelled by driver via negotiation-update');
 setBookingStatus('cancelled');
 setCanCancel(false);
 
 // Store cancelled driver ID to exclude them in NearbyDriversPage
 if (booking?.driverId) {
 try {
 localStorage.setItem('cancelled_driver_id', String(booking.driverId));
 console.log('💾 Stored cancelled driver ID:', booking.driverId);
 } catch (e) {
 console.warn('⚠️ Could not store cancelled driver ID:', e);
 }
 }
 
 // Show popup notification
 setMessageModal({
 show: true,
 message:'Your ride has been cancelled. The driver has rejected your ride.',
 type:'warning',
 title:'Ride Cancelled by Driver'});
 
 // Show"Find Another Driver"button
 setShowFindAnotherDriver(true);
 
 // Close negotiation popup if open
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 
 return; // Exit early, don't process other updates
 }
 
 // CRITICAL: Handle cancellation even if negotiation was accepted (driver can cancel after accepting)
 // Check for cancellation status regardless of negotiationAccepted state
 if (data.status ==='cancelled') {
 // Check if it's driver cancellation (if cancelledBy is not set, check if we have acceptedFare - means driver accepted then cancelled)
 const isDriverCancellation = data.cancelledBy ==='driver'|| 
 (acceptedFare && data.cancelledBy !=='user'&& bookingStatus ==='accepted');
 
 if (isDriverCancellation) {
 console.log('❌ Ride cancelled by driver via negotiation-update (after acceptance)');
 setBookingStatus('cancelled');
 setCanCancel(false);
 
 // Store cancelled driver ID to exclude them in NearbyDriversPage
 if (booking?.driverId) {
 try {
 localStorage.setItem('cancelled_driver_id', String(booking.driverId));
 console.log('💾 Stored cancelled driver ID:', booking.driverId);
 } catch (e) {
 console.warn('⚠️ Could not store cancelled driver ID:', e);
 }
 }
 
 // Show popup notification
 setMessageModal({
 show: true,
 message:'Your ride has been cancelled. The driver has rejected your ride.',
 type:'warning',
 title:'Ride Cancelled by Driver'});
 
 // Show"Find Another Driver"button
 setShowFindAnotherDriver(true);
 
 // Close negotiation popup if open
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 
 return; // Exit early, don't process other updates
 }
 }
 
 // CRITICAL: If negotiation was already accepted, ignore all negotiation updates (except cancellations which are handled above)
 if (negotiationAccepted) {
 console.log("🚫 Negotiation already accepted - ignoring all negotiation updates");
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return;
 }

 // If fare is already accepted, ignore negotiation updates
 if (acceptedFare || booking?.fare) {
 console.log("💰 Fare already accepted - ignoring negotiation updates");
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return;
 }

 // If trip has already started or finished, completely ignore any further negotiation messages
 const tripLockedStatuses = ["driver_arrived","in_progress","completed",
 ];
 const currentStatus = bookingStatus || booking?.status;
 if (currentStatus && tripLockedStatuses.includes(currentStatus) && data.status !=="in_progress") {
 console.log("⏹ Ignoring negotiation update - trip already started or completed. Status:",
 currentStatus
 );
 if (showNegotiationPopup) {
 console.log("🚪 Closing negotiation popup because trip is already in progress or finished");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return;
 }

 // IMPORTANT: Check status first - don't show negotiation popup for status updates
 // Only show negotiation popup for actual fare proposals (when amount is present and status is not driver_arrived)
 if (data.status ==="in_progress") {
 console.log("✅ OTP verified - trip started via negotiation-update");
 setBookingStatus("in_progress");

 // Close the pickup OTP popup and show success popup once
 if (!otpVerificationPopupShownRef.current) {
 setShowPickupOtpPopup(false);
 setShowOtpSuccessPopup(true);
 otpVerificationPopupShownRef.current = true;
 }

 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"in_progress",
 }));
 }

 // Close negotiation popup if it's open
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 return;
 }

 if (data.status ==="driver_arrived"|| data.status ==="accepted") {
 // Driver has arrived at pickup or trip started - don't show negotiation popup
 console.log("🚗 Driver arrived/trip started - status:", data.status);
 console.log("💰 Fare data:", {
 dataFare: data.fare,
 bookingFare: data.booking?.fare,
 hasFare: !!(data.fare || data.booking?.fare)
 });
 
 setBookingStatus(
 data.status ==="driver_arrived"?"driver_arrived":"accepted");

 // CRITICAL: Update accepted fare if provided (for"accepted"status)
 if (data.status ==="accepted") {
 const fareToSet = data.fare || data.booking?.fare;
 if (fareToSet) {
 console.log("💰 Setting accepted fare:", fareToSet);
 setAcceptedFare(fareToSet);
 // Mark negotiation as accepted to prevent popup
 setNegotiationAccepted(true);
 }
 }

 // CRITICAL: Fetch OTP when driver arrives (status: driver_arrived)
 if (data.status ==="driver_arrived") {
 console.log("🚗 Driver arrived via negotiation-update - checking for OTP...");
 console.log("🔍 Full negotiation-update data:", JSON.stringify(data, null, 2));
 
 // First, check if OTP is in the data - check multiple possible locations
 const dataOtp = data.otp || 
 data.booking?.otp?.code || 
 data.booking?.otp || 
 data.booking?.otpCode ||
 (typeof data.booking?.otp ==='string'? data.booking.otp : null);
 
 console.log("🔍 OTP extraction attempt:", {'data.otp': data.otp,'data.booking?.otp?.code': data.booking?.otp?.code,'data.booking?.otp': data.booking?.otp,'data.booking?.otpCode': data.booking?.otpCode,'extractedOtp': dataOtp
 });
 
 if (dataOtp) {
 console.log("🔐 OTP found in negotiation-update data:", dataOtp,"Type:", typeof dataOtp);
 const extractedOtp = extractOtpCode(dataOtp);
 if (extractedOtp) {
 const oldOtp = otpCode;
 setOtpCode(extractedOtp);
 prevOtpCodeRef.current = extractedOtp; // Update ref for popup logic
 console.log("✅ OTP set from negotiation-update:", extractedOtp,"Old OTP:", oldOtp);
 
 // Also update booking object with OTP
 setBooking((prev) => ({
 ...prev,
 otp: { code: extractedOtp, expiresAt: data.booking?.otp?.expiresAt || data.expiresAt },
 otpCode: extractedOtp
 }));
 
 // Only show popup if OTP is new (not a resend of the same OTP)
 if (oldOtp !== extractedOtp) {
 otpReceivedTimeRef.current = Date.now();
 openPickupOtpPopupIfNeeded(extractedOtp);
 }
 } else {
 console.warn("⚠️ OTP found but extractOtpCode returned null/undefined");
 }
 } else {
 // If OTP not in data, fetch from API
 console.log("🔐 OTP not in negotiation-update data, will fetch from API via useEffect");
 }
 }

 // Close negotiation popup if it's open
 if (showNegotiationPopup) {
 console.log("🚪 Closing negotiation popup - driver has arrived/trip started");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }

 // Update booking data if provided
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:
 data.status ==="driver_arrived"?"driver_arrived":"accepted",
 // CRITICAL: Ensure fare is set in booking object
 fare: data.fare || data.booking.fare || prev.fare,
 // CRITICAL: Include OTP if present
 otp: data.booking.otp || data.otp ? { code: data.booking.otp?.code || data.otp } : prev.otp,
 }));
 }
 } else if (data.status ==="completed") {
 // Ride completed
 console.log("✅ Ride completed");
 setBookingStatus("completed");

 // Close negotiation popup if it's open
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 }

 // Update booking data if provided
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"completed",
 }));
 }
 } else if (
 data.proposedBy ==="driver"&&
 data.amount &&
 data.status !=="driver_arrived"&&
 data.status !=="accepted"&&
 data.status !=="in_progress") {
 // Driver sent an offer - show negotiation popup ONLY if:
 // 1. Negotiation was NOT already accepted
 // 2. proposedBy is'driver'// 3. amount is present
 // 4. status is NOT'driver_arrived','accepted', or'in_progress'(to prevent popup when trip starts)
 // 5. payment is NOT completed (to prevent popup after payment confirmation)
 // 6. Current booking status is not trip-started
 // 7. Fare is not already accepted

 // CRITICAL: Never show popup if negotiation was already accepted
 if (negotiationAccepted) {
 console.log("🚫 Negotiation already accepted - ignoring new proposal");
 return;
 }

 // Check if fare is already accepted
 if (acceptedFare || booking?.fare || data.booking?.fare) {
 console.log("💰 Fare already accepted - not showing negotiation popup");
 return;
 }

 const currentStatus = bookingStatus || booking?.status;
 if (
 ["driver_arrived","accepted","in_progress","completed"].includes(
 currentStatus
 )
 ) {
 console.log("⏹ Not showing negotiation popup - trip already started. Current status:",
 currentStatus
 );
 return;
 }

 const paymentCompleted =
 data.booking?.payment?.status ==="completed"||
 data.booking?.paymentStatus ==="completed"||
 paymentStatus?.status ==="completed"||
 booking?.payment?.status ==="completed"||
 booking?.paymentStatus ==="completed";

 if (paymentCompleted) {
 console.log("💰 Payment already completed - not showing negotiation popup");
 return; // Don't show negotiation popup if payment is completed
 }

 console.log("🚗 Driver proposed amount:", data.amount);
 console.log("💰 Full negotiation data:", data);

 // Prepare booking data for NegotiationPopup
 const negotiationBookingData = {
 id: bookingId,
 _id: bookingId,
 username: booking.username || user?.username,
 driverId: driver?.driverId || data.booking?.driverId,
 driverName: driver?.name || data.booking?.driverName ||"Driver",
 // Priority: pickupLocation (Google Places address) > pickupLocationName (pincode search)
 pickupLocation: pickupLocation || booking.pickupLocation || booking.pickupLocationName ||"",
 dropoffLocation: dropoffLocation || booking.dropoffLocation ||"",
 paymentMethod: booking.paymentMethod ||"cash",
 vehicleType: booking.vehicleType || driver?.vehicleType ||"",
 bookingType:
 booking.bookingType ||
 data.booking?.bookingType ||
 data.bookingType ||"book-now",
 scheduledStartDate:
 booking.scheduledStartDate ||
 data.booking?.scheduledStartDate ||
 data.scheduledStartDate ||
 null,
 scheduledStartTime:
 booking.scheduledStartTime ||
 data.booking?.scheduledStartTime ||
 data.scheduledStartTime ||
 null,
 scheduledEndDate:
 booking.scheduledEndDate ||
 data.booking?.scheduledEndDate ||
 data.scheduledEndDate ||
 null,
 // Include negotiation data so popup can initialize with amount
 negotiation: {
 currentAmount: data.amount,
 proposedBy:"driver",
 },
 };

 console.log("📦 Setting negotiation booking data:",
 negotiationBookingData
 );
 setNegotiationBooking(negotiationBookingData);
 setShowNegotiationPopup(true);
 console.log("✅ Negotiation popup should now be visible");
 } else if (data.status ==="confirmed"|| data.status ==="accepted") {
 // Final confirmation - close negotiation and show success
 console.log("✅ Booking accepted! Fare:",
 data.fare
 );
 setBookingStatus("accepted");
 if (data.fare) {
 setAcceptedFare(data.fare);
 }
 // CRITICAL: OTP is NOT set when driver accepts (status: accepted)
 // OTP will be generated ONLY when driver arrives (status: driver_arrived)
 // This applies to both regular and reserve bookings
 console.log("📅 Booking accepted: OTP will be generated when driver arrives and clicks'I arrived'");

 // Update booking data if provided
 if (data.booking) {
 setBooking((prev) => {
 const updated = {
 ...prev,
 ...data.booking,
 status:"accepted",
 };

 // CRITICAL: OTP is NOT extracted from booking object when status is"accepted"// OTP will be generated ONLY when driver arrives (status: driver_arrived)
 // This applies to both regular and reserve bookings
 console.log("📅 Booking accepted: OTP will be generated when driver arrives");

 return updated;
 });
 }

 // CRITICAL: For reserve bookings, ALWAYS navigate to reserve booking details page after fare acceptance
 // Check if message indicates trip started - if so, don't navigate (trip already in progress)
 const isTripStarted =
 data.message && data.message.toLowerCase().includes("trip started");
 const isReserveBookingForNav = data.bookingType ==="reserve"|| booking?.bookingType ==="reserve";
 
 if (
 !isTripStarted &&
 isReserveBookingForNav
 ) {
 // For reserve bookings, navigate to reserve booking details page
 // This ensures user sees the dedicated reserve booking page instead of staying on BookingConfirmationPage
 console.log("📅 Reserve booking accepted! Navigating to reserve booking details page...");
 const reserveBookingId = data.bookingId || bookingId || booking?.id || booking?._id;
 if (reserveBookingId) {
 setTimeout(() => {
 navigate(`/reserve-booking/${reserveBookingId}`);
 }, 1500); // Give time to show success message
 } else {
 console.error("❌ Cannot navigate: Reserve booking ID is missing");
 }
 }
 } else if (data.driverBusy === true || (data.status ==="cancelled"&& data.driverId)) {
 // Driver has accepted another ride - show driver busy popup
 console.log("🚫 Driver busy - driver accepted another ride");
 console.log("📦 Driver busy data received:", data);
 
 // Only show popup if booking is still pending/negotiating
 const currentStatus = bookingStatus || booking?.status;
 if (['pending','negotiating'].includes(currentStatus)) {
 setDriverBusyData({
 driverName: data.driverName || booking?.driverName ||'Driver',
 booking: {
 ...booking,
 pickupPincode: booking?.pickupPincode ||'',
 vehicleType: booking?.vehicleType ||'',
 paymentMethod: booking?.paymentMethod ||'cash',
 bookingType: booking?.bookingType ||'book-now',
 driverId: data.driverId || booking?.driverId
 }
 });
 setShowDriverBusyPopup(true);
 
 // Update booking status to cancelled
 setBookingStatus('cancelled');
 setBooking((prev) => ({
 ...prev,
 status:'cancelled'}));
 }
 } else if (data.status ==="rejected") {
 // Driver rejected the ride - show popup immediately
 console.log("❌ Driver rejected the ride - showing rejection popup");
 console.log("📦 Rejection data received:", data);
 setBookingStatus("rejected");
 // Update booking data if provided, but preserve existing booking data
 if (data.booking) {
 setBooking((prev) => {
 const updated = {
 ...prev,
 ...data.booking,
 status:"rejected",
 // Preserve important fields from previous booking
 pickupPincode:
 prev?.pickupPincode || data.booking.pickupPincode,
 pickupLocationName:
 prev?.pickupLocationName ||
 data.booking.pickupLocationName ||
 data.booking.pickupLocation,
 dropoffLocation:
 prev?.dropoffLocation || data.booking.dropoffLocation,
 vehicleType: prev?.vehicleType || data.booking.vehicleType,
 paymentMethod:
 prev?.paymentMethod || data.booking.paymentMethod,
 bookingType: prev?.bookingType || data.booking.bookingType,
 pickupPhoto: prev?.pickupPhoto || data.booking.pickupPhoto,
 scheduledStartDate:
 prev?.scheduledStartDate || data.booking.scheduledStartDate,
 scheduledStartTime:
 prev?.scheduledStartTime || data.booking.scheduledStartTime,
 scheduledEndDate:
 prev?.scheduledEndDate || data.booking.scheduledEndDate,
 rideId: prev?.rideId || data.booking.rideId,
 // Preserve driverId to exclude rejected driver
 driverId: prev?.driverId || data.booking?.driverId || null,
 };
 console.log("📦 Updated booking data:", updated);
 return updated;
 });
 }
 // Show rejection popup in real-time
 setShowRejectionPopup(true);
 console.log("✅ Rejection popup state set to true");
 } else if (data.status ==="cancelled") {
 // Booking was cancelled - update status and navigate to dashboard
 console.log("❌ Booking cancelled via socket update");
 setBookingStatus("cancelled");
 
 // Update booking data if provided
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"cancelled",
 }));
 }
 
 // Close any open popups
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 
 // CRITICAL: Only navigate to dashboard if user is still on booking-confirmation page
 // If user has already navigated to nearby-drivers, don't auto-navigate
 const currentPath = window.location.pathname;
 if (currentPath.includes("/booking-confirmation") && !currentPath.includes("/nearby-drivers")) {
 console.log("🔄 User still on booking-confirmation page, navigating to dashboard");
 setTimeout(() => {
 // Double-check pathname before navigating (user might have navigated away)
 if (window.location.pathname.includes("/booking-confirmation")) {
 navigate("/dashboard");
 } else {
 console.log("⚠️ User already navigated away, skipping auto-navigation");
 }
 }, 2000);
 } else {
 console.log("⚠️ User already navigated to nearby-drivers or other page, skipping auto-navigation");
 }
 }
 } else {
 // Even if booking IDs don't match, check if it's a rejection for any booking
 // This handles cases where booking ID format might differ
 if (
 data.status ==="rejected"||
 data.type ==="ride_rejected"||
 data.type ==="ride_cancelled") {
 console.log("⚠️ Received rejection event but booking IDs don't match exactly");
 console.log("📦 Rejection data:", data);
 console.log("🔍 Will check booking status via API as fallback");

 // As a fallback, check booking status via API
 const checkBookingStatus = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 if (fetchedBooking.status ==="rejected") {
 console.log("✅ Booking is rejected - showing popup");
 setBookingStatus("rejected");
 setBooking(fetchedBooking);
 setShowRejectionPopup(true);
 }
 }
 } catch (error) {
 console.error("Error checking booking status:", error);
 }
 };
 // Check after a short delay to allow socket event to process first
 setTimeout(checkBookingStatus, 1000);
 }
 }
 });

 // Listen for OTP
 newSocket.on("otp-generated", (data) => {
 console.log("🔐 OTP received via otp-generated event:", data);
 console.log("🔍 OTP event data - bookingType:", data.bookingType,"booking.bookingType:", data.booking?.bookingType);
 const receivedBookingId = String(data.bookingId || data.booking?._id || data.booking?.id ||"");
 const receivedRideId = String(data.rideId || data.booking?.rideId ||"");
 const currentBookingId = String(bookingId);
 const currentRideId = String(booking.rideId ||"");

 // Flexible matching: check if received ID matches current bookingId or rideId
 const bookingIdMatch = receivedBookingId && (
 receivedBookingId === currentBookingId ||
 receivedBookingId === currentRideId
 );
 const rideIdMatch = receivedRideId && (
 receivedRideId === currentBookingId ||
 receivedRideId === currentRideId
 );

 if (bookingIdMatch || rideIdMatch) {
 console.log("✅ OTP received for current booking - OTP:",
 data.otp,"Fare:",
 data.fare,"Status:",
 data.status,"BookingType:",
 data.bookingType || data.booking?.bookingType
 );
 
 // CRITICAL: OTP is ONLY set when driver arrives (status: driver_arrived)
 // OTP is NOT set when driver accepts (status: accepted)
 // This applies to both regular and reserve bookings
 if (data.status ==="driver_arrived") {
 console.log("🚗 Setting booking status to driver_arrived (driver arrived)");
 setBookingStatus("driver_arrived");
 } else if (data.status ==="accepted") {
 console.log("✅ Booking accepted - OTP will be generated when driver arrives");
 setBookingStatus("accepted");
 }
 
 if (data.fare) {
 setAcceptedFare(data.fare);
 }
 
 // CRITICAL: Set OTP when driver arrives (status: driver_arrived) OR when OTP is resent
 // OTP is NOT set when driver accepts (status: accepted)
 // Also update OTP if status is already driver_arrived (for resend cases)
 if (data.otp && (data.status ==="driver_arrived"|| bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress")) {
 console.log("🔐 Setting OTP code (driver arrived or resend):",
 data.otp,"Status:",
 data.status,"Current Booking Status:",
 bookingStatus,"Type:",
 typeof data.otp
 );
 const extractedOtp = extractOtpCode(data.otp);
 if (extractedOtp) {
 const oldOtp = otpCode;
 setOtpCode(extractedOtp);
 prevOtpCodeRef.current = extractedOtp; // Update ref for popup logic
 otpReceivedTimeRef.current = Date.now(); // Track when OTP was received
 
 // Update booking object with new OTP
 if (data.booking?.otp) {
 setBooking((prev) => ({
 ...prev,
 otp: data.booking.otp,
 otpCode: extractedOtp
 }));
 }
 
 console.log("🔐 OTP updated via socket:", { old: oldOtp, new: extractedOtp, timestamp: otpReceivedTimeRef.current });
 
 // Only show popup if OTP is new (not a resend of the same OTP)
 if (oldOtp !== extractedOtp) {
 openPickupOtpPopupIfNeeded(extractedOtp);
 }
 }
 } else if (data.otp && data.status !=="driver_arrived"&& bookingStatus !=="driver_arrived") {
 console.log("⚠️ OTP received but status is not driver_arrived. OTP will be shown when driver arrives.");
 }
 
 // Update booking object with correct status if provided
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status: data.status ==="in_progress"?"in_progress": data.status ==="driver_arrived"?"driver_arrived": (data.booking.status ||"accepted"),
 }));
 }

 // CRITICAL: For reserve bookings, navigate to reserve booking details page ONLY when booking is first accepted
 // Do NOT navigate when driver arrives (status: driver_arrived) - user should stay on current page to see OTP
 const isReserveBookingForNav = data.bookingType ==="reserve"|| data.booking?.bookingType ==="reserve"|| booking?.bookingType ==="reserve";
 const isFirstAcceptance = data.status ==="accepted"&& bookingStatus !=="accepted"&& bookingStatus !=="driver_arrived";
 
 if (isReserveBookingForNav && isFirstAcceptance) {
 console.log("📅 Reserve booking accepted! Navigating to reserve booking details page...");
 setTimeout(() => {
 navigate(`/reserve-booking/${currentBookingId}`);
 }, 1500); // Give time to show success message
 } else if (isReserveBookingForNav && data.status ==="driver_arrived") {
 console.log("🚗 Reserve booking: Driver arrived - staying on current page to show OTP");
 }
 }
 });

 // Listen for status updates
 newSocket.on("new-notification", (data) => {
 console.log("🔔 Notification received:", data);
 console.log("🔍 Notification booking ID check - Received:",
 data.bookingId || data.booking?.id,"Current:",
 bookingId
 );
 
 // More flexible booking ID matching
 const notificationBookingId = String(
 data.bookingId || data.booking?.id || data.booking?._id ||"");
 const currentBookingIdStr = String(bookingId);
 const notificationIdShort = notificationBookingId.slice(-10);
 const currentIdShort = currentBookingIdStr.slice(-10);
 const idsMatch =
 notificationBookingId === currentBookingIdStr ||
 notificationIdShort === currentIdShort ||
 data.bookingId === bookingId ||
 data.booking?.id === bookingId ||
 data.booking?._id === bookingId;

 console.log("🔍 Notification ID match:", idsMatch);

 if (idsMatch) {
 // Handle driver cancellation
 if (data.type ==='booking_cancelled'&& data.cancelledBy ==='driver') {
 console.log('❌ Ride cancelled by driver via new-notification');
 setBookingStatus('cancelled');
 setCanCancel(false);
 
 // Store cancelled driver ID to exclude them in NearbyDriversPage
 if (booking?.driverId) {
 try {
 localStorage.setItem('cancelled_driver_id', String(booking.driverId));
 console.log('💾 Stored cancelled driver ID:', booking.driverId);
 } catch (e) {
 console.warn('⚠️ Could not store cancelled driver ID:', e);
 }
 }
 
 // Show popup notification
 setMessageModal({
 show: true,
 message:'Your ride has been cancelled. The driver has rejected your ride.',
 type:'warning',
 title:'Ride Cancelled by Driver'});
 
 // Show"Find Another Driver"button
 setShowFindAnotherDriver(true);
 
 return; // Exit early, don't process other updates
 }
 
 // Handle user cancellation (existing logic)
 if (data.type ==='booking_cancelled'&& data.cancelledBy ==='user') {
 console.log('✅ User cancelled ride - no action needed');
 return;
 }
 // Check if this notification explicitly indicates OTP verification (trip started)
 const isOtpVerifiedSignal = data.status ==="in_progress"|| data.otpVerified === true;
 if (isOtpVerifiedSignal && !otpVerificationPopupShownRef.current) {
 console.log("✅ OTP verification detected from notification - showing success popup", {
 type: data.type,
 status: data.status,
 otpVerified: data.otpVerified
 });
 // Close the pickup OTP popup
 setShowPickupOtpPopup(false);
 // Ensure status reflects OTP verification
 setBookingStatus("in_progress");
 setTimeout(() => {
 setShowOtpSuccessPopup(true);
 otpVerificationPopupShownRef.current = true;
 console.log("✅ showOtpSuccessPopup set to true from notification");
 }, 100);
 }
 
 if (data.type ==="ride_accepted") {
 console.log("✅ Ride accepted notification");
 setBookingStatus("accepted");
 } else if (data.type ==="trip_started") {
 console.log("🚗 Trip started notification received!");
 console.log("📦 Notification data:", data);

 // Close negotiation popup immediately when trip starts
 if (showNegotiationPopup) {
 console.log("🚪 Closing negotiation popup - trip started");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }

 // Update booking status immediately
 setBookingStatus("accepted");

 // CRITICAL: OTP is NOT extracted from trip_started notification when status is"accepted"// OTP will be generated ONLY when driver arrives (status: driver_arrived)
 console.log("✅ Trip started - OTP will be generated when driver arrives and clicks'I arrived'");

 // CRITICAL: Always fetch full booking data from API to ensure we have correct pickupLocation (Google Places address)
 // This is especially important for reserve bookings when trip starts
 const notificationBookingId = data.bookingId || data.booking?.id || data.booking?._id || booking?.id || booking?._id;
 if (notificationBookingId) {
 console.log("📋 Fetching full booking data for trip_started notification to ensure correct pickupLocation...");
 const fetchFullBooking = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(notificationBookingId),
 {
 headers: {'Authorization':`Bearer ${token}`}
 }
 );
 
 if (response.data.success && response.data.booking) {
 const fetchedBooking = response.data.booking;
 console.log("✅ Fetched full booking data for trip_started notification:", {
 pickupLocation: fetchedBooking.pickupLocation,
 pickupLocationName: fetchedBooking.pickupLocationName,
 dropoffLocation: fetchedBooking.dropoffLocation
 });
 
 // Update booking with fetched data
 setBooking((prev) => ({
 ...prev,
 ...fetchedBooking,
 status: fetchedBooking.status ||"accepted",
 }));
 
 // Priority: fetchedBooking.pickupLocation (Google Places) - NEVER use pickupLocationName for state
 if (fetchedBooking.pickupLocation) {
 setPickupLocation(fetchedBooking.pickupLocation);
 }
 if (fetchedBooking.dropoffLocation) {
 setDropoffLocation(fetchedBooking.dropoffLocation);
 }

 // Update fare if provided
 if (fetchedBooking.fare) {
 setAcceptedFare(fetchedBooking.fare);
 }
 }
 } catch (error) {
 console.error("Error fetching full booking details for trip_started notification:", error);
 // Fallback: Use notification data if fetch fails
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status: data.booking.status ||"accepted",
 }));
 if (data.booking.pickupLocation) {
 setPickupLocation(data.booking.pickupLocation);
 }
 if (data.booking.dropoffLocation) {
 setDropoffLocation(data.booking.dropoffLocation);
 }
 if (data.booking.fare) {
 setAcceptedFare(data.booking.fare);
 }
 }
 }
 };
 
 fetchFullBooking();
 } else if (data.booking) {
 // Fallback: Use notification data if bookingId not available
 console.log("✅ Updating booking with notification data (no bookingId for fetch)");
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status: data.booking.status ||"accepted",
 }));
 if (data.booking.pickupLocation) {
 setPickupLocation(data.booking.pickupLocation);
 }
 if (data.booking.dropoffLocation) {
 setDropoffLocation(data.booking.dropoffLocation);
 }
 if (data.booking.fare) {
 setAcceptedFare(data.booking.fare);
 }
 }
 } else if (data.type ==="driver_arrived") {
 const statusFromNotification = data.status ||"driver_arrived";
 console.log("🚗 Driver arrived notification", { status: statusFromNotification });
 setBookingStatus(statusFromNotification);
 
 // If this is actually an OTP-verified signal, do not re-run arrival logic
 if (statusFromNotification ==="in_progress") {
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"in_progress",
 }));
 }
 return;
 }

 // CRITICAL: Fetch OTP when driver arrives (applies to both regular and reserve bookings)
 console.log("🚗 Driver arrived - checking for OTP...");
 
 // First, check if OTP is already in the notification data
 const notificationOtp = data.otp || data.booking?.otp?.code || data.booking?.otp || data.booking?.otpCode;
 if (notificationOtp) {
 console.log("🔐 OTP found in notification data, setting directly:", notificationOtp);
 const extractedOtp = extractOtpCode(notificationOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 console.log("✅ OTP successfully set from notification:", extractedOtp);
 
 // Also update booking object with OTP and status
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"driver_arrived",
 otp: data.booking.otp || { code: extractedOtp },
 }));
 }
 }
 } else {
 // If OTP not in notification, fetch from API
 console.log("🔐 OTP not in notification, fetching from API...");
 const fetchOTPForDriverArrived = async () => {
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.get(
 Api.Booking.getBooking(bookingId),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 ).catch((fetchError) => {
 // Handle 404 - bookingId might be a rideId, try to find actual bookingId
 if (fetchError.response?.status === 404) {
 console.log('⚠️ Booking not found (404) - trying to find actual bookingId...');
 return { data: { success: false, booking: null }, is404: true };
 }
 throw fetchError;
 });
 
 // Handle 404 case - try to find actual bookingId
 if (response.is404) {
 const rideId = booking.rideId || bookingId;
 
 // Try to find booking in user's bookings
 try {
 console.log('🔍 Fetching user bookings to find actual bookingId...');
 const userBookingsResponse = await axios.get(Api.Booking.myBookings, {
 headers: { Authorization:`Bearer ${token}`},
 });
 
 if (userBookingsResponse.data.success && userBookingsResponse.data.bookings) {
 // Find booking with matching rideId or bookingId
 const matchingBooking = userBookingsResponse.data.bookings.find(b => {
 const bId = String(b._id || b.id ||'');
 const bRideId = String(b.rideId ||'');
 const searchBookingId = String(bookingId ||'');
 const searchRideId = String(rideId ||'');
 
 return (
 bId === searchBookingId ||
 bId === searchRideId ||
 bRideId === searchRideId ||
 bRideId === searchBookingId ||
 (b.status ==='driver_arrived'&& bRideId === searchRideId)
 );
 });
 
 if (matchingBooking) {
 const foundBookingId = matchingBooking._id || matchingBooking.id;
 console.log('✅ Found actual bookingId:', foundBookingId);
 
 // Fetch OTP with actual bookingId
 try {
 const actualResponse = await axios.get(
 Api.Booking.getBooking(foundBookingId),
 {
 headers: { Authorization:`Bearer ${token}`},
 }
 );
 
 if (actualResponse.data.success && actualResponse.data.booking) {
 const fetchedOtp =
 actualResponse.data.booking.otp?.code ||
 actualResponse.data.booking.otp ||
 actualResponse.data.booking.otpCode;
 if (fetchedOtp) {
 console.log("🔐 Setting OTP (driver arrived):", fetchedOtp);
 const extractedOtp = extractOtpCode(fetchedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 console.log("✅ OTP successfully set from API:", extractedOtp);
 
 // Also update booking object with OTP and correct bookingId
 setBooking((prev) => ({
 ...prev,
 _id: actualResponse.data.booking._id || prev._id,
 id: actualResponse.data.booking.id || prev.id,
 otp: actualResponse.data.booking.otp || { code: fetchedOtp },
 status:"driver_arrived",
 }));
 }
 }
 }
 } catch (actualError) {
 console.error("Error fetching OTP with actual bookingId:", actualError);
 }
 }
 }
 } catch (bookingsError) {
 console.error("Error fetching user bookings:", bookingsError);
 }
 return;
 }
 
 if (response.data.success && response.data.booking) {
 const fetchedOtp =
 response.data.booking.otp?.code ||
 response.data.booking.otp ||
 response.data.booking.otpCode;
 if (fetchedOtp) {
 console.log("🔐 Setting OTP (driver arrived):", fetchedOtp);
 const extractedOtp = extractOtpCode(fetchedOtp);
 if (extractedOtp) {
 setOtpCode(extractedOtp);
 console.log("✅ OTP successfully set from API:", extractedOtp);
 
 // Also update booking object with OTP
 setBooking((prev) => ({
 ...prev,
 otp: response.data.booking.otp || { code: fetchedOtp },
 status:"driver_arrived",
 }));
 }
 } else {
 console.log("⚠️ OTP not found in booking - may need to wait for generation");
 }
 }
 } catch (error) {
 console.error("Error fetching OTP (driver arrived):", error);
 }
 };
 fetchOTPForDriverArrived();
 }
 
 // Update booking data if provided in notification
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"driver_arrived",
 }));
 
 // Update pickup/dropoff locations if provided
 if (data.booking.pickupLocation) {
 setPickupLocation(data.booking.pickupLocation);
 }
 if (data.booking.dropoffLocation) {
 setDropoffLocation(data.booking.dropoffLocation);
 }
 }
 } else if (data.type ==="ride_completed") {
 console.log("✅ Ride completed notification");
 setBookingStatus("completed");
 // Show payment modal if payment is pending
 if (
 !data.booking?.payment?.status ||
 data.booking.payment.status ==="pending") {
 setTimeout(() => {
 setShowPaymentModal(true);
 }, 1000);
 } else {
 setPaymentStatus(data.booking.payment);
 }
 } else if (
 data.type ==="ride_cancelled"||
 data.type ==="ride_rejected") {
 console.log("❌ Ride rejected/cancelled notification - showing rejection popup");
 console.log("📦 Rejection notification data received:", data);
 setBookingStatus("rejected");
 // Update booking data if provided, but preserve existing booking data
 if (data.booking) {
 setBooking((prev) => {
 const updated = {
 ...prev,
 ...data.booking,
 status:"rejected",
 // Preserve important fields from previous booking
 pickupPincode:
 prev?.pickupPincode || data.booking.pickupPincode,
 pickupLocationName:
 prev?.pickupLocationName ||
 data.booking.pickupLocationName ||
 data.booking.pickupLocation,
 dropoffLocation:
 prev?.dropoffLocation || data.booking.dropoffLocation,
 vehicleType: prev?.vehicleType || data.booking.vehicleType,
 paymentMethod:
 prev?.paymentMethod || data.booking.paymentMethod,
 bookingType: prev?.bookingType || data.booking.bookingType,
 pickupPhoto: prev?.pickupPhoto || data.booking.pickupPhoto,
 scheduledStartDate:
 prev?.scheduledStartDate || data.booking.scheduledStartDate,
 scheduledStartTime:
 prev?.scheduledStartTime || data.booking.scheduledStartTime,
 scheduledEndDate:
 prev?.scheduledEndDate || data.booking.scheduledEndDate,
 rideId: prev?.rideId || data.booking.rideId,
 };
 console.log("📦 Updated booking data from notification:",
 updated
 );
 return updated;
 });
 }
 // Show rejection popup in real-time
 setShowRejectionPopup(true);
 console.log("✅ Rejection popup state set to true");
 } else if (data.type ==="ride_cancelled"|| data.status ==="cancelled") {
 // Ride was cancelled - update status and navigate to dashboard
 console.log("❌ Ride cancelled notification received");
 setBookingStatus("cancelled");
 
 // Update booking data if provided
 if (data.booking) {
 setBooking((prev) => ({
 ...prev,
 ...data.booking,
 status:"cancelled",
 }));
 }
 
 // Close any open popups
 if (showNegotiationPopup) {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 
 // Close cancel confirmation dialog if open
 if (showCancelConfirm) {
 setShowCancelConfirm(false);
 }
 
 // CRITICAL: Only navigate to dashboard if user is still on booking-confirmation page
 // If user has already navigated to nearby-drivers, don't auto-navigate
 const currentPath = window.location.pathname;
 if (currentPath.includes("/booking-confirmation") && !currentPath.includes("/nearby-drivers")) {
 console.log("🔄 User still on booking-confirmation page, navigating to dashboard");
 setTimeout(() => {
 // Double-check pathname before navigating (user might have navigated away)
 if (window.location.pathname.includes("/booking-confirmation")) {
 navigate("/dashboard");
 } else {
 console.log("⚠️ User already navigated away, skipping auto-navigation");
 }
 }, 2000);
 } else {
 console.log("⚠️ User already navigated to nearby-drivers or other page, skipping auto-navigation");
 }
 } else if (data.type ==="payment_completed"|| data.showRating) {
 console.log("💰 Payment completed notification — awaiting driver confirmation");
 setPaymentStatus((prev) => ({
 ...(prev || {}),
 status:"completed",
 method: data.paymentMethod || data.booking?.payment?.method || data.booking?.paymentMethod,
 driverConfirmedAt: null,
 }));
 } else if (data.type ==="payment_confirmed") {
 console.log("✅ Payment confirmed by driver notification");
 setPaymentStatus({
 status:"completed",
 method: data.paymentMethod || data.booking?.payment?.method || data.booking?.paymentMethod,
 driverConfirmedAt: data.driverConfirmedAt || data.booking?.payment?.driverConfirmedAt || new Date().toISOString(),
 });
 if (!hasRated) {
 setTimeout(() => setShowRatingModal(true), 1500);
 }
 // Close negotiation popup if it's open (payment is confirmed)
 if (showNegotiationPopup) {
 console.log("💰 Payment confirmed - closing negotiation popup");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 }
 }
 });

 // Listen for payment completed event
 newSocket.on("payment-completed", (data) => {
 console.log("💰 Payment completed via Socket.IO:", data);
 if (String(data.bookingId) === String(bookingId)) {
 setPaymentStatus({
 status:"completed",
 method: data.paymentMethod,
 driverConfirmedAt: null,
 });
 if (showNegotiationPopup) {
 console.log("💰 Payment completed - closing negotiation popup");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 }
 });

 newSocket.on("payment_confirmed", (data) => {
 console.log("✅ Payment confirmed by driver via Socket.IO:", data);
 if (String(data.bookingId) === String(bookingId)) {
 setPaymentStatus({
 status:"completed",
 method: data.paymentMethod || data.booking?.payment?.method,
 driverConfirmedAt: data.driverConfirmedAt || data.booking?.payment?.driverConfirmedAt || new Date().toISOString(),
 });
 if (!hasRated) {
 setTimeout(() => setShowRatingModal(true), 1500);
 }
 }
 });

 // CRITICAL: Listen for driver-busy event (when driver accepts another user's ride)
 // Set up listener immediately after socket connection
 const handleDriverBusy = (data) => {
 console.log("🚫 Driver busy event received:", data);
 console.log("📦 Full driver-busy data:", JSON.stringify(data, null, 2));
 
 // Verify this event is for the current booking
 const receivedBookingId = data.bookingId || data.booking?.id || data.booking?._id;
 const currentBookingId = String(bookingId);
 
 console.log("🔍 Comparing booking IDs - Received:", receivedBookingId,"Current:", currentBookingId);
 
 if (String(receivedBookingId) === currentBookingId) {
 console.log("✅ Booking ID matches - showing driver busy popup");
 
 // Only show popup if booking is still pending/negotiating
 const currentStatus = bookingStatus || booking?.status;
 console.log("📊 Current booking status:", currentStatus);
 
 if (['pending','negotiating'].includes(currentStatus)) {
 console.log("✅ Status is pending/negotiating - showing popup");
 
 const busyData = {
 driverName: data.driverName || booking?.driverName ||'Driver',
 booking: {
 ...booking,
 ...(data.booking || {}),
 pickupPincode: data.booking?.pickupPincode || booking?.pickupPincode ||'',
 vehicleType: data.booking?.vehicleType || booking?.vehicleType ||'',
 paymentMethod: data.booking?.paymentMethod || booking?.paymentMethod ||'cash',
 bookingType: data.booking?.bookingType || booking?.bookingType ||'book-now',
 driverId: data.driverId || booking?.driverId
 }
 };
 
 setDriverBusyData(busyData);
 setShowDriverBusyPopup(true);
 
 // Update booking status to cancelled
 setBookingStatus('cancelled');
 setBooking((prev) => ({
 ...prev,
 status:'cancelled'}));
 
 console.log("✅ Driver busy popup state updated");
 } else {
 console.log("⚠️ Booking status is not pending/negotiating, not showing popup");
 }
 } else {
 console.log("⚠️ Booking ID mismatch - ignoring driver-busy event");
 }
 };
 
 // Register listener immediately
 newSocket.on("driver-busy", handleDriverBusy);
 console.log("✅ Registered driver-busy event listener");

 // CRITICAL: Also listen on singleton socket as backup (in case newSocket fails)
 const singletonSocket = getSocket();
 let handleDriverBusyBackup = null;
 
 if (singletonSocket && singletonSocket !== newSocket) {
 handleDriverBusyBackup = (data) => {
 console.log("🚫 Driver busy event received on singleton socket:", data);
 const receivedBookingId = data.bookingId || data.booking?.id || data.booking?._id;
 const currentBookingId = String(bookingId);
 
 if (String(receivedBookingId) === currentBookingId) {
 console.log("✅ Booking ID matches on singleton socket - showing driver busy popup");
 const currentStatus = bookingStatus || booking?.status;
 
 if (['pending','negotiating'].includes(currentStatus)) {
 const busyData = {
 driverName: data.driverName || booking?.driverName ||'Driver',
 booking: {
 ...booking,
 ...(data.booking || {}),
 pickupPincode: data.booking?.pickupPincode || booking?.pickupPincode ||'',
 vehicleType: data.booking?.vehicleType || booking?.vehicleType ||'',
 paymentMethod: data.booking?.paymentMethod || booking?.paymentMethod ||'cash',
 bookingType: data.booking?.bookingType || booking?.bookingType ||'book-now',
 driverId: data.driverId || booking?.driverId
 }
 };
 
 setDriverBusyData(busyData);
 setShowDriverBusyPopup(true);
 setBookingStatus('cancelled');
 setBooking((prev) => ({
 ...prev,
 status:'cancelled'}));
 }
 }
 };
 
 singletonSocket.on("driver-busy", handleDriverBusyBackup);
 console.log("✅ Registered driver-busy event listener on singleton socket as backup");
 }

 return () => {
 // Cleanup singleton listener
 if (singletonSocket && handleDriverBusyBackup) {
 singletonSocket.off("driver-busy", handleDriverBusyBackup);
 }
 
 // Cleanup newSocket
 if (newSocket) {
 const currentBookingId = booking?.id || booking?._id;
 if (currentBookingId) {
 newSocket.emit("leave-booking-room", currentBookingId);
 }
 newSocket.disconnect();
 console.log("🔌 User Socket.IO disconnected");
 }
 };
 // CRITICAL: Use booking?.id instead of booking object to prevent unnecessary re-connections
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [booking?.id || booking?._id, bookingStatus]); // Only re-run if bookingId or status changes, not on every booking object change

 // Listen for booking status updates via Socket.IO (legacy)
 // CRITICAL: Use bookingId instead of booking object to prevent infinite loops
 useEffect(() => {
 if (!booking) return;

 const bookingId = booking.id || booking._id;
 if (!bookingId) return;

 const socket = getSocket();
 if (!socket) return;

 const handleBookingUpdate = (data) => {
 if (String(data.bookingId) === String(bookingId)) {
 console.log("📨 Booking status updated:", data);
 
 // Also check if status changed to in_progress (more reliable indicator)
 if (data.status ==="in_progress"&& bookingStatus ==="driver_arrived"&& !otpVerificationPopupShownRef.current) {
 console.log("✅ OTP verified - status changed to in_progress from booking-update event");
 console.log("📊 Closing pickup OTP popup and showing success popup");
 // Close the pickup OTP popup
 setShowPickupOtpPopup(false);
 setShowOtpSuccessPopup(true);
 otpVerificationPopupShownRef.current = true;
 }
 
 // CRITICAL: Only update status if it actually changed
 if (data.status !== bookingStatus) {
 setBookingStatus(data.status);
 }

 // CRITICAL: Only navigate if status changed and we're not already on the target page
 // Prevent navigation loops
 const currentPath = window.location.pathname;
 
 // If accepted, navigate to ride tracking page (only if not already there and ride is not completed)
 // CRITICAL: Don't navigate to ride-tracking if ride is already completed
 if (data.status ==="accepted"&& 
 !currentPath.includes("/ride-tracking") && 
 bookingStatus !=="completed"&&
 data.status !=="completed") {
 console.log("🚗 Navigating to ride tracking page");
 setTimeout(() => {
 navigate("/ride-tracking", {
 state: { booking: { ...booking, status:"accepted"} },
 });
 }, 2000);
 }

 // If cancelled, show message and go back (only if not already on dashboard or nearby-drivers)
 if (data.status ==="cancelled"&& !currentPath.includes("/dashboard") && !currentPath.includes("/nearby-drivers")) {
 console.log("❌ Booking cancelled - navigating to dashboard");
 setTimeout(() => {
 // Double-check pathname before navigating (user might have navigated away)
 const newPath = window.location.pathname;
 if (newPath.includes("/booking-confirmation") && !newPath.includes("/nearby-drivers")) {
 navigate("/dashboard");
 } else {
 console.log("⚠️ User already navigated away, skipping auto-navigation");
 }
 }, 3000);
 }
 }
 };

 socket.on("booking-update", handleBookingUpdate);

 return () => {
 socket.off("booking-update", handleBookingUpdate);
 };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [booking?.id, booking?._id, bookingStatus, navigate]); // CRITICAL: Use booking?.id instead of booking object to prevent infinite loops

 // Track OTP verification success - show popup when OTP is verified
 const prevStatusForOtpVerificationRef = useRef(bookingStatus);
 const otpVerificationPopupShownRef = useRef(false);
 const bookingIdForOtpRef = useRef(booking?.id || booking?._id);
 const lastBookingUpdateTimeRef = useRef(null);
 
 useEffect(() => {
 const prevStatus = prevStatusForOtpVerificationRef.current;
 const currentStatus = bookingStatus;
 const currentBookingId = booking?.id || booking?._id;
 
 console.log("🔍 OTP Verification Check:", {
 prevStatus,
 currentStatus,
 currentBookingId,
 hasOtp: !!otpCode,
 popupShown: otpVerificationPopupShownRef.current
 });
 
 // Reset popup flag if booking changed
 if (currentBookingId !== bookingIdForOtpRef.current) {
 console.log("🔄 Booking changed - resetting OTP popup flag");
 otpVerificationPopupShownRef.current = false;
 bookingIdForOtpRef.current = currentBookingId;
 lastBookingUpdateTimeRef.current = null;
 }
 
 // Update ref for next comparison
 prevStatusForOtpVerificationRef.current = currentStatus;
 
 // Show success popup when status changes from"driver_arrived"to"in_progress"// This indicates OTP was successfully verified by the driver
 if (prevStatus ==="driver_arrived"&& currentStatus ==="in_progress"&& !otpVerificationPopupShownRef.current) {
 console.log("✅ OTP verified successfully - showing success popup (status changed to in_progress)");
 console.log("📊 Closing pickup OTP popup and showing success popup");
 // Close the pickup OTP popup
 setShowPickupOtpPopup(false);
 setShowOtpSuccessPopup(true);
 otpVerificationPopupShownRef.current = true;
 }
 }, [bookingStatus, booking?.id, booking?._id, otpCode]);

 const handleCancel = () => {
 if (window.confirm("Are you sure you want to cancel this booking?")) {
 // TODO: Call cancel booking API
 navigate("/dashboard");
 }
 };

 // Handle Cancel Ride - Only within 3-minute window after acceptance
 const handleCancelRide = async () => {
 const bookingId = booking?.id || booking?._id;
 if (!bookingId) {
 setMessageModal({ show: true, message:'Booking ID is missing', type:'error', title:'Error'});
 return;
 }

 if (bookingStatus ==="pending") {
 setShowCancelConfirm(true);
 return;
 }

 // Check if cancellation is allowed (status must be"accepted"and within 3-minute window)
 if (bookingStatus !=="accepted") {
 setMessageModal({ 
 show: true, 
 message:'Cancellation is only allowed when the ride is accepted and within 3 minutes.', 
 type:'warning', 
 title:'Cannot Cancel'});
 return;
 }

 // Check 3-minute window
 if (!canCancel) {
 setMessageModal({ 
 show: true, 
 message:'Cancellation is only allowed within 3 minutes after the ride is accepted. The time limit has expired.', 
 type:'warning', 
 title:'Cancellation Not Allowed'});
 return;
 }

 // Show confirmation dialog
 setShowCancelConfirm(true);
 };

 const confirmCancelRide = async () => {
 const bookingId = booking?.id || booking?._id;
 if (!bookingId) {
 setMessageModal({ show: true, message:'Booking ID is missing', type:'error', title:'Error'});
 return;
 }

 // Allow direct cancellation while request is still pending
 if (bookingStatus !=="pending"&& bookingStatus !=="accepted") {
 setMessageModal({
 show: true,
 message:'This ride can no longer be cancelled from the current status.',
 type:'warning',
 title:'Cannot Cancel'});
 setShowCancelConfirm(false);
 return;
 }

 // Double-check cancellation window for accepted rides
 if (bookingStatus ==="accepted"&& !canCancel) {
 setMessageModal({ 
 show: true, 
 message:'Cancellation is only allowed within 3 minutes after the ride is accepted.', 
 type:'warning', 
 title:'Cannot Cancel'});
 setShowCancelConfirm(false);
 return;
 }

 // Check if ride can still be cancelled (not arrived or later)
 if (["driver_arrived","in_progress","completed"].includes(bookingStatus)) {
 setMessageModal({ show: true, message:'Cannot cancel ride - driver has already arrived', type:'warning', title:'Cannot Cancel'});
 setShowCancelConfirm(false);
 return;
 }

 setCancelling(true);
 try {
 const token = localStorage.getItem("uttaapp_token");
 const response = await axios.patch(
 Api.Booking.updateStatus(bookingId),
 {
 status:"cancelled",
 cancelledBy:"user"},
 {
 headers: {
 Authorization:`Bearer ${token}`,'Content-Type':'application/json'},
 }
 );

 if (response.data.success) {
 console.log("✅ Ride cancelled successfully:", response.data);
 
 // Update booking status immediately
 setBookingStatus("cancelled");
 setCanCancel(false);
 setBooking((prev) => ({
 ...prev,
 status:"cancelled",
 }));

 // Close confirmation dialog
 setShowCancelConfirm(false);

 // Show success message in app UI
 setSuccessMessage({
 show: true,
 message:"Your ride has been cancelled successfully. The driver has been notified.",
 title:"Ride Cancelled"});

 // Navigate to rides page after a delay
 setTimeout(() => {
 navigate('/rides');
 }, 2000);
 } else {
 throw new Error(response.data.message ||"Failed to cancel ride");
 }
 } catch (error) {
 console.error("Error cancelling ride:", error);
 setMessageModal({ 
 show: true, 
 message: error.response?.data?.message || error.message ||"Failed to cancel ride. Please try again.", 
 type:'error', 
 title:'Error'});
 } finally {
 setCancelling(false);
 }
 };

 const handleFindAnotherDriver = async () => {
 if (bookingStatus !=="cancelled") {
 await cancelBookingForAnotherDriver();
 }

 const pincodeValue = booking?.pickupPincode || location.state?.pickupPincode;
 const vehicleTypeValue =
 booking?.vehicleType || driver?.vehicleType || location.state?.vehicleType;

 if (!pincodeValue || !vehicleTypeValue) {
 setMessageModal({
 show: true,
 message:"Missing required information (pincode or vehicle type). Please try booking again from the book-ride page.",
 type:"warning",
 title:"Missing Information",
 });
 return;
 }

 const navigationState = {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 paymentMethod:
 booking?.paymentMethod || location.state?.paymentMethod ||"cash",
 pickupLocation:
 pickupLocation || booking?.pickupLocation || location.state?.pickupLocation,
 pickupLocationName:
 booking?.pickupLocationName ||
 location.state?.pickupLocationName ||
 pickupLocation,
 pickupPincode: booking?.pickupPincode || location.state?.pickupPincode,
 pickupLandmark:
 booking?.pickupLandmark || location.state?.pickupLandmark ||"",
 dropoffLocation:
 dropoffLocation ||
 booking?.dropoffLocation ||
 location.state?.dropoffLocation,
 dropoffLandmark:
 booking?.dropoffLandmark || location.state?.dropoffLandmark ||"",
 bookingType: booking?.bookingType || location.state?.bookingType ||"book-now",
 rideId: booking?.rideId || location.state?.rideId || null,
 pickupPhoto: booking?.pickupPhoto || location.state?.pickupPhoto || null,
 startDate:
 booking?.scheduledStartDate || location.state?.startDate || null,
 startTime:
 booking?.scheduledStartTime || location.state?.startTime || null,
 endDate: booking?.scheduledEndDate || location.state?.endDate || null,
 excludedDriverId: (() => {
 try {
 const cancelledDriverId = localStorage.getItem("cancelled_driver_id");
 if (cancelledDriverId) return cancelledDriverId;
 } catch (e) {
 console.warn("Could not read cancelled driver ID:", e);
 }
 return booking?.driverId || driver?.driverId || null;
 })(),
 };

 try {
 localStorage.setItem("nearby_drivers_nav_state",
 JSON.stringify(navigationState)
 );
 } catch (e) {
 console.warn("Could not save navigation state to localStorage:", e);
 }

 navigate("/nearby-drivers", { state: navigationState });
 };

 useEffect(() => {
 if (showFindAnotherDriver || bookingStatus ==="cancelled") {
 setDismissedFindAnotherDriverPopup(false);
 }
 }, [showFindAnotherDriver, bookingStatus]);

 const safeOtpCode = extractOtpCode(otpCode);
 const resolvedPickupLocation =
 booking?.pickupLocation || pickupLocation || booking?.pickupLocationName ||"--";
 const resolvedDropoffLocation =
 booking?.dropoffLocation || dropoffLocation ||"--";
 const displayFare = acceptedFare || booking?.fare || booking?.acceptedFare ||"--";
 const driverRating = getDriverDisplayRating(driver, booking?.driverRating);
 const userPaymentSubmitted = isUserPaymentSubmitted(booking, paymentStatus);
 const driverPaymentConfirmed = isDriverPaymentConfirmed(booking, paymentStatus);
 const paymentFullySettled = isPaymentFullySettled(booking, paymentStatus);
 const canShowMapAction =
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress";
 const canShowCallAction =
 driver?.phone &&
 (bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress") &&
 bookingStatus !=="completed";
 const statusMeta = {
 pending: {
 eyebrow:"Booking in queue",
 title:"Waiting for driver confirmation",
 description:"Your trip is live. We will update this screen as soon as the driver responds.",
 pill:"text-amber-700 bg-amber-50 border-amber-200",
 accent:"from-amber-100 via-white to-orange-50",
 },
 accepted: {
 eyebrow:"Driver assigned",
 title:"Your ride is confirmed",
 description:"Driver details, route progress, and ride actions are ready below.",
 pill:"text-sky-700 bg-sky-50 border-sky-200",
 accent:"from-sky-100 via-white to-cyan-50",
 },
 driver_arrived: {
 eyebrow:"Driver at pickup",
 title:"Share the pickup OTP",
 description:"Your driver has reached the pickup point. Share the OTP to begin the ride.",
 pill:"text-emerald-700 bg-emerald-50 border-emerald-200",
 accent:"from-emerald-100 via-white to-teal-50",
 },
 in_progress: {
 eyebrow:"Ride in progress",
 title:"Trip is underway",
 description:"Live route access and ride details stay available here until completion.",
 pill:"text-emerald-700 bg-emerald-50 border-emerald-200",
 accent:"from-emerald-100 via-white to-lime-50",
 },
 completed: {
 eyebrow:"Trip completed",
 title:"Ride completed successfully",
 description:"Review fare, finish payment if needed, and close out the trip smoothly.",
 pill:"text-slate-700 bg-slate-100 border-slate-200",
 accent:"from-slate-100 via-white to-slate-50",
 },
 cancelled: {
 eyebrow:"Ride cancelled",
 title:"This booking was cancelled",
 description:"You can request another driver whenever you are ready.",
 pill:"text-rose-700 bg-rose-50 border-rose-200",
 accent:"from-rose-100 via-white to-orange-50",
 },
 rejected: {
 eyebrow:"Driver unavailable",
 title:"The booking was rejected",
 description:"You can continue by choosing another nearby driver.",
 pill:"text-rose-700 bg-rose-50 border-rose-200",
 accent:"from-rose-100 via-white to-orange-50",
 },
 };
 const currentStatusMeta = statusMeta[bookingStatus] || statusMeta.pending;
 const progressSteps = [
 {
 key:"pending",
 label:"Requested",
 active:
 bookingStatus ==="pending"||
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed",
 },
 {
 key:"accepted",
 label:"Confirmed",
 active:
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed",
 },
 {
 key:"driver_arrived",
 label:"Arrived",
 active:
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed",
 },
 {
 key:"completed",
 label:"Completed",
 active: bookingStatus ==="completed",
 },
 ];

 // Show loading state
 if (loading) {
 if (isWebView) {
 return (
 <div className="min-h-screen bg-[linear-gradient(180deg,#f7faf8_0%,#eef6f2_52%,#ffffff_100%)] px-4 py-8 text-slate-900">
 <div className="mx-auto max-w-5xl rounded-[32px] border border-emerald-100 bg-white/90 p-10 shadow-[0_28px_80px_-52px_rgba(15,23,42,0.22)]">
 <div className="flex flex-col items-center text-center">
 <div className="flex h-20 w-20 items-center justify-center rounded-[26px] bg-emerald-50">
 <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-100 border-t-emerald-600"/>
 </div>
 <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.3em] text-emerald-700">
 UttaApp Ride Desk
 </p>
 <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] text-slate-950">
 Loading booking details
 </h2>
 <p className="mt-3 max-w-xl text-sm leading-7 text-slate-600">
 We are syncing your driver, fare, and trip status so this page opens in the latest state.
 </p>
 </div>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-gray-50 flex items-center justify-center">
 <div className="text-center">
 <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-900 mx-auto mb-4"></div>
 <p className="text-gray-600">Loading booking details...</p>
 </div>
 </div>
 );
 }

 // Show error state
 if (error || !booking) {
 if (isWebView) {
 return (
 <div className="min-h-screen bg-[linear-gradient(180deg,#f8faf9_0%,#eef6f2_52%,#ffffff_100%)] px-4 py-8 text-slate-900">
 <div className="mx-auto max-w-4xl rounded-[32px] border border-rose-100 bg-white p-10 text-center shadow-[0_28px_80px_-52px_rgba(15,23,42,0.18)]">
 <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-[26px] bg-rose-50 text-rose-600">
 <svg className="h-10 w-10"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 9v2m0 4h.01m-7.938 4h15.876c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
 </svg>
 </div>
 <h2 className="mt-6 text-3xl font-black tracking-[-0.04em] text-slate-950">
 No booking data found
 </h2>
 <p className="mt-3 text-sm leading-7 text-slate-600">
 This session does not have an active trip. Return to the dashboard and start a fresh ride request.
 </p>
 <button
 onClick={() => {
 localStorage.removeItem("current_booking_data");
 navigate("/dashboard");
 }}
 className="mt-6 inline-flex items-center justify-center rounded-[18px] bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800">
 Go to Dashboard
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className="min-h-screen bg-gray-50 flex items-center justify-center">
 <div className="text-center">
 <h2 className="text-xl font-semibold text-gray-900 mb-4">
 No booking data found
 </h2>
 <p className="text-gray-600 mb-4">
 Please go back to dashboard and try again.
 </p>
 <button
 onClick={() => {
 localStorage.removeItem("current_booking_data");
 navigate("/dashboard");
 }}
 className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
 Go to Dashboard
 </button>
 </div>
 </div>
 );
 }

 return (
 <div className={`min-h-screen overflow-x-hidden ${isWebView ?"bg-[linear-gradient(180deg,#f8fbfa_0%,#edf5f1_46%,#ffffff_100%)]":"bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"}`}>
 {isWebView && (
 <>
 <div className="px-4 py-4 md:px-6 md:py-6">
 <div className="mx-auto max-w-7xl">
 <div className="overflow-hidden rounded-[34px] border border-white/70 bg-[linear-gradient(135deg,rgba(255,255,255,0.96)_0%,rgba(244,250,247,0.98)_100%)] shadow-[0_30px_90px_-52px_rgba(15,23,42,0.26)]">
 <div className={`border-b border-emerald-100/80 bg-gradient-to-r ${currentStatusMeta.accent} px-5 py-5 md:px-8 md:py-7`}>
 <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
 <div className="max-w-3xl">
 <div className="inline-flex items-center gap-2 rounded-full border border-white/80 bg-white/80 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.28em] text-emerald-700">
 <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500"/>
 UttaApp Ride Desk
 </div>
 <div className="mt-4 flex flex-wrap items-center gap-3">
 <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${currentStatusMeta.pill}`}>
 {currentStatusMeta.eyebrow}
 </span>
 <span className="inline-flex rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
 Booking #{booking?.id?.slice(-8) || booking?._id?.slice(-8) ||"N/A"}
 </span>
 </div>
 <h1 className="mt-4 text-3xl font-black tracking-[-0.05em] text-slate-950 md:text-[2.8rem]">
 {currentStatusMeta.title}
 </h1>
 <p className="mt-3 max-w-2xl text-sm leading-7 text-slate-600 md:text-[15px]">
 {currentStatusMeta.description}
 </p>
 </div>

 <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 lg:min-w-[360px] lg:flex-1">
 <div className="rounded-[24px] border border-white/80 bg-white/85 px-4 py-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.24)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Status</p>
 <p className="mt-2 text-lg font-black text-slate-950">
 {bookingStatus ==="pending"?"Pending": bookingStatus ==="accepted"?"Accepted": bookingStatus ==="driver_arrived"?"Arrived": bookingStatus ==="in_progress"?"In Progress": bookingStatus ==="completed"?"Completed": bookingStatus ==="cancelled"?"Cancelled":"Rejected"}
 </p>
 </div>
 <div className="rounded-[24px] border border-white/80 bg-white/85 px-4 py-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.24)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Fare</p>
 <p className="mt-2 text-lg font-black text-slate-950">&#8377;{displayFare}</p>
 </div>
 <div className="rounded-[24px] border border-white/80 bg-white/85 px-4 py-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.24)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Payment</p>
 <p className="mt-2 text-lg font-black text-slate-950">
 {paymentFullySettled
 ?"Paid"
 : userPaymentSubmitted
 ?"Awaiting driver"
 : booking?.paymentMethod ||"Cash"}
 </p>
 </div>
 <div className="rounded-[24px] border border-white/80 bg-white/85 px-4 py-4 shadow-[0_18px_48px_-36px_rgba(15,23,42,0.24)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Waiting Time</p>
 <p className="mt-2 text-lg font-black text-slate-950">{formatTime(elapsedTime)}</p>
 </div>
 </div>
 </div>
 </div>

 <div className="grid gap-6 px-5 py-5 md:px-8 md:py-8 xl:grid-cols-[1.35fr,0.85fr]">
 <div className="space-y-6">
 <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr]">
 <div className="rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.16)]">
 <div className="flex items-start justify-between gap-4">
 <div className="flex min-w-0 items-center gap-4">
 <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-[24px] border border-emerald-100 bg-emerald-50">
 {driver?.profilePicture && getImageUrl(driver.profilePicture) ? (
 <img
 src={getImageUrl(driver.profilePicture)}
 alt={driver?.name ||"Driver"}
 className="h-full w-full object-cover"onError={(e) => {
 e.target.style.display ="none";
 if (e.target.nextSibling) e.target.nextSibling.style.display ="flex";
 }}
 />
 ) : null}
 <div
 className="h-full w-full items-center justify-center bg-emerald-50 text-2xl font-black text-emerald-700"style={{ display: driver?.profilePicture && getImageUrl(driver.profilePicture) ?"none":"flex"}}
 >
 {driver?.name?.charAt(0)?.toUpperCase() ||"D"}
 </div>
 </div>
 <div className="min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Assigned Driver</p>
 <h2 className="mt-2 truncate text-2xl font-black tracking-[-0.04em] text-slate-950">
 {driver?.name || booking?.driverName ||"Driver"}
 </h2>
 <div className="mt-3 flex flex-wrap items-center gap-2">
 <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">
 <svg className="h-3.5 w-3.5 text-amber-500"fill="currentColor"viewBox="0 0 20 20">
 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
 </svg>
 {driverRating}
 </span>
 <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
 {booking?.vehicleType || driver?.vehicleType ||"Vehicle"}
 </span>
 {driver?.vehicleNumber && (
 <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
 {driver.vehicleNumber}
 </span>
 )}
 </div>
 </div>
 </div>

 {canShowCallAction && (
 <a
 href={`tel:${driver.phone}`}
 className="inline-flex h-11 w-11 items-center justify-center rounded-[16px] bg-slate-900 text-white transition-colors hover:bg-slate-800"aria-label="Call driver">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
 </svg>
 </a>
 )}
 </div>

 {canShowCallAction && (
 <div className="mt-4 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Driver Contact</p>
 <p className="mt-2 text-sm font-semibold text-slate-800 notranslate"translate="no">
 {String(driver.phone)}
 </p>
 </div>
 )}
 </div>

 <div className="rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.16)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Progress</p>
 <div className="mt-4 space-y-3">
 {progressSteps.map((step, index) => (
 <div key={step.key} className="flex items-center gap-3">
 <div className={`flex h-10 w-10 items-center justify-center rounded-full border text-sm font-bold ${step.active ?"border-emerald-500 bg-emerald-500 text-white":"border-slate-200 bg-slate-50 text-slate-400"}`}>
 {step.active ? (
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2.5} d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 index + 1
 )}
 </div>
 <div className="flex-1 rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
 <p className={`text-sm font-semibold ${step.active ?"text-slate-950":"text-slate-500"}`}>{step.label}</p>
 </div>
 </div>
 ))}
 </div>
 </div>
 </div>

 <div className="rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.16)]">
 <div className="flex items-center justify-between gap-3">
 <div>
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Trip Route</p>
 <h3 className="mt-2 text-xl font-black tracking-[-0.03em] text-slate-950">Pickup and destination</h3>
 </div>
 {canShowMapAction && (
 <button
 onClick={openGoogleMaps}
 className="inline-flex items-center justify-center rounded-[16px] bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
 Open Map
 </button>
 )}
 </div>

 <div className="mt-5 space-y-4">
 <div className="flex gap-4 rounded-[22px] border border-sky-100 bg-sky-50/70 px-4 py-4">
 <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-sky-600 text-white">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 </div>
 <div className="min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-sky-700">Pickup</p>
 <p className="mt-2 text-sm font-semibold leading-6 text-slate-900">{resolvedPickupLocation}</p>
 </div>
 </div>

 <div className="flex gap-4 rounded-[22px] border border-emerald-100 bg-emerald-50/70 px-4 py-4">
 <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-white">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 01.553-.894L9 2m0 18l6-2m-6 2V2m6 16l5.447-2.724A1 1 0 0021 14.382V3.618a1 1 0 00-.553-.894L15 0m0 18V0m-6 2l6-2"/>
 </svg>
 </div>
 <div className="min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-700">Dropoff</p>
 <p className="mt-2 text-sm font-semibold leading-6 text-slate-900">{resolvedDropoffLocation}</p>
 </div>
 </div>
 </div>
 </div>

 </div>

 <div className="space-y-6">
 <div className="rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.16)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Ride Actions</p>
 <div className="mt-4 grid gap-3">
 {canShowMapAction && (
 <button
 onClick={openGoogleMaps}
 className="inline-flex items-center justify-center rounded-[18px] bg-slate-900 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800">
 View Route
 </button>
 )}
 {booking && (
 <button
 onClick={() => setShowShareRide(true)}
 className="inline-flex items-center justify-between rounded-[18px] border border-sky-200 bg-sky-50 px-4 py-3 text-left text-slate-800 transition-colors hover:bg-sky-100">
 <span className="flex items-center gap-3">
 <span className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-white text-sky-700 shadow-[0_12px_24px_-20px_rgba(15,23,42,0.28)]">
 <svg className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M8.684 13.342C9.886 12.128 11 11.5 12 11.5c1 0 2.114.628 3.316 1.842M15 6.5a3 3 0 11-6 0 3 3 0 016 0zM6 20a6 6 0 1112 0H6z"/>
 </svg>
 </span>
 <span>
 <span className="block text-sm font-semibold text-slate-900">Share Ride</span>
 <span className="block text-xs text-slate-500">Send trip details to family or friends</span>
 </span>
 </span>
 <svg className="h-5 w-5 text-sky-700"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M9 5l7 7-7 7"/>
 </svg>
 </button>
 )}
 {bookingStatus ==="completed"&& acceptedFare && !userPaymentSubmitted && (
 <button
 onClick={() => setShowPaymentModal(true)}
 className="inline-flex items-center justify-center rounded-[18px] bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-emerald-700">
 Complete Payment
 </button>
 )}
 </div>
 </div>

 {safeOtpCode && (bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress") && (
 <div className="rounded-[28px] border border-emerald-200 bg-[linear-gradient(180deg,#f1fbf6_0%,#ffffff_100%)] p-5 shadow-[0_22px_60px_-42px_rgba(16,185,129,0.2)] notranslate"translate="no">
 <div className="flex items-center justify-between gap-4">
 <div>
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-emerald-700 notranslate"translate="no">Pickup OTP</p>
 <p className="mt-3 text-4xl font-black tracking-[0.35em] text-slate-950 notranslate"translate="no"lang="en">
 {String(safeOtpCode)}
 </p>
 <p className="mt-3 text-sm text-slate-600">Share this code with the driver at pickup.</p>
 </div>
 <button
 onClick={() => setShowPickupOtpPopup(true)}
 className="inline-flex items-center justify-center rounded-[16px] bg-white px-4 py-3 text-sm font-semibold text-emerald-700 shadow-[0_14px_36px_-28px_rgba(15,23,42,0.24)] transition-colors hover:bg-emerald-50">
 Open OTP
 </button>
 </div>

 <button
 onClick={handleResendOTP}
 disabled={resendCountdown > 0 || resending}
 className="mt-4 text-sm font-semibold text-emerald-700 underline underline-offset-4 disabled:text-slate-400 disabled:no-underline">
 {resending
 ?"Updating...": resendCountdown > 0
 ?`Resend OTP in ${formatTime(resendCountdown)}`:"Resend OTP"}
 </button>
 {resendMessage && (
 <p className={`mt-2 text-xs ${resendMessage.includes("successfully") ?"text-emerald-700":"text-rose-600"}`}>
 {resendMessage}
 </p>
 )}
 </div>
 )}

 <div className="rounded-[28px] border border-slate-200/80 bg-white p-5 shadow-[0_22px_60px_-42px_rgba(15,23,42,0.16)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-400">Trip Snapshot</p>
 <div className="mt-4 grid gap-3">
 <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Vehicle</p>
 <p className="mt-2 text-sm font-semibold text-slate-900">{booking?.vehicleType || driver?.vehicleType ||"Car"}</p>
 </div>
 <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Payment Method</p>
 <p className="mt-2 text-sm font-semibold text-slate-900">{booking?.paymentMethod ||"Cash"}</p>
 </div>
 <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Requested At</p>
 <p className="mt-2 text-sm font-semibold text-slate-900">
 {booking?.createdAt
 ? new Date(booking.createdAt).toLocaleTimeString("en-IN", {
 hour:"2-digit",
 minute:"2-digit",
 })
 :"--"}
 </p>
 </div>
 </div>
 </div>

 {(bookingStatus ==="pending"|| bookingStatus ==="accepted") && (
 <div className="rounded-[24px] border border-rose-200 bg-rose-50 px-4 py-4 shadow-[0_18px_40px_-34px_rgba(244,63,94,0.2)]">
 <button
 onClick={handleCancelRide}
 disabled={bookingStatus ==="accepted"&& !canCancel}
 className={`inline-flex w-full items-center justify-center rounded-[16px] px-4 py-3 text-sm font-semibold transition-colors ${
 bookingStatus ==="pending"|| canCancel
 ?"bg-rose-600 text-white hover:bg-rose-700":"cursor-not-allowed bg-rose-100 text-rose-400"}`}
 >
 {bookingStatus ==="pending"?"Cancel Request": canCancel
 ?"Cancel Ride":"Cancel Window Expired"}
 </button>
 {bookingStatus ==="pending"&& (
 <p className="mt-2 text-xs text-rose-700">
 You can cancel this booking request before the driver arrives.
 </p>
 )}
 {bookingStatus ==="accepted"&& !canCancel && (
 <p className="mt-2 text-xs text-rose-700">
 Ride cancellation is allowed only within 3 minutes after acceptance.
 </p>
 )}
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>

 {(showFindAnotherDriver || bookingStatus ==="cancelled") && !dismissedFindAnotherDriverPopup && (
 <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-slate-950/30 px-3 sm:px-4 backdrop-blur-[2px]">
 <div className="w-full max-w-md sm:max-w-lg lg:max-w-xl max-h-[min(92dvh,92vh)] overflow-y-auto overscroll-contain rounded-[30px] border border-orange-200 bg-[linear-gradient(180deg,#fff8f1_0%,#ffffff_100%)] p-5 sm:p-6 shadow-[0_28px_80px_-42px_rgba(249,115,22,0.35)]">
 <div className="flex h-14 w-14 items-center justify-center rounded-[18px] bg-orange-100 text-orange-600">
 <svg className="h-7 w-7"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 </div>
 <h3 className="mt-5 text-2xl font-black tracking-[-0.03em] text-orange-950">Need another driver?</h3>
 <p className="mt-3 text-sm leading-7 text-orange-900">
 {bookingStatus ==="cancelled"?"The driver has cancelled this ride. You can continue with another nearby driver.": booking?.bookingType ==="reserve"?"The driver has not responded in 10 minutes. You can continue with another available driver.":"The driver has not responded in 5 minutes. You can continue with another available driver."}
 </p>
 <div className="mt-5 grid gap-3 sm:grid-cols-2">
 <button
 onClick={() => setDismissedFindAnotherDriverPopup(true)}
 className="inline-flex w-full items-center justify-center rounded-[18px] border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50">
 Cancel
 </button>
 <button
 onClick={handleFindAnotherDriver}
 className="inline-flex w-full items-center justify-center rounded-[18px] bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-orange-600">
 Find Another Driver
 </button>
 </div>
 </div>
 </div>
 )}
 </>
 )}

 {!isWebView && (
 <>
 {/* Mobile Header */}
 <div className="md:hidden fixed inset-x-0 top-0 z-40 border-b border-slate-200/70 bg-white/95 backdrop-blur-xl shadow-[0_14px_30px_-24px_rgba(15,23,42,0.25)]">
 <div className="px-4 pb-3 pt-[max(env(safe-area-inset-top),0.8rem)]">
 <div className="rounded-[20px] border border-slate-200 bg-[linear-gradient(135deg,#f8fafc_0%,#eef4ff_65%,#ffffff_100%)] px-3.5 py-3 shadow-[0_16px_38px_-30px_rgba(15,23,42,0.22)]">
 <div className="flex items-start justify-between gap-3">
 <div className="min-w-0">
 <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">UttaApp Ride</p>
 <h1 className="mt-1 truncate text-[1.02rem] font-black tracking-[-0.02em] text-slate-900">
 Booking Details
 </h1>
 </div>

 <div className="shrink-0 text-right">
 <span className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] ${
 bookingStatus ==="completed"?"border-emerald-200 bg-emerald-50 text-emerald-700": bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress"?"border-blue-200 bg-blue-50 text-blue-700": bookingStatus ==="accepted"?"border-indigo-200 bg-indigo-50 text-indigo-700": bookingStatus ==="pending"?"border-amber-200 bg-amber-50 text-amber-700":"border-slate-200 bg-slate-100 text-slate-600"}`}>
 {bookingStatus ==="driver_arrived"?"Arrived": bookingStatus ==="in_progress"?"On Trip": bookingStatus ||"Active"}
 </span>
 <p className="mt-1 text-[10px] font-bold text-slate-500">
 #{booking?.id?.slice(-4) || booking?._id?.slice(-4) ||"N/A"}
 </p>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Web Layout - Based on Uploaded Image Design */}
 <div className="hidden md:block min-h-screen">
 {/* Main Content - Simplified Design */}
 <div className="max-w-4xl mx-auto px-4 py-6">
 <div className="bg-white rounded-lg overflow-hidden">
 {/* Waiting for Driver Response - Show when pending */}
 {bookingStatus ==="pending"&& (
 <div className="px-6 py-6 border-b border-gray-200 bg-gray-50 text-center">
 <div className="flex flex-col items-center">
 <div className="relative mb-4">
 <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-300">
 <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-600"></div>
 <div className="absolute inset-0 flex items-center justify-center">
 <svg
 className="w-8 h-8 text-gray-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 </div>
 </div>
 <h2 className="text-xl font-semibold text-gray-900 mb-2">
 Waiting for Driver Response
 </h2>
 <p className="text-gray-600 text-sm mb-3">
 Your booking request has been sent to{""}
 {driver?.name ||"the driver"}
 </p>
 <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
 <p className="text-gray-700 font-medium text-sm">
 Waiting time: {formatTime(elapsedTime)}
 </p>
 </div>
 
 {/* Find Another Driver Button - Show after timeout or driver cancellation */}
 {(showFindAnotherDriver || bookingStatus ==="cancelled") && (
 <div className="mt-4 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
 <p className="text-sm text-orange-800 mb-3 font-medium">
 {bookingStatus ==="cancelled"?"The driver has cancelled your ride. You can find another driver.": booking?.bookingType ==="reserve"?"The driver hasn't responded in 10 minutes. You can find another driver.":"The driver hasn't responded in 5 minutes. You can find another driver."}
 </p>
 <button
 onClick={async () => {
 // Cancel the booking before navigating (user selected another driver)
 // Only cancel if not already cancelled
 if (bookingStatus !=="cancelled") {
 await cancelBookingForAnotherDriver();
 }

 // CRITICAL: Validate required fields before navigation
 const pincodeValue = booking?.pickupPincode || location.state?.pickupPincode;
 const vehicleTypeValue = booking?.vehicleType || driver?.vehicleType || location.state?.vehicleType;
 
 if (!pincodeValue || !vehicleTypeValue) {
 console.error('❌ Missing required data for navigation:', {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 booking: booking,
 driver: driver,
 locationState: location.state
 });
 setMessageModal({ show: true, message:'Missing required information (pincode or vehicle type). Please try booking again from the book-ride page.', type:'warning', title:'Missing Information'});
 return; // Don't navigate if required data is missing
 }

 // Prepare navigation state with all booking data
 const navigationState = {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 paymentMethod:
 booking?.paymentMethod ||
 location.state?.paymentMethod ||"cash",
 pickupLocation:
 pickupLocation ||
 booking?.pickupLocation ||
 location.state?.pickupLocation,
 pickupLocationName:
 booking?.pickupLocationName ||
 location.state?.pickupLocationName ||
 pickupLocation,
 pickupPincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 pickupLandmark:
 booking?.pickupLandmark ||
 location.state?.pickupLandmark ||"",
 dropoffLocation:
 dropoffLocation ||
 booking?.dropoffLocation ||
 location.state?.dropoffLocation,
 dropoffLandmark:
 booking?.dropoffLandmark ||
 location.state?.dropoffLandmark ||"",
 bookingType:
 booking?.bookingType ||
 location.state?.bookingType ||"book-now",
 rideId:
 booking?.rideId || location.state?.rideId || null,
 pickupPhoto:
 booking?.pickupPhoto ||
 location.state?.pickupPhoto ||
 null,
 startDate:
 booking?.scheduledStartDate ||
 location.state?.startDate ||
 null,
 startTime:
 booking?.scheduledStartTime ||
 location.state?.startTime ||
 null,
 endDate:
 booking?.scheduledEndDate ||
 location.state?.endDate ||
 null,
 excludedDriverId: (() => {
 // Use cancelled driver ID from localStorage if available, otherwise use current driver ID
 try {
 const cancelledDriverId = localStorage.getItem('cancelled_driver_id');
 if (cancelledDriverId) {
 console.log('🚫 Using cancelled driver ID from localStorage:', cancelledDriverId);
 return cancelledDriverId;
 }
 } catch (e) {
 console.warn('⚠️ Could not read cancelled driver ID:', e);
 }
 return booking?.driverId || driver?.driverId || null; // Exclude current driver
 })(),
 };

 console.log("🚗 Navigating to nearby drivers (Web - Timeout) with state:",
 navigationState
 );

 // CRITICAL: Save navigation state to localStorage as backup (for VPS/production)
 try {
 localStorage.setItem('nearby_drivers_nav_state', JSON.stringify(navigationState));
 console.log('✅ Navigation state saved to localStorage');
 } catch (e) {
 console.warn('⚠️ Could not save navigation state to localStorage:', e);
 }

 // Navigate to nearby drivers page
 navigate("/nearby-drivers", { state: navigationState });
 }}
 className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 <span>Find Another Driver</span>
 </button>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Progress Tracker - At Top - Show for all statuses */}
 {(bookingStatus ==="pending"||
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed") && (
 <div className="px-6 py-4 border-b border-gray-200 bg-white">
 <div className="relative">
 {/* Background connecting line */}
 <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-300 z-0"></div>

 <div className="flex items-center justify-between relative z-10">
 {/* Step 1: Waiting/Requested */}
 <div className="flex flex-col items-center flex-1">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
 bookingStatus ==="pending"?"bg-blue-500 border-blue-500": bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"bg-gray-900 border-gray-900":"bg-white border-gray-300"}`}
 >
 {bookingStatus ==="pending"? (
 <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
 ) : bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"? (
 <svg
 className="w-5 h-5 text-green-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 <p
 className={`text-xs font-semibold mt-2 text-center ${
 bookingStatus ==="pending"?"text-blue-600": bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"text-gray-900":"text-gray-500"}`}
 >
 {bookingStatus ==="pending"?"Waiting":"Requested"}
 </p>
 </div>

 {/* Step 2: Driver Accepted */}
 <div className="flex flex-col items-center flex-1">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"bg-gray-900 border-gray-900":"bg-white border-gray-300"}`}
 >
 {bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"? (
 <svg
 className="w-5 h-5 text-green-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 <p
 className={`text-xs font-semibold mt-2 text-center ${
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"text-gray-900":"text-gray-500"}`}
 >
 Accepted
 </p>
 </div>

 {/* Step 3: Driver Arrived */}
 <div className="flex flex-col items-center flex-1">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"bg-gray-900 border-gray-900":"bg-white border-gray-300"}`}
 >
 {bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"? (
 <svg
 className="w-5 h-5 text-green-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 <p
 className={`text-xs font-semibold mt-2 text-center ${
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"?"text-gray-900":"text-gray-500"}`}
 >
 Arrived
 </p>
 </div>

 {/* Step 4: Ride Completed */}
 <div className="flex flex-col items-center flex-1">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
 bookingStatus ==="completed"?"bg-gray-900 border-gray-900":"bg-white border-gray-300"}`}
 >
 {bookingStatus ==="completed"? (
 <svg
 className="w-5 h-5 text-green-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 <p
 className={`text-xs font-semibold mt-2 text-center ${
 bookingStatus ==="completed"?"text-gray-900":"text-gray-500"}`}
 >
 Completed
 </p>
 </div>

 {/* Step 5: Payment Done */}
 <div className="flex flex-col items-center flex-1">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
 driverPaymentConfirmed?"bg-gray-900 border-gray-900":"bg-white border-gray-300"}`}
 >
 {driverPaymentConfirmed ? (
 <svg
 className="w-5 h-5 text-green-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : (
 <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
 )}
 </div>
 <p
 className={`text-xs font-semibold mt-2 text-center ${
 driverPaymentConfirmed
 ?"text-gray-900"
 : userPaymentSubmitted
 ?"text-amber-700"
 :"text-gray-500"}`}
 >
 {userPaymentSubmitted && !driverPaymentConfirmed ?"Awaiting driver":"Payment"}
 </p>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Status Image - Web Layout - Hidden on Mobile, Show on Desktop only for accepted and later */}
 {(bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed") && (
 <div className="hidden md:block px-6 py-4 border-b border-gray-200 bg-gray-50">
 <div className="flex items-center justify-center">
 {bookingStatus ==="accepted"? (
 <img
 src="/rideaccepted.png"alt="Ride Accepted"className="h-48 w-auto object-contain"onError={(e) => {
 e.target.style.display ="none";
 }}
 />
 ) : bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"? (
 <img
 src="/driverarrived.png"alt="Driver Arrived"className="h-48 w-auto object-contain"onError={(e) => {
 e.target.style.display ="none";
 }}
 />
 ) : bookingStatus ==="completed"? (
 <img
 src="/ridecompleted.png"alt="Ride Completed"className="h-48 w-auto object-contain"onError={(e) => {
 e.target.style.display ="none";
 }}
 />
 ) : null}
 </div>
 </div>
 )}

 {/* Status Header - Simplified - Show for accepted and later */}
 {(bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed") && (
 <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
 <div className="flex items-center justify-between">
 <div className="flex items-center space-x-3">
 <div
 className={`w-10 h-10 rounded-full flex items-center justify-center ${
 bookingStatus ==="accepted"?"bg-blue-100": bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"?"bg-green-100": bookingStatus ==="completed"?"bg-gray-100":"bg-gray-100"}`}
 >
 {bookingStatus ==="accepted"? (
 <svg
 className="w-5 h-5 text-blue-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 ) : bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"? (
 <svg
 className="w-5 h-5 text-green-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 ) : bookingStatus ==="completed"? (
 <svg
 className="w-5 h-5 text-gray-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M5 13l4 4L19 7"/>
 </svg>
 ) : null}
 </div>
 <div>
 <h2 className="text-lg font-semibold text-gray-900">
 {bookingStatus ==="accepted"?"Ride Accepted": bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"?"Driver Arrived": bookingStatus ==="completed"?"Ride Completed":"Booking Details"}
 </h2>
 <p className="text-sm text-gray-600">
 {bookingStatus ==="accepted"?"Your ride has been confirmed": bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"?"Driver has arrived at pickup location": bookingStatus ==="completed"?"Ride completed successfully":"Booking in progress"}
 </p>
 </div>
 </div>
 {bookingStatus ==="accepted"&& acceptedFare && (
 <div className="text-right">
 <p className="text-xs text-gray-500 mb-1">Fare</p>
 <p className="text-xl font-bold text-gray-900">
 ₹{acceptedFare}
 </p>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Booking Details - Simplified */}
 <div className="p-6">
 <div className="space-y-4">
 {/* Driver Info Section - Simplified */}
 <div className="pb-3 border-b border-gray-200">
 <h4 className="text-base font-semibold text-gray-900 mb-3">
 Driver Information
 </h4>
 <div className="flex items-center space-x-4">
 <div className="relative">
 <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-300">
 {driver?.profilePicture &&
 getImageUrl(driver.profilePicture) ? (
 <img
 src={getImageUrl(driver.profilePicture)}
 alt={driver.name}
 className="w-full h-full object-cover"onError={(e) => {
 // If image fails to load, show initial instead
 e.target.style.display ="none";
 const parent = e.target.parentElement;
 if (parent && !parent.querySelector("span")) {
 const initial = document.createElement("span");
 initial.className ="text-gray-600 font-semibold text-xl";
 initial.textContent =
 driver?.name?.charAt(0)?.toUpperCase() ||"D";
 parent.appendChild(initial);
 }
 }}
 />
 ) : (
 <span className="text-gray-600 font-semibold text-xl">
 {driver?.name?.charAt(0)?.toUpperCase() ||"D"}
 </span>
 )}
 </div>
 </div>
 <div className="flex-1 min-w-0">
 <h3 className="text-lg font-semibold text-gray-900 mb-1">
 {driver?.name ||"Driver"}
 </h3>
 <div className="flex items-center gap-4 text-sm">
 <div className="flex items-center text-gray-700">
 <svg
 className="w-4 h-4 text-yellow-500 mr-1"fill="currentColor"viewBox="0 0 20 20">
 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
 </svg>
 <span className="font-medium">
 {getDriverDisplayRating(driver, booking?.driverRating)}
 </span>
 </div>
 {driver?.phone &&
 (bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress") &&
 bookingStatus !=="completed"&& (
 <div className="flex items-center space-x-2 notranslate"translate="no">
 <span 
 className="text-gray-600 notranslate"translate="no"lang="en"data-translate="no"style={{ 
 unicodeBidi:'isolate', 
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none',
 whiteSpace:'nowrap'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(driver.phone).split('').map((digit, idx) => (
 <span key={idx} className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}>{digit}</span>
 ))}
 </span>
 <a
 href={`tel:${driver.phone}`}
 className="flex items-center justify-center w-8 h-8 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors notranslate"translate="no"lang="en"data-translate="no"aria-label="Call driver">
 <svg
 className="w-4 h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
 </svg>
 </a>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>

 {/* Trip Details Section - Simplified */}
 <div className="pb-4 border-b border-gray-200">
 <h4 className="text-base font-semibold text-gray-900 mb-3">
 Trip Details
 </h4>
 
 {/* OTP Display - Simple - Show within Trip Details */}
 {/* CRITICAL: Safely extract OTP code to prevent React error */}
 {/* OTP is ONLY shown when driver arrives (status: driver_arrived or in_progress) */}
 {/* OTP is NOT shown when driver accepts (status: accepted) */}
 {(() => {
 const shouldShowOTP = 
 otpCode && (
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress");
 
 if (!shouldShowOTP) return null;
 
 const safeOtpCode = extractOtpCode(otpCode);
 if (!safeOtpCode) return null;
 
 return (
 <div key={`otp-${safeOtpCode}`} className="bg-gray-50 rounded-lg p-3 border border-gray-200 mb-4 notranslate"translate="no">
 <div className="flex items-center justify-between notranslate"translate="no">
 <div className="notranslate"translate="no">
 <p className="text-xs text-gray-500 mb-1 notranslate"translate="no">Pickup OTP</p>
 <p 
 className="text-2xl font-bold text-gray-900 tracking-wider notranslate"translate="no"lang="en"data-translate="no"style={{ 
 unicodeBidi:'isolate', 
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(safeOtpCode).split('').map((digit, idx) => (
 <span key={idx} className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}>{digit}</span>
 ))}
 </p>
 </div>
 <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center notranslate"translate="no">
 <svg
 className="w-5 h-5 text-gray-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
 </svg>
 </div>
 </div>
 {/* Resend OTP Section */}
 <div className="mt-3 pt-3 border-t border-gray-200">
 {resendMessage && (
 <div className={`mb-2 text-xs text-center ${
 resendMessage.includes('successfully') 
 ?'text-green-600':'text-red-600'}`}>
 {resendMessage}
 </div>
 )}
 <button
 onClick={handleResendOTP}
 disabled={resendCountdown > 0 || resending}
 className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium underline transition-colors disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed">
 {resending ? (
 <span className="flex items-center justify-center">
 <svg className="animate-spin w-3 h-3 mr-1"fill="none"viewBox="0 0 24 24">
 <circle className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"></circle>
 <path className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 Updating...
 </span>
 ) : resendCountdown > 0 ? (`Resend OTP in ${formatTime(resendCountdown)}`) : ('Resend OTP')}
 </button>
 </div>
 </div>
 );
 })()}
 
 {/* Pickup and Dropoff Locations */}
 <div className="space-y-3 mb-4">
 {/* Pickup Location */}
 <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
 <div className="flex items-start space-x-3">
 <div className="flex-shrink-0 mt-0.5">
 <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
 <svg
 className="w-4 h-4 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 </div>
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-xs text-gray-500 mb-1">Pickup Location</p>
 {/* Priority: booking.pickupLocation (Google Places address) > pickupLocation state > booking.pickupLocationName (pincode search) */}
 <p className="text-sm font-semibold text-gray-900 break-words">
 {(() => {
 const displayValue = booking?.pickupLocation || pickupLocation || booking?.pickupLocationName ||'--';
 console.log('📍 Trip Details - Displaying Pickup Location:', {
 bookingPickupLocation: booking?.pickupLocation,
 pickupLocationState: pickupLocation,
 bookingPickupLocationName: booking?.pickupLocationName,
 displayValue: displayValue,
 source: booking?.pickupLocation ?'booking.pickupLocation (Google Places)': 
 pickupLocation ?'pickupLocation state': 
 booking?.pickupLocationName ?'booking.pickupLocationName (pincode search)':'--'});
 return displayValue;
 })()}
 </p>
 </div>
 </div>
 </div>

 {/* Dropoff Location */}
 <div className="bg-red-50 rounded-lg p-3 border border-red-200">
 <div className="flex items-start space-x-3">
 <div className="flex-shrink-0 mt-0.5">
 <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center">
 <svg
 className="w-4 h-4 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 </div>
 </div>
 <div className="flex-1 min-w-0">
 <p className="text-xs text-gray-500 mb-1">Drop-off Location</p>
 <p className="text-sm font-semibold text-gray-900 break-words">
 {booking?.dropoffLocation || 
 dropoffLocation ||'--'}
 </p>
 </div>
 </div>
 </div>

 {/* Cancel Ride Button - Only show when status is"accepted"and within 3-minute window */}
 {bookingStatus ==="accepted"&& canCancel && bookingStatus !=="cancelled"&& (
 <div className="mt-3">
 <button
 onClick={handleCancelRide}
 disabled={cancelling}
 className="w-full px-4 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2">
 {cancelling ? (
 <>
 <svg className="animate-spin w-5 h-5"fill="none"viewBox="0 0 24 24">
 <circle className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"></circle>
 <path className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Cancelling...</span>
 </>
 ) : (
 <>
 <svg className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 <span>Cancel Ride</span>
 </>
 )}
 </button>
 {acceptedAtTimestamp && (
 <p className="text-xs text-gray-500 mt-2 text-center">
 Cancellation available for {Math.max(0, Math.ceil((3 * 60 * 1000 - (Date.now() - acceptedAtTimestamp)) / 1000))} more seconds
 </p>
 )}
 </div>
 )}

 {/* View in Map and Share Ride Buttons */}
 {(bookingStatus ==="accepted"|| bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress") && (
 <div className="flex gap-3 mt-3">
 <button
 onClick={openGoogleMaps}
 className="flex-1 px-4 py-3 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
 </svg>
 <span>View in Map</span>
 </button>
 <button
 onClick={() => setShowShareRide(true)}
 className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"/>
 </svg>
 <span>Share Ride</span>
 </button>
 </div>
 )}
 </div>

 {/* Pickup Time and Fare */}
 <div className="grid grid-cols-2 gap-3">
 <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
 <p className="text-xs text-gray-500 mb-1">Pickup Time</p>
 <p className="text-base font-semibold text-gray-900">
 {booking?.createdAt
 ? new Date(booking.createdAt).toLocaleTimeString("en-IN",
 { hour:"2-digit", minute:"2-digit"}
 )
 :"--"}
 </p>
 </div>
 <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
 <p className="text-xs text-gray-500 mb-1">Fare</p>
 <p className="text-lg font-bold text-gray-900">
 ₹{acceptedFare || booking?.fare ||"--"}
 </p>
 </div>
 </div>
 </div>

 {/* Booking Information Section - Simplified */}
 <div>
 <h4 className="text-base font-semibold text-gray-900 mb-3">
 Booking Information
 </h4>
 <div className="space-y-3">
 <div className="flex items-center justify-between py-2 border-b border-gray-200">
 <span className="text-sm text-gray-600">Booking ID</span>
 <span className="text-sm font-semibold text-gray-900 font-mono">
 #
 {booking?.id?.slice(-10) ||
 booking?._id?.slice(-10) ||"--"}
 </span>
 </div>
 <div className="flex items-center justify-between py-2 border-b border-gray-200">
 <span className="text-sm text-gray-600">
 Payment Method
 </span>
 <span className="text-sm font-semibold text-gray-900 capitalize">
 {booking?.paymentMethod ||"Cash"}
 </span>
 </div>
 <div className="flex items-center justify-between py-2 border-b border-gray-200">
 <span className="text-sm text-gray-600">
 Vehicle Type
 </span>
 <span className="text-sm font-semibold text-gray-900">
 {booking?.vehicleType || driver?.vehicleType ||"Car"}
 </span>
 </div>
 <div className="flex items-center justify-between py-2">
 <span className="text-sm text-gray-600">Status</span>
 <span
 className={`text-xs font-semibold px-3 py-1 rounded ${
 bookingStatus ==="accepted"?"bg-blue-100 text-blue-900": bookingStatus ==="driver_arrived"?"bg-blue-100 text-blue-900": bookingStatus ==="completed"?"bg-green-100 text-green-900":"bg-gray-100 text-gray-900"}`}
 >
 {bookingStatus ==="accepted"?"Accepted": bookingStatus ==="driver_arrived"?"Arrived": bookingStatus ==="completed"?"Completed":"Waiting"}
 </span>
 </div>
 </div>
 </div>
 </div>
 </div>

 {/* Cancel Ride Button - Web Only - Show until driver arrives */}
 {bookingStatus !=="driver_arrived"&&
 bookingStatus !=="in_progress"&&
 bookingStatus !=="completed"&&
 bookingStatus !=="cancelled"&&
 bookingStatus !=="rejected"&&
 bookingStatus !=="accepted"&& (
 <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
 <button
 onClick={handleCancelRide}
 disabled={cancelling}
 className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
 {cancelling ? (
 <>
 <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
 <span>Cancelling...</span>
 </>
 ) : (
 <>
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 <span>Cancel Ride</span>
 </>
 )}
 </button>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* Mobile Layout - Keep Original Design */}
 <div className="md:hidden overflow-x-hidden relative pt-[98px]">
 {/* Backdrop Blur Overlay - Show when pending - Covers all content */}
 {bookingStatus ==="pending"&& (
 <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-md z-30"></div>
 )}
 <div className="md:max-w-4xl md:mx-auto px-0 md:px-4 py-0 md:py-6 overflow-x-hidden relative">
 {/* Status Card - Highlighted when pending */}
 <div className={`rounded-lg overflow-hidden mb-0 md:mb-6 w-full transition-all ${
 bookingStatus ==="pending"?"bg-white relative z-[60]":"bg-white"}`}>
 {/* Animated Header - Conditional based on status */}
 {bookingStatus ==="accepted"? (
 <DriverAccepted
 driver={driver}
 acceptedFare={acceptedFare}
 otpCode={otpCode}
 booking={booking}
 />
 ) : bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"? (
 <DriverArrived driver={driver} otpCode={otpCode} />
 ) : bookingStatus ==="completed"? (
 <RideCompleted
 booking={booking}
 paymentStatus={paymentStatus}
 showPaymentModal={showPaymentModal}
 setShowPaymentModal={setShowPaymentModal}
 />
 ) : bookingStatus ==="rejected"? (
 <div className="bg-white p-6 md:p-8 text-center border-b border-gray-200">
 <div className="flex flex-col items-center">
 {/* Rejected Icon */}
 <div className="relative mb-4">
 <div className="w-20 h-20 md:w-24 md:h-24 bg-gray-100 rounded-full flex items-center justify-center border-2 border-gray-300">
 <svg
 className="w-12 h-12 md:w-16 md:h-16 text-gray-700"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={3}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </div>
 </div>

 <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
 Ride Rejected
 </h2>
 <p className="text-gray-600 text-sm md:text-base mb-4">
 The driver has rejected your ride request
 </p>

 {/* Find Another Driver Button */}
 <button
 onClick={() => {
 // Prepare navigation state with all booking data
 const navigationState = {
 pincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 vehicleType:
 booking?.vehicleType ||
 driver?.vehicleType ||
 location.state?.vehicleType,
 paymentMethod:
 booking?.paymentMethod ||
 location.state?.paymentMethod ||"cash",
 pickupLocation:
 pickupLocation ||
 booking?.pickupLocation ||
 location.state?.pickupLocation,
 pickupLocationName:
 booking?.pickupLocationName ||
 location.state?.pickupLocationName ||
 pickupLocation,
 pickupPincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 pickupLandmark:
 booking?.pickupLandmark ||
 location.state?.pickupLandmark ||"",
 dropoffLocation:
 dropoffLocation ||
 booking?.dropoffLocation ||
 location.state?.dropoffLocation,
 dropoffLandmark:
 booking?.dropoffLandmark ||
 location.state?.dropoffLandmark ||"",
 bookingType:
 booking?.bookingType ||
 location.state?.bookingType ||"book-now",
 rideId:
 booking?.rideId || location.state?.rideId || null,
 pickupPhoto:
 booking?.pickupPhoto ||
 location.state?.pickupPhoto ||
 null,
 startDate:
 booking?.scheduledStartDate ||
 location.state?.startDate ||
 null,
 startTime:
 booking?.scheduledStartTime ||
 location.state?.startTime ||
 null,
 endDate:
 booking?.scheduledEndDate ||
 location.state?.endDate ||
 null,
 };

 console.log("🚗 Navigating to nearby drivers (Mobile) with state:",
 navigationState
 );

 // CRITICAL: Save navigation state to localStorage as backup (for VPS/production)
 try {
 localStorage.setItem('nearby_drivers_nav_state', JSON.stringify(navigationState));
 console.log('✅ Navigation state saved to localStorage');
 } catch (e) {
 console.warn('⚠️ Could not save navigation state to localStorage:', e);
 }

 // Navigate to nearby drivers page
 navigate("/nearby-drivers", { state: navigationState });
 }}
 className="mt-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-4 rounded-lg shadow-sm hover:shadow-md transition-all flex items-center space-x-2">
 <svg
 className="w-6 h-6"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 <span>Find Another Driver</span>
 </button>
 </div>
 </div>
 ) : (
 <div className={`p-6 md:p-8 text-center ${
 bookingStatus ==="pending"?"bg-white border-b-0":"bg-white border-b border-gray-200"}`}>
 <div className="flex flex-col items-center">
 {/* Animated Waiting Icon */}
 <div className="relative mb-4">
 <div className={`w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center border-2 ${
 bookingStatus ==="pending"?"bg-blue-50 border-blue-400":"bg-gray-100 border-gray-300"}`}>
 <div className={`animate-spin rounded-full h-16 w-16 md:h-20 md:w-20 border-4 ${
 bookingStatus ==="pending"?"border-blue-100 border-t-blue-600":"border-gray-200 border-t-gray-600"}`}></div>
 <div className="absolute inset-0 flex items-center justify-center">
 <svg
 className={`w-10 h-10 md:w-12 md:h-12 ${
 bookingStatus ==="pending"?"text-blue-600":"text-gray-600"}`}
 fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 </div>
 </div>

 <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${
 bookingStatus ==="pending"?"text-blue-900":"text-gray-900"}`}>
 Waiting for Driver Response
 </h2>
 <p className={`text-sm md:text-base mb-3 ${
 bookingStatus ==="pending"?"text-blue-700":"text-gray-600"}`}>
 Your booking request has been sent to{""}
 {driver?.name ||"Driver"}
 </p>
 <div className={`px-4 py-2 rounded-lg border ${
 bookingStatus ==="pending"?"bg-blue-50 border-blue-200":"bg-gray-50 border-gray-100"}`}>
 <p className={`font-medium text-sm ${
 bookingStatus ==="pending"?"text-blue-800":"text-gray-700"}`}>
 Waiting time: {formatTime(elapsedTime)}
 </p>
 </div>
 
 {/* Action Buttons - Cancel Booking - Hide when cancelled */}
 {bookingStatus !=="cancelled"&& bookingStatus !=="rejected"&& bookingStatus !=="completed"&& (
 <div className="mt-6 flex flex-col gap-3 w-full">
 <button
 onClick={handleCancelRide}
 className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2 shadow-sm">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 <span>Cancel Booking</span>
 </button>
 </div>
 )}
 
 {/* Find Another Driver Button - Show after timeout or driver cancellation */}
 {(showFindAnotherDriver || bookingStatus ==="cancelled") && (
 <div className="mt-4 bg-orange-50 border-2 border-orange-300 rounded-lg p-4">
 <p className="text-sm text-orange-800 mb-3 font-medium">
 {bookingStatus ==="cancelled"?"The driver has cancelled your ride. You can find another driver.": booking?.bookingType ==="reserve"?"The driver hasn't responded in 10 minutes. You can find another driver.":"The driver hasn't responded in 5 minutes. You can find another driver."}
 </p>
 <button
 onClick={async () => {
 // Cancel the booking before navigating (user selected another driver)
 // Only cancel if not already cancelled
 if (bookingStatus !=="cancelled") {
 await cancelBookingForAnotherDriver();
 }

 // CRITICAL: Validate required fields before navigation
 const pincodeValue = booking?.pickupPincode || location.state?.pickupPincode;
 const vehicleTypeValue = booking?.vehicleType || driver?.vehicleType || location.state?.vehicleType;
 
 if (!pincodeValue || !vehicleTypeValue) {
 console.error('❌ Missing required data for navigation:', {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 booking: booking,
 driver: driver,
 locationState: location.state
 });
 setMessageModal({ show: true, message:'Missing required information (pincode or vehicle type). Please try booking again from the book-ride page.', type:'warning', title:'Missing Information'});
 return; // Don't navigate if required data is missing
 }

 // Prepare navigation state with all booking data
 const navigationState = {
 pincode: pincodeValue,
 vehicleType: vehicleTypeValue,
 paymentMethod:
 booking?.paymentMethod ||
 location.state?.paymentMethod ||"cash",
 pickupLocation:
 pickupLocation ||
 booking?.pickupLocation ||
 location.state?.pickupLocation,
 pickupLocationName:
 booking?.pickupLocationName ||
 location.state?.pickupLocationName ||
 pickupLocation,
 pickupPincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 pickupLandmark:
 booking?.pickupLandmark ||
 location.state?.pickupLandmark ||"",
 dropoffLocation:
 dropoffLocation ||
 booking?.dropoffLocation ||
 location.state?.dropoffLocation,
 dropoffLandmark:
 booking?.dropoffLandmark ||
 location.state?.dropoffLandmark ||"",
 bookingType:
 booking?.bookingType ||
 location.state?.bookingType ||"book-now",
 rideId:
 booking?.rideId || location.state?.rideId || null,
 pickupPhoto:
 booking?.pickupPhoto ||
 location.state?.pickupPhoto ||
 null,
 startDate:
 booking?.scheduledStartDate ||
 location.state?.startDate ||
 null,
 startTime:
 booking?.scheduledStartTime ||
 location.state?.startTime ||
 null,
 endDate:
 booking?.scheduledEndDate ||
 location.state?.endDate ||
 null,
 excludedDriverId: (() => {
 // Use cancelled driver ID from localStorage if available, otherwise use current driver ID
 try {
 const cancelledDriverId = localStorage.getItem('cancelled_driver_id');
 if (cancelledDriverId) {
 console.log('🚫 Using cancelled driver ID from localStorage:', cancelledDriverId);
 return cancelledDriverId;
 }
 } catch (e) {
 console.warn('⚠️ Could not read cancelled driver ID:', e);
 }
 return booking?.driverId || driver?.driverId || null; // Exclude current driver
 })(),
 };

 console.log("🚗 Navigating to nearby drivers (Mobile - Timeout) with state:",
 navigationState
 );

 // CRITICAL: Save navigation state to localStorage as backup (for VPS/production)
 try {
 localStorage.setItem('nearby_drivers_nav_state', JSON.stringify(navigationState));
 console.log('✅ Navigation state saved to localStorage');
 } catch (e) {
 console.warn('⚠️ Could not save navigation state to localStorage:', e);
 }

 // Navigate to nearby drivers page
 navigate("/nearby-drivers", { state: navigationState });
 }}
 className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 <span>Find Another Driver</span>
 </button>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Booking Details - Single Card - Hidden when pending - Mobile Optimized */}
 {bookingStatus !=="pending"&& (
 <div className="px-3 py-3 md:p-6 md:py-8 overflow-x-hidden relative">
 <div className="bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_100%)] rounded-[26px] md:rounded-lg overflow-hidden w-full border border-slate-200/80 shadow-[0_24px_60px_-42px_rgba(15,23,42,0.22)] md:border-0 md:shadow-none">
 {/* Ride Progress Tracker - Mobile Only - Show only when ride is accepted or later - Single Line Layout */}
 {bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"? (
 <div className="md:hidden border-b border-slate-200/70 bg-[linear-gradient(180deg,#f8fbff_0%,#eef4ff_100%)] px-4 py-4 overflow-x-hidden">
 <div className="flex items-start justify-between gap-3">
 <div>
 <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Ride timeline</p>
 <p className="mt-1 text-base font-black tracking-[-0.02em] text-slate-900">Live trip progress</p>
 </div>
 <span className="rounded-full bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 ring-1 ring-emerald-200">
 Active
 </span>
 </div>

 <div className="mt-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
 <div className="flex min-w-max gap-2.5">
 {[
 {
 key:"requested",
 label:"Requested",
 done: true,
 active: false,
 },
 {
 key:"accepted",
 label:"Accepted",
 done: ["accepted","driver_arrived","in_progress","completed"].includes(bookingStatus),
 active: bookingStatus ==="accepted",
 },
 {
 key:"arrived",
 label:"Arrived",
 done: ["driver_arrived","in_progress","completed"].includes(bookingStatus),
 active: bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress",
 },
 {
 key:"completed",
 label:"Completed",
 done: bookingStatus ==="completed",
 active: bookingStatus ==="completed"&& paymentStatus?.status !=="completed",
 },
 {
 key:"payment",
 label:"Payment",
 done: isPaymentFullySettled(booking, paymentStatus),
 active:
 isUserPaymentSubmitted(booking, paymentStatus) &&
 !isPaymentFullySettled(booking, paymentStatus),
 },
 ].map((step, index) => (
 <div
 key={step.key}
 className={`min-w-[96px] rounded-[18px] border px-3 py-2.5 ${
 step.active
 ?"border-slate-900 bg-slate-900 text-white": step.done
 ?"border-emerald-200 bg-emerald-50 text-emerald-900":"border-slate-200 bg-white text-slate-500"}`}
 >
 <div className="flex items-center justify-between">
 <span
 className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-black ${
 step.active
 ?"bg-white/15 text-white": step.done
 ?"bg-emerald-600 text-white":"bg-slate-100 text-slate-500"}`}
 >
 {step.done ?"✓": index + 1}
 </span>
 {step.active && (
 <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-300">
 Live
 </span>
 )}
 </div>
 <p className="mt-2 text-[10px] font-black uppercase tracking-[0.14em]">{step.label}</p>
 </div>
 ))}
 </div>
 </div>
 </div>
 ) : null}

 <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 overflow-x-hidden">
 {/* Driver Info Section - Mobile Optimized */}
 <div className="overflow-x-hidden rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_16px_44px_-34px_rgba(15,23,42,0.15)] md:pb-6">
 <h4 className="text-base md:text-lg font-black text-slate-900 mb-3 md:mb-4 flex items-center justify-between">
 <span className="flex items-center">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-900 rounded-xl flex items-center justify-center mr-2 md:mr-3 flex-shrink-0">
 <svg
 className="w-5 h-5 md:w-6 md:h-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
 </svg>
 </div>
 Driver Information
 </span>
 <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600">
 {bookingStatus ==="completed"?"Done":"Live"}
 </span>
 </h4>
 <div className="flex items-center space-x-3 md:space-x-5 w-full overflow-x-hidden">
 <div className="relative flex-shrink-0">
 <div className="w-20 h-20 md:w-28 md:h-28 rounded-[22px] bg-[linear-gradient(135deg,#0f172a_0%,#334155_100%)] flex items-center justify-center overflow-hidden border border-slate-200">
 {driver?.profilePicture &&
 getImageUrl(driver.profilePicture) ? (
 <img
 src={getImageUrl(driver.profilePicture)}
 alt={driver?.name ||"Driver"}
 className="w-full h-full object-cover"onError={(e) => {
 e.target.style.display ="none";
 const parent = e.target.parentElement;
 if (parent && !parent.querySelector("span")) {
 const initial =
 document.createElement("span");
 initial.className ="text-gray-700 font-bold text-3xl md:text-4xl";
 initial.textContent =
 driver?.name?.charAt(0)?.toUpperCase() ||"D";
 parent.appendChild(initial);
 }
 }}
 />
 ) : (
 <span className="text-white font-bold text-3xl md:text-4xl">
 {driver?.name?.charAt(0)?.toUpperCase() ||"D"}
 </span>
 )}
 </div>
 </div>
 <div className="flex-1 min-w-0 overflow-hidden">
 <h3 className="text-lg md:text-2xl font-black text-slate-900 mb-1 md:mb-2 truncate">
 {driver?.name || booking?.driverName ||"Driver"}
 </h3>
 <div className="flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm">
 <div className="flex items-center bg-amber-50 px-2 md:px-3 py-1 rounded-full border border-amber-200 flex-shrink-0">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-amber-600 mr-1 md:mr-1.5 flex-shrink-0"fill="currentColor"viewBox="0 0 20 20">
 <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
 </svg>
 <span className="font-semibold text-amber-900">
 {getDriverDisplayRating(driver, booking?.driverRating)}
 </span>
 </div>
 <div className="flex items-center text-slate-700 bg-slate-50 px-2 md:px-3 py-1 rounded-full border border-slate-200 min-w-0 max-w-full">
 <svg
 className="w-3.5 h-3.5 md:w-4 md:h-4 text-indigo-600 mr-1 md:mr-1.5 flex-shrink-0"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"/>
 </svg>
 <span className="font-medium truncate text-xs md:text-sm">
 {driver?.vehicleType ||
 booking?.vehicleType ||"Vehicle"}
 {driver?.vehicleNumber
 ?`- ${driver.vehicleNumber}`:""}
 </span>
 </div>
 </div>
 {/* Driver Phone Number - Show only after acceptance, hide after completion */}
 {driver?.phone &&
 (bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress") &&
 bookingStatus !=="completed"&& (
 <div className="mt-2 md:mt-3 flex items-center bg-emerald-50 px-3 md:px-4 py-2 rounded-2xl border border-emerald-200 overflow-hidden notranslate"translate="no">
 <div className="flex items-center space-x-2 min-w-0 flex-1 notranslate"translate="no">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-green-600 flex-shrink-0"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
 </svg>
 <span 
 className="text-sm md:text-base font-semibold text-green-900 truncate notranslate"translate="no"lang="en"data-translate="no"style={{ 
 unicodeBidi:'isolate', 
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none',
 whiteSpace:'nowrap'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(driver.phone).split('').map((digit, idx) => (
 <span key={idx} className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}>{digit}</span>
 ))}
 </span>
 </div>
 <a
 href={`tel:${driver.phone}`}
 className="ml-2 flex items-center justify-center w-9 h-9 md:w-10 md:h-10 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-all duration-200 flex-shrink-0 notranslate"translate="no"lang="en"data-translate="no"aria-label="Call driver">
 <svg
 className="w-4 h-4 md:w-5 md:h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
 </svg>
 </a>
 </div>
 )}
 </div>
 </div>
 </div>

 {/* Trip Details Section - Mobile Optimized */}
 <div className="rounded-[22px] border border-slate-200 bg-white p-4 shadow-[0_16px_44px_-34px_rgba(15,23,42,0.15)] md:pb-6">
 <h4 className="text-base md:text-lg font-black text-slate-900 flex items-center justify-between mb-3 md:mb-5">
 <div className="flex items-center">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-slate-900 rounded-xl flex items-center justify-center mr-2 md:mr-3">
 <svg
 className="w-5 h-5 md:w-6 md:h-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
 </svg>
 </div>
 Trip Details
 </div>
 {/* More Button - Mobile Only */}
 <button
 onClick={() => setShowBookingInfoModal(true)}
 className="md:hidden text-slate-700 hover:text-slate-900 text-sm font-semibold flex items-center space-x-1 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 border border-slate-200 transition-all">
 <span>More</span>
 <svg
 className="w-4 h-4"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 5l7 7-7 7"/>
 </svg>
 </button>
 </h4>

 <div className="mb-4 grid grid-cols-2 gap-2.5">
 <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
 <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Pickup Time</p>
 <p className="mt-1 text-sm font-black text-slate-900">
 {booking?.createdAt
 ? new Date(booking.createdAt).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit"})
 :"--"}
 </p>
 </div>
 <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
 <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Fare</p>
 <p className="mt-1 text-sm font-black text-slate-900">₹{acceptedFare || booking?.fare ||"--"}</p>
 </div>
 </div>

 {/* OTP Display - Mobile View */}
 {/* CRITICAL: Safely extract OTP code to prevent React error */}
 {/* OTP is ONLY shown when driver arrives (status: driver_arrived or in_progress) */}
 {/* OTP is NOT shown when driver accepts (status: accepted) */}
 {(() => {
 const shouldShowOTP = 
 otpCode && (
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress");
 
 if (!shouldShowOTP) return null;
 
 const safeOtpCode = extractOtpCode(otpCode);
 if (!safeOtpCode) return null;
 
 return (
 <div className="bg-[linear-gradient(180deg,#eef6ff_0%,#ffffff_100%)] rounded-[18px] p-4 border border-blue-200 mb-5 notranslate"translate="no">
 <div className="flex items-center justify-between notranslate"translate="no">
 <div className="flex-1 notranslate"translate="no">
 <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1 notranslate"translate="no">Pickup OTP</p>
 <p 
 className="text-3xl font-black text-blue-900 tracking-widest notranslate"translate="no"lang="en"data-translate="no"style={{ 
 unicodeBidi:'isolate', 
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(safeOtpCode).split('').map((digit, idx) => (
 <span key={idx} className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}>{digit}</span>
 ))}
 </p>
 <p className="text-xs text-blue-600 mt-1 notranslate"translate="no">Share with driver at pickup</p>
 </div>
 <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center ml-3 notranslate"translate="no">
 <svg
 className="w-7 h-7 text-blue-700"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
 </svg>
 </div>
 </div>
 </div>
 );
 })()}

 <div className="space-y-3 md:space-y-4">
 {/* Pickup Location */}
 <div className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 bg-[linear-gradient(180deg,#f1f7ff_0%,#ffffff_100%)] rounded-[18px] border border-blue-200 overflow-hidden">
 <div className="w-10 h-10 md:w-12 md:h-12 bg-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
 <svg
 className="w-5 h-5 md:w-6 md:h-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 </div>
 <div className="flex-1 min-w-0 overflow-hidden">
 <p className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
 Pickup Location
 </p>
 {/* Priority: booking.pickupLocation (Google Places address) > pickupLocation state > booking.pickupLocationName (pincode search) */}
 <p className="text-sm md:text-base font-bold text-gray-900 break-words leading-tight md:leading-normal">
 {(() => {
 const displayValue = booking?.pickupLocation || pickupLocation || booking?.pickupLocationName ||'--';
 console.log('📍 Mobile Trip Details - Displaying Pickup Location:', {
 bookingPickupLocation: booking?.pickupLocation,
 pickupLocationState: pickupLocation,
 bookingPickupLocationName: booking?.pickupLocationName,
 displayValue: displayValue,
 source: booking?.pickupLocation ?'booking.pickupLocation (Google Places)': 
 pickupLocation ?'pickupLocation state': 
 booking?.pickupLocationName ?'booking.pickupLocationName (pincode search)':'--'});
 return displayValue;
 })()}
 </p>
 </div>
 </div>

 {/* Connecting Line */}
 <div className="flex items-center justify-center my-1.5 md:my-2">
 <div className="flex-1 h-px bg-gray-200"></div>
 <div className="mx-2 md:mx-3 flex h-6 w-6 items-center justify-center rounded-full bg-slate-100">
 <svg className="w-3.5 h-3.5 text-slate-500"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6"/>
 </svg>
 </div>
 <div className="flex-1 h-px bg-gray-200"></div>
 </div>

 {/* Dropoff Location */}
 <div className="flex items-start space-x-3 md:space-x-4 p-3 md:p-4 bg-[linear-gradient(180deg,#fff2f2_0%,#ffffff_100%)] rounded-[18px] border border-rose-200 overflow-hidden">
 <div className="w-10 h-10 md:w-12 md:h-12 bg-rose-600 rounded-2xl flex items-center justify-center flex-shrink-0">
 <svg
 className="w-5 h-5 md:w-6 md:h-6 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
 </svg>
 </div>
 <div className="flex-1 min-w-0 overflow-hidden">
 <p className="text-[10px] md:text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
 Drop-off Location
 </p>
 <p className="text-sm md:text-base font-bold text-gray-900 break-words leading-tight md:leading-normal">
 {booking?.dropoffLocation || 
 dropoffLocation ||'--'}
 </p>
 </div>
 </div>

 {/* Cancel Ride Button - Mobile - Only show when status is"accepted"and within 3-minute window */}
 {bookingStatus ==="accepted"&& canCancel && bookingStatus !=="cancelled"&& (
 <div className="mt-3 md:mt-4 rounded-[18px] border border-rose-200 bg-rose-50 p-3">
 <button
 onClick={handleCancelRide}
 disabled={cancelling}
 className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white rounded-xl text-sm md:text-base font-semibold transition-colors flex items-center justify-center space-x-2">
 {cancelling ? (
 <>
 <svg className="animate-spin w-4 h-4 md:w-5 md:h-5"fill="none"viewBox="0 0 24 24">
 <circle className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"></circle>
 <path className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
 </svg>
 <span>Cancelling...</span>
 </>
 ) : (
 <>
 <svg className="w-4 h-4 md:w-5 md:h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 <span>Cancel Ride</span>
 </>
 )}
 </button>
 {acceptedAtTimestamp && (
 <p className="text-[10px] md:text-xs text-rose-700 mt-2 text-center">
 Cancellation available for {Math.max(0, Math.ceil((3 * 60 * 1000 - (Date.now() - acceptedAtTimestamp)) / 1000))} more seconds
 </p>
 )}
 </div>
 )}

 {/* View in Map Button - Show when driver accepts or arrives (including reserve bookings after trip starts) */}
 {(bookingStatus ==="accepted"|| bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress") && (
 <button
 onClick={openGoogleMaps}
 className="w-full mt-3 md:mt-4 px-3 md:px-4 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm md:text-base font-semibold transition-all flex items-center justify-center space-x-2">
 <svg
 className="w-4 h-4 md:w-5 md:h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
 </svg>
 <span>View in Map</span>
 {driverLocation && (
 <span className="text-[10px] md:text-xs text-slate-300 ml-1 md:ml-2">
 (Driver Location)
 </span>
 )}
 </button>
 )}
 </div>
 </div>

 {/* Booking Information Section - Mobile Optimized - Hidden in Mobile, Shown in Desktop */}
 <div className="hidden md:block">
 <h4 className="text-base md:text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3 md:mb-4 flex items-center">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center mr-2 md:mr-3">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
 </svg>
 </div>
 Booking Information
 </h4>
 <div className="space-y-2.5 md:space-y-3">
 <div className="flex items-center justify-between py-2 md:py-3 border-b border-emerald-100">
 <div className="flex items-center space-x-2 md:space-x-3">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
 </svg>
 </div>
 <div>
 <p className="text-[10px] md:text-xs font-semibold text-emerald-600 uppercase tracking-wide">
 Booking ID
 </p>
 <p className="text-sm md:text-base font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent font-mono">
 #
 {(booking?.id || booking?._id)
 ?.slice(-8)
 .toUpperCase() ||"--"}
 </p>
 </div>
 </div>
 </div>

 <div className="flex items-center justify-between py-2 md:py-3 border-b border-emerald-100">
 <div className="flex items-center space-x-2 md:space-x-3">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
 {booking?.paymentMethod ==="online"? (
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
 </svg>
 ) : (
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
 </svg>
 )}
 </div>
 <div>
 <p className="text-[10px] md:text-xs font-semibold text-cyan-600 uppercase tracking-wide">
 Payment Method
 </p>
 <p className="text-sm md:text-base font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent capitalize">
 {booking?.paymentMethod ||"Cash"}
 </p>
 </div>
 </div>
 </div>

 <div className="flex items-center justify-between py-2 md:py-3 border-b border-emerald-100">
 <div className="flex items-center space-x-2 md:space-x-3">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-orange-500 to-amber-600 rounded-lg flex items-center justify-center">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"/>
 </svg>
 </div>
 <div>
 <p className="text-[10px] md:text-xs font-semibold text-orange-600 uppercase tracking-wide">
 Vehicle Type
 </p>
 <p className="text-sm md:text-base font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
 {booking?.vehicleType ||
 driver?.vehicleType ||"Vehicle"}
 </p>
 </div>
 </div>
 </div>

 <div className="flex items-center justify-between py-2 md:py-3 border-b border-emerald-100">
 <div className="flex items-center space-x-2 md:space-x-3">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 <div>
 <p className="text-[10px] md:text-xs font-semibold text-emerald-600 uppercase tracking-wide">
 Status
 </p>
 <span
 className={`inline-flex items-center px-2.5 md:px-3 py-1 rounded-lg text-xs md:text-sm font-semibold ${
 bookingStatus ==="accepted"?"bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0": bookingStatus ==="driver_arrived"?"bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0": bookingStatus ==="completed"?"bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0":"bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0"}`}
 >
 {bookingStatus ==="accepted"?"Accepted": bookingStatus ==="driver_arrived"?"Arrived": bookingStatus ==="completed"?"Completed":"Waiting"}
 </span>
 </div>
 </div>
 </div>

 {acceptedFare && (
 <div className="flex items-center justify-between py-3 md:py-4 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-lg px-3 md:px-4 border border-emerald-200 mt-3 md:mt-4">
 <div className="flex items-center space-x-2 md:space-x-3">
 <div className="w-8 h-8 md:w-10 md:h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
 <svg
 className="w-4 h-4 md:w-5 md:h-5 text-white"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 <div>
 <p className="text-[10px] md:text-xs font-semibold text-emerald-700 uppercase tracking-wide">
 Accepted Fare
 </p>
 <p className="text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
 ₹{acceptedFare}
 </p>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Info Message and Action Buttons - Show only when ride is NOT accepted and NOT pending */}
 {!(
 bookingStatus ==="accepted"||
 bookingStatus ==="driver_arrived"||
 bookingStatus ==="in_progress"||
 bookingStatus ==="completed"||
 bookingStatus ==="pending") && (
 <>
 {bookingStatus ==="rejected"? (
 <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-6 mb-4">
 <div className="flex items-start space-x-4">
 <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
 <svg
 className="w-6 h-6 text-red-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </div>
 <div className="flex-1">
 <h5 className="text-lg font-bold text-red-900 mb-2">
 Ride Rejected
 </h5>
 <p className="text-sm text-red-700 leading-relaxed">
 The driver has rejected your ride request. Please find
 another driver.
 </p>
 </div>
 </div>
 </div>
 ) : (
 <div className="bg-gray-50 border-l-4 border-gray-400 rounded-lg p-6">
 <div className="flex items-start space-x-4">
 <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
 <svg
 className="w-6 h-6 text-gray-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
 </svg>
 </div>
 <div className="flex-1">
 <h5 className="text-lg font-bold text-gray-900 mb-2">
 What happens next?
 </h5>
 <p className="text-sm text-gray-700 leading-relaxed">
 The driver will be notified of your request. Once they
 accept, you'll receive real-time updates about your
 ride status and can communicate with your driver
 through the app.
 </p>
 </div>
 </div>
 </div>
 )}

 {/* Action Buttons - Show only when ride is NOT accepted */}
 <div className="flex flex-col sm:flex-row gap-4 pt-2">
 {bookingStatus ==="rejected"? (
 <button
 onClick={() => {
 // Prepare navigation state with all booking data
 const navigationState = {
 pincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 vehicleType:
 booking?.vehicleType ||
 driver?.vehicleType ||
 location.state?.vehicleType,
 paymentMethod:
 booking?.paymentMethod ||
 location.state?.paymentMethod ||"cash",
 pickupLocation:
 pickupLocation ||
 booking?.pickupLocation ||
 location.state?.pickupLocation,
 pickupLocationName:
 booking?.pickupLocationName ||
 location.state?.pickupLocationName ||
 pickupLocation,
 pickupPincode:
 booking?.pickupPincode ||
 location.state?.pickupPincode,
 pickupLandmark:
 booking?.pickupLandmark ||
 location.state?.pickupLandmark ||"",
 dropoffLocation:
 dropoffLocation ||
 booking?.dropoffLocation ||
 location.state?.dropoffLocation,
 dropoffLandmark:
 booking?.dropoffLandmark ||
 location.state?.dropoffLandmark ||"",
 bookingType:
 booking?.bookingType ||
 location.state?.bookingType ||"book-now",
 rideId:
 booking?.rideId || location.state?.rideId || null,
 pickupPhoto:
 booking?.pickupPhoto ||
 location.state?.pickupPhoto ||
 null,
 startDate:
 booking?.scheduledStartDate ||
 location.state?.startDate ||
 null,
 startTime:
 booking?.scheduledStartTime ||
 location.state?.startTime ||
 null,
 endDate:
 booking?.scheduledEndDate ||
 location.state?.endDate ||
 null,
 };

 console.log("🚗 Navigating to nearby drivers (Web) with state:",
 navigationState
 );

 // CRITICAL: Save navigation state to localStorage as backup (for VPS/production)
 try {
 localStorage.setItem('nearby_drivers_nav_state', JSON.stringify(navigationState));
 console.log('✅ Navigation state saved to localStorage');
 } catch (e) {
 console.warn('⚠️ Could not save navigation state to localStorage:', e);
 }

 // Navigate to nearby drivers page
 navigate("/nearby-drivers", { state: navigationState });
 }}
 className="flex-1 px-6 py-4 bg-gray-900 text-white hover:bg-gray-800 rounded-lg font-semibold text-base transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
 </svg>
 <span>Find Another Driver</span>
 </button>
 ) : (
 bookingStatus !=="cancelled"&& bookingStatus !=="rejected"&& bookingStatus !=="completed"&& (
 <button
 onClick={handleCancel}
 className="flex-1 px-6 py-4 bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg font-semibold text-base transition-colors flex items-center justify-center space-x-2">
 <svg
 className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 <span>Cancel Booking</span>
 </button>
 )
 )}
 </div>
 </>
 )}
 </div>
 </div>
 </div>
 </>
 )}

 {/* Pickup OTP Popup - opens when driver arrives */}
 {showPickupOtpPopup && (() => {
 const safeOtpCode = extractOtpCode(otpCode);
 if (!safeOtpCode) return null;

 return (
 <div
 className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[10004] p-4"onClick={() => setShowPickupOtpPopup(false)}
 >
 <div
 className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-gray-200"onClick={(e) => e.stopPropagation()}
 >
 <div className="p-5 border-b border-gray-200 flex items-center justify-between">
 <div>
 <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide notranslate"translate="no">Pickup OTP</p>
 <h3 className="text-lg font-bold text-gray-900 notranslate"translate="no">Share with driver at pickup</h3>
 </div>
 <button
 type="button"onClick={() => setShowPickupOtpPopup(false)}
 className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"aria-label="Close">
 <svg className="w-5 h-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>

 <div className="p-6">
 <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between notranslate"translate="no">
 <div className="notranslate"translate="no">
 <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 notranslate"translate="no">OTP</p>
 <p
 className="text-5xl font-black text-blue-900 tracking-widest notranslate"translate="no"lang="en"data-translate="no"style={{
 unicodeBidi:'isolate',
 direction:'ltr',
 userSelect:'none',
 WebkitUserSelect:'none',
 MozUserSelect:'none',
 msUserSelect:'none'}}
 suppressContentEditableWarning={true}
 contentEditable={false}
 >
 {String(safeOtpCode).split('').map((digit, idx) => (
 <span
 key={idx}
 className="notranslate"translate="no"lang="en"data-translate="no"style={{ unicodeBidi:'isolate', direction:'ltr'}}
 >
 {digit}
 </span>
 ))}
 </p>
 </div>
 <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center ml-4 notranslate"translate="no">
 <svg className="w-7 h-7 text-blue-700"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path strokeLinecap="round"strokeLinejoin="round"strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
 </svg>
 </div>
 </div>

 <div className="mt-5 flex gap-3">
 <button
 type="button"onClick={() => setShowPickupOtpPopup(false)}
 className="flex-1 px-4 py-3 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors">
 OK
 </button>
 </div>
 </div>
 </div>
 </div>
 );
 })()}

 {/* Negotiation Popup - Shows when driver proposes amount */}
 {showNegotiationPopup && negotiationBooking && (
 <NegotiationPopup
 booking={negotiationBooking}
 onClose={() => {
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }}
 onConfirmed={(data) => {
 console.log("✅ Negotiation confirmed:", data);

 // CRITICAL: Mark negotiation as accepted to prevent popup from showing again
 setNegotiationAccepted(true);
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 setBookingStatus("accepted");

 // Set accepted fare
 if (data.fare) {
 setAcceptedFare(data.fare);
 }
 // CRITICAL: OTP is NOT set when negotiation is confirmed (status: accepted)
 // OTP will be generated ONLY when driver arrives (status: driver_arrived)
 console.log("✅ Negotiation confirmed - OTP will be generated when driver arrives");

 // Update booking object to remove negotiation data and set fare
 if (data.booking) {
 setBooking((prev) => {
 const updated = {
 ...prev,
 ...data.booking,
 status:"accepted",
 fare: data.fare || prev.fare,
 // Clear negotiation data to prevent popup from showing again
 negotiation: undefined,
 };

 // CRITICAL: OTP is NOT extracted from booking object when status is"accepted"// OTP will be generated ONLY when driver arrives (status: driver_arrived)
 console.log("✅ Booking accepted - OTP will be generated when driver arrives");

 return updated;
 });
 } else {
 // Clear negotiation data from existing booking
 setBooking((prev) => ({
 ...prev,
 status:"accepted",
 fare: data.fare || prev.fare,
 negotiation: undefined,
 }));
 }

 // CRITICAL: OTP is NOT fetched from API when status is"accepted"// OTP will be generated ONLY when driver arrives (status: driver_arrived)
 console.log("✅ Booking accepted - OTP will be generated when driver arrives");

 console.log("✅ Booking accepted - Fare:",
 data.fare
 );
 console.log("🚫 Negotiation popup will NOT show again - marked as accepted");
 }}
 />
 )}

 {/* Payment Modal - Shows when ride is completed and payment is pending */}
 {showPaymentModal && bookingStatus ==="completed"&& acceptedFare && (
 <PaymentModal
 booking={booking}
 fare={acceptedFare || booking?.fare}
 user={user}
 onPaymentComplete={(paymentData) => {
 console.log("✅ Payment completed:", paymentData);
 setShowPaymentModal(false);
 setPaymentStatus({
 status:"completed",
 method: paymentData.paymentMethod,
 transactionId: paymentData.transactionId,
 driverConfirmedAt: null,
 });

 if (showNegotiationPopup) {
 console.log("💰 Payment submitted - closing negotiation popup");
 setShowNegotiationPopup(false);
 setNegotiationBooking(null);
 }
 }}
 onClose={() => {
 setShowPaymentModal(false);
 }}
 />
 )}

 {/* Rating Modal - Shows after payment completion */}
 {(() => {
 const shouldShow =
 showRatingModal &&
 (booking || bookingId) &&
 !hasRated &&
 isPaymentFullySettled(booking, paymentStatus);
 if (showRatingModal) {
 console.log("🔍 Rating Modal Render Check:", {
 showRatingModal,
 hasBooking: !!booking,
 bookingId,
 hasRated,
 shouldShow
 });
 }
 return shouldShow;
 })() && (
 <RatingModal
 booking={booking || { _id: bookingId, id: bookingId }}
 driverName={driver?.name || booking?.driverName ||"Driver"}
 onRatingComplete={(ratingData) => {
 console.log("⭐ Rating submitted:", ratingData);
 setHasRated(true);
 setShowRatingModal(false);
 // Navigate to dashboard immediately after rating is submitted
 // Set flag to indicate ride completion - this will lock back button on homepage
 sessionStorage.setItem('user_ride_completed','true');
 localStorage.setItem('user_ride_completed','true');
 // Clear ALL booking-related storage to ensure fresh start for next ride
 clearAllBookingStorage();
 // Use replace: true to clear the entire ride flow stack
 try {
 navigate("/dashboard", { replace: true });
 console.log("🧹 Cleared ride flow stack - back button will not return to ride screens");
 console.log("🔒 Set ride completion flag - homepage back button will be locked");
 } catch (error) {
 console.error("Navigation error, using window.location:", error);
 // Use replace to clear history
 window.history.replaceState(null,'','/dashboard');
 window.location.href ="/dashboard";
 }
 }}
 onClose={() => {
 setShowRatingModal(false);
 setHasRated(true); // Mark as rated even if skipped
 // Navigate to dashboard immediately after closing rating modal
 // Set flag to indicate ride completion - this will lock back button on homepage
 sessionStorage.setItem('user_ride_completed','true');
 localStorage.setItem('user_ride_completed','true');
 // Clear ALL booking-related storage to ensure fresh start for next ride
 clearAllBookingStorage();
 // Use replace: true to clear the entire ride flow stack
 try {
 navigate("/dashboard", { replace: true });
 console.log("🧹 Cleared ride flow stack - back button will not return to ride screens");
 console.log("🔒 Set ride completion flag - homepage back button will be locked");
 } catch (error) {
 console.error("Navigation error, using window.location:", error);
 // Use replace to clear history
 window.history.replaceState(null,'','/dashboard');
 window.location.href ="/dashboard";
 }
 }}
 />
 )}

 {/* Rejection Popup - Shows in real-time when driver rejects */}
 {(showRejectionPopup || bookingStatus ==="rejected") && (
 <RejectionPopup
 booking={booking}
 pickupLocation={pickupLocation}
 dropoffLocation={dropoffLocation}
 location={location}
 onClose={() => {
 console.log("🚪 Closing rejection popup");
 setShowRejectionPopup(false);
 // Don't reset bookingStatus to allow user to see rejection state
 }}
 />
 )}

 {/* Driver Busy Popup - Shows when driver accepts another user's ride */}
 {showDriverBusyPopup && driverBusyData && (
 <DriverBusyPopup
 driverName={driverBusyData.driverName}
 booking={driverBusyData.booking}
 onClose={() => {
 console.log("🚪 Closing driver busy popup");
 setShowDriverBusyPopup(false);
 setDriverBusyData(null);
 }}
 />
 )}

 {/* Cancel Ride Confirmation Dialog - Web Only */}
 {showCancelConfirm && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10002] p-3 sm:p-4">
 <div className="bg-white rounded-lg w-full max-w-md sm:max-w-lg max-h-[min(92dvh,92vh)] overflow-y-auto overscroll-contain animate-slideUp">
 <div className="p-6">
 <div className="flex items-center justify-center mb-4">
 <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
 <svg
 className="w-8 h-8 text-red-600"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
 </svg>
 </div>
 </div>
 <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
 Cancel Ride?
 </h3>
 <p className="text-gray-600 text-center mb-6">
 Are you sure you want to cancel this ride? This action cannot be
 undone. The driver will be notified of the cancellation.
 </p>
 <div className="flex gap-3">
 <button
 onClick={() => setShowCancelConfirm(false)}
 disabled={cancelling}
 className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
 Keep Ride
 </button>
 <button
 onClick={confirmCancelRide}
 disabled={cancelling}
 className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
 {cancelling ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
 <span>Cancelling...</span>
 </>
 ) : (
 <span>Yes, Cancel Ride</span>
 )}
 </button>
 </div>
 </div>
 </div>
 </div>
 )}

 {/* Success Message Modal - Show when ride is cancelled successfully */}
 {successMessage.show && (
 <MessageModal
 message={successMessage.message}
 title={successMessage.title}
 type="success"onClose={() => {
 setSuccessMessage({ show: false, message:'', title:''});
 // Navigate to dashboard after closing the success message
 navigate("/dashboard");
 }}
 />
 )}

 {/* General Message Modal - For errors, warnings, and info */}
 {messageModal.show && (
 <MessageModal
 message={messageModal.message}
 title={messageModal.title}
 type={messageModal.type}
 onClose={() => setMessageModal({ show: false, message:'', type:'info', title:'Message'})}
 />
 )}

 {/* OTP Success Popup */}
 <OtpSuccessPopup
 show={showOtpSuccessPopup}
 onClose={() => {
 console.log("🔴 OtpSuccessPopup: onClose called");
 setShowOtpSuccessPopup(false);
 }}
 message="OTP verified successfully"title="OTP Verified"autoClose={true}
 autoCloseDelay={4000}
 />
 

 {/* Share Ride Modal */}
 {showShareRide && booking && (
 <ShareRide
 bookingId={booking.id || booking._id || null}
 rideId={booking.rideId || null}
 onClose={() => setShowShareRide(false)}
 />
 )}

 {/* Booking Information Popup Modal - Mobile Only */}
 {showBookingInfoModal && (
 <div className="md:hidden fixed inset-0 z-[10003] bg-slate-950/55 backdrop-blur-sm flex items-end justify-center">
 <div className="w-full max-h-[90vh] overflow-y-auto rounded-t-[30px] border-t border-white/20 bg-[linear-gradient(180deg,#f6f9ff_0%,#eef3ff_42%,#ffffff_100%)] shadow-[0_-24px_70px_-36px_rgba(15,23,42,0.55)] animate-slideUp">
 <div className="sticky top-0 z-10 rounded-t-[30px] border-b border-slate-200/80 bg-white/95 px-4 pb-4 pt-2 backdrop-blur-xl">
 <div className="mx-auto mb-2 h-1.5 w-14 rounded-full bg-slate-300"/>
 <div className="flex items-center justify-between gap-3">
 <h3 className="flex items-center gap-2.5 text-base font-black tracking-[-0.01em] text-slate-900">
 <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white shadow-[0_16px_30px_-20px_rgba(15,23,42,0.7)]">
 <svg
 className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
 </svg>
 </span>
 Booking Information
 </h3>

 <button
 onClick={() => setShowBookingInfoModal(false)}
 className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"aria-label="Close booking information">
 <svg
 className="h-5 w-5"fill="none"stroke="currentColor"viewBox="0 0 24 24">
 <path
 strokeLinecap="round"strokeLinejoin="round"strokeWidth={2}
 d="M6 18L18 6M6 6l12 12"/>
 </svg>
 </button>
 </div>
 </div>

 <div className="px-4 pb-5 pt-4">
 <div className="rounded-[24px] border border-slate-200/90 bg-white p-4 shadow-[0_26px_54px_-40px_rgba(15,23,42,0.35)]">
 <div className="flex items-center justify-between rounded-[16px] border border-slate-200 bg-slate-50 px-3.5 py-2.5">
 <p className="text-[10px] font-bold uppercase tracking-[0.17em] text-slate-500">Status</p>
 <span
 className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.13em] ${
 bookingStatus ==="completed"?"border-emerald-200 bg-emerald-50 text-emerald-700": bookingStatus ==="driver_arrived"|| bookingStatus ==="in_progress"?"border-blue-200 bg-blue-50 text-blue-700": bookingStatus ==="accepted"?"border-indigo-200 bg-indigo-50 text-indigo-700":"border-amber-200 bg-amber-50 text-amber-700"}`}
 >
 {bookingStatus ==="accepted"?"Accepted": bookingStatus ==="driver_arrived"?"Arrived": bookingStatus ==="in_progress"?"On Trip": bookingStatus ==="completed"?"Completed":"Waiting"}
 </span>
 </div>

 <div className="mt-3 space-y-2.5">
 <div className="rounded-[16px] border border-slate-200 bg-[linear-gradient(180deg,#f8fbff_0%,#f2f6ff_100%)] px-3.5 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Booking ID</p>
 <p className="mt-1.5 text-[1.05rem] font-black tracking-[0.04em] text-slate-900 font-mono">
 #{(booking?.id || booking?._id)?.slice(-8).toUpperCase() ||"--"}
 </p>
 </div>

 <div className="grid grid-cols-2 gap-2.5">
 <div className="rounded-[16px] border border-slate-200 bg-white px-3.5 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Payment</p>
 <p className="mt-1.5 text-sm font-black text-slate-900 capitalize">{booking?.paymentMethod ||"Cash"}</p>
 </div>
 <div className="rounded-[16px] border border-slate-200 bg-white px-3.5 py-3">
 <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Vehicle</p>
 <p className="mt-1.5 text-sm font-black text-slate-900">
 {booking?.vehicleType || driver?.vehicleType ||"Vehicle"}
 </p>
 </div>
 </div>

 {acceptedFare && (
 <div className="rounded-[18px] border border-emerald-200 bg-[linear-gradient(135deg,#ecfdf3_0%,#f4fff8_50%,#ffffff_100%)] px-4 py-3.5 shadow-[0_16px_34px_-26px_rgba(5,150,105,0.45)]">
 <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700">Accepted Fare</p>
 <div className="mt-1.5 flex items-center justify-between">
 <p className="text-2xl font-black tracking-[-0.02em] text-emerald-800">₹{acceptedFare}</p>
 <span className="inline-flex rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700">
 Confirmed
 </span>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 );
};

export default BookingConfirmationPage;
